// The cashbook — live-parity single page (live /cashbook): title row with Auto|Manual
// top-right, email-intake row, account cards strip (rules + PDF-password icons, badge),
// sticky summary (ledger balance · unallocated pill · re-run allocations · IN/OUT/
// projected · ✨ reconcile · post), Auto bank-feed view (search, select-all, anomaly
// banner, REF column, allocate chip + AI suggestion under the description, AUTO/MANUAL
// source badges, running balance, inline ✎ edit), Manual capture view (Deposits/
// Payments tabs, quick-add with G/Debtor/Creditor/VAT pills + local typeahead with
// balances, Inc-Ref/Keep-Description switches), allocation popover (suggestions +
// save-as-rule + apply-to-similar + debtor/creditor contact step), bulk move/flip/
// delete, statement import (CSV deterministic / PDF via AI), reconcile panel.
//
// tina4-js discipline: ONE reactive body per region (signals read at the top, no nested
// reactive slots — the append bug), form state in plain vars so typing never re-renders,
// floating dropdown/popover as imperative DOM (focus survives).
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import { parseAmount } from '../shared/formats.js';
import { mountFloatRoot, closeFloatRoot } from '../shared/floatroot.js';
import { dropdownKeydown, highlightFirst } from '../shared/dropnav.js';
import { sortTh, nextSort } from '../shared/sortcols.js';
import { tenantShell } from './tshell.js';
import { paneFx } from '../shared/pane.js';
import { t } from '../shared/i18n.js';
import { rowDelete } from '../shared/confirm.js';
import { settlementDetails } from './bank_settlement_details.js';
import { fmtR, fmtRS, fmtMoney, fmtAmountOnly, baseCurrencySymbol, postGateMessage, inclusiveTax, incrementRef, scoreGL, confPct, formatDate, formatGlCode, controlTag, importErrorNotice, suggestionFor, bankFigures, reconOffBy, canClearGaps, baseCurrencyCode } from './bank_helpers.js';
import { coopReview } from './coop_review.js';
import { groupCards, sectionName, latestSet, openSetsFor, setStatus, jobSets, sectionChoices, linkableCoops, heldWhy,
         heldReasons, withMoves, movedOutText, coopLinkStart, limitFigures, termsText, limitBody } from './coop_helpers.js';

// The local date, as governance.js builds it. toISOString() gives the UTC date: in Cape Town (UTC+2) before 02:00
// "today" was still yesterday, so a new bank line and an opening balance started on the day before (2026-10-09).
const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = () => isoLocal(new Date());
// {name} placeholders filled from a map (every occurrence)
const fill = (text, values) => Object.entries(values).reduce((s, [k, v]) => s.split('{' + k + '}').join(v ?? ''), text);

export function cashbookScreen(companyId, initialAccountId){
  // ── state ──────────────────────────────────────────────────────────────────
  const accounts = signal([]), reference = signal(null), lines = signal([]);
  const settlementCache = new Map(); // in-memory only; journal/amount key invalidates changed settlements
  // the working account survives re-mounts, refreshes and review round-trips — actions
  // must never bounce the user back to the first/busiest card (live keeps your place)
  const SEL_KEY = `sleek_bank_sel_${companyId}`;
  let savedSel = 0;
  try { savedSel = Number(sessionStorage.getItem(SEL_KEY) || 0); } catch { /* storage off */ }
  const sel = signal(initialAccountId ? Number(initialAccountId) : savedSel);
  const balance = signal(null);
  const mode = signal('auto'), tab = signal('deposit'), onlyUnalloc = signal(false);
  const autoQ = signal('');
  const selected = signal(new Set());
  const loading = signal(true), busy = signal(''), toast = signal(null);
  const pwdOpenId = signal(0), addOpen = signal(false), importOpen = signal(false);
  // guided "new bank account" wizard: step 1 details · 2 GL (new unique code / existing) ·
  // 3 opening balance + date · 4 confirm. The opening balance is posted as a real journal on
  // create (Dr bank GL / Cr Opening Balance Equity) so the GL ledger balance opens correctly.
  const addStep = signal(1), addNextCode = signal('');
  // wrong-account-drop guard: when a dropped statement's account number doesn't match the card,
  // the backend holds the import and returns account_mismatch — we confirm before importing. The held drop is the
  // API's (listed under "Waiting for you" until answered, 2026-10-27): the question carries its job, never the files.
  const mismatch = signal(null);   // {accountId, accountName, detected, expected, jobId, files}
  const editId = signal(0), confirmPost = signal(false), confirmDelId = signal(0), confirmBulkDel = signal(false);
  // live dateFmt parity: table dates follow the COMPANY's date format (reference bundle)
  const dmy = iso => formatDate(iso, (reference.value || {}).date_format);
  // ledger-code display: base codes are stored flat (9500); real sub-accounts keep their dot
  const gcode = code => formatGlCode(code);
  // ── one currency per figure (owner 2026-10-19: "I DO NOT WANT CURRENCIES TO BLEED INTO EACH OTHER") ──
  // An account's own figures (balance / staged / projected) and the ONE mark they carry; the company
  // base (bank-reference base_currency_id) tells a foreign account from a base one — never 'ZAR'.
  const figsOf = (block, acct) => bankFigures(block, acct, { id: (reference.value || {}).base_currency_id });
  // the mark of a currency picked by id in the add-account wizard ('' = the company base)
  const curSym = id => {
    const c = id ? ((reference.value || {}).currencies || []).find(x => String(x.id) === String(id)) : null;
    return c ? (c.symbol || c.code || '') : baseCurrencySymbol();
  };
  // the marks a typed amount in that currency may carry — its symbol and code ('' / null = the company
  // base); parseAmount refuses any other currency's mark ("$ 50" is not fifty euro)
  const curMarks = (id, acct) => {
    const ref = reference.value || {}, isBase = id === null || id === undefined || id === '';
    const want = isBase ? ref.base_currency_id : id;
    const c = (want === null || want === undefined) ? null : (ref.currencies || []).find(x => String(x.id) === String(want));
    if(c) return [c.symbol, c.code];
    if(acct && (acct.currency_symbol || acct.currency_code)) return [acct.currency_symbol, acct.currency_code];
    return isBase ? [baseCurrencySymbol(), baseCurrencyCode()] : [];
  };
  // …of the open account (its register lines are in its own currency)
  const openMarks = () => { const a = accounts.value.find(x => x.id === sel.value); return a ? curMarks(a.currency_id, a) : curMarks(''); };
  const locate = signal(null);             // "see this line on the statement" modal: {status,title,imgUrl,box,page,pageCount,foundPage,foundBox,error}
  let locateSeq = 0;                        // guards async page loads against a stale/closed modal
  const jobStates = signal({});            // panel/+Add drops: jobId → {id,stage,progress,stalled,lastProgressAt,label,bodies,readAgain,cancelling}. SEVERAL can run at once (queued behind one worker) — each keeps its own progress row, so a 2nd drop no longer wipes the 1st (owner 2026-09-08).
  const cardJobs = signal({});             // per-card drops: accountId → {stage, progress} (v2 _setCardImport)
  const noKeyNotice = signal(null);        // persistent import notice {needsKey,msg} — set proactively when no AI key, and when a drop errors (replaces the old 2.6s toast that was missed)
  const NO_KEY_MSG = 'To import statements or auto-allocate entries with AI, add your own AI API key.';  // one wording, used by the proactive notice AND the no-key drop guard
  const dieselOn = signal(false);          // live TAX_CODE_DIESEL ('25') shows a litres input
  // the REGISTER — the bank statement forever (Andre 2026-08-24): EVERY line whatever
  // its status, server-paged for infinite scroll + server-side search, grey/red locks
  const regRows = signal([]), regTotal = signal(0), regPostedTotal = signal(0), regLoading = signal(false);
  const regStmts = signal([]);              // the account's statements, for the register "Jump to" nav
  const regOpening = signal(null), regOpeningDate = signal('');   // account opening balance (a journal, shown as the starting balance)
  const regMoves = signal([]);              // a co-op cashbook's balance moves between the co-op's sub-accounts (journals with no line: rows of their own)
  const regView = signal('');               // register filter: '' = all (chronological, posted+unposted inline); 'unposted' = the "Show Unposted" checkbox hides posted rows (server-side posted filter)
  const regGaps = signal(false);            // "Show gaps" filter: only is_gap rows (balance mismatches parked for review) — server-side, holds across pages
  // how many of the account's lines carry the gap warning (the API's gap_count — the whole account, whatever is
  // shown); while the account reconciles the summary offers to clear them all (confirmClearGaps: its inline ask)
  const regGapCount = signal(0), confirmClearGaps = signal(false);
  // "Transfers to check": only the lines a payment between two of the company's own accounts needs the user for (the
  // API's bank_transfer_checks: in the books twice, or its other side booked to something else) — server-side, holds
  // across pages; regChecks is how many the account has
  const regTransfers = signal(false), regChecks = signal(0);
  const regSort = signal({ key: 'date', dir: 'desc' });   // column sort — server-side (holds across pages)
  let regPage = 0, regDone = false, regQ = '', regGen = 0;
  let regFrom = '', regTo = '';             // register txn-date range filter (From / To)

  // quick-add + panel state — plain vars: typing must never trigger a re-render
  let fDate = todayIso(), fDest = 'G', fGlId = 0, fGlLabel = '', fContactId = 0,
      fRef = '', fDesc = '', fTaxId = '', fCcId = '', fAmount = '',
      fIncRef = false, fKeepDesc = false, fDiesel = '';
  let autoQTimer = null;
  let editVals = {};
  let pwdVal = '', bulkTarget = '';
  let aName = '', aNo = '', aBank = '', aCur = '';   // aCur '' → the company base currency (#24)
  let aOpening = '', aOpeningDate = todayIso();       // opening balance amount + date (step 3)
  const aGlMode = signal('new');                     // step 2: mint a new unique GL code, or link an existing GL
  let aCode = '', aGlId = 0;
  let importAccount = '', importRef = '';
  let addImportJobId = 0;                             // held needs_account job to import (reusing its extracted data) once the wizard creates the account
  // ── co-op (supplier trading) accounts — plan/supplier-cashbooks.md §9 ──
  // A co-op's section cashbooks are grouped under one expandable header card; /coops adds the co-ops with no cashbook
  // yet and the sub-accounts each co-op can have; /statement-sets the co-op statements imported, newest first.
  const coops = signal([]), coopProviders = signal([]), coopSets = signal([]);
  const COOPS_OPEN_KEY = `sleek_bank_coops_open_${companyId}`;
  let savedOpen = [];
  try { savedOpen = JSON.parse(sessionStorage.getItem(COOPS_OPEN_KEY) || '[]'); } catch { /* storage off */ }
  const coopsOpen = signal(new Set((Array.isArray(savedOpen) ? savedOpen : []).map(Number)));
  // the selected co-op cashbook's statements that do not tie (held), as the last check of them found
  const acctHeld = signal({ accountId: 0, sets: [] });
  let heldSeq = 0;
  // the new-account form's co-op side: the kind (bank | coop), an existing co-op or a new one, the sub-account; and a
  // statement held for its co-op (needs_coop) that the form links or makes the co-op for
  const aKind = signal('bank'), cMode = signal('existing'), cGroupId = signal(''), cProvider = signal('');
  const coopHeld = signal(null);                      // {read: the needs_coop pre-fill, jobId}
  let cName = '', cRef = '', cSection = '';
  // the company's statements held for their account or co-op (GET /bank-imports/held), listed above the cards until
  // each is imported or discarded: the form asks about one at a time, and a closed form, a card click, another held
  // statement or a reload let it go, waiting where nothing showed it (2026-10-26). heldDiscardId: the one whose
  // Discard asks "Discard? Yes / No". heldPick: the bank account chosen on the list for a statement whose number was
  // not read (job id → account id) — a plain object: choosing never draws the page
  const heldImports = signal([]), heldDiscardId = signal(0);
  const heldPick = {};
  // a co-op cashbook's own credit limit, terms and interest (plan §1, §9), edited in place on its card (✎): the card
  // being edited, what its form says (plain — typing never draws the page), and why a save was refused
  const limitEditId = signal(0), limitErr = signal('');
  let limVal = '', termsVal = '', rateVal = '';

  const P = id => `/companies/${companyId}` + id;
  let toastTimer = null;
  const setToast = (msg, kind) => { clearTimeout(toastTimer); toast.value = { msg, kind: kind || 'ok' }; toastTimer = setTimeout(() => { toast.value = null; }, 2600); };
  // an action toast (e.g. the delete Undo) — carries a button and lingers longer (v2's 8s undo window)
  const setActionToast = (msg, label, fn, kind) => {
    clearTimeout(toastTimer);
    toast.value = { msg, kind: kind || 'ok', action: { label, fn } };
    toastTimer = setTimeout(() => { toast.value = null; }, 8000);
  };
  // The env-correct intake address comes from /bank-reference (import_email) — it carries this
  // environment's prefix (dev-/staging-/none). Fall back to the bare form only if an older API
  // didn't send it. Read inside a reactive slot so it fills in once the reference loads.
  const intakeAddress = () => (reference.value && reference.value.import_email)
    || `company${companyId}@aboo.co.za`;

  // ── data loading ───────────────────────────────────────────────────────────
  // The company's co-ops and their imported statements (plan §9). A company with no co-op asks for no statements.
  async function loadCoops(){
    try {
      const r = await api.get(P('/coops'));
      coops.value = (r && r.data) || [];
      coopProviders.value = (r && r.providers) || [];
    } catch { /* an API without co-ops: the cards are the banks alone */ }
    if(!coops.value.length){ coopSets.value = []; return; }
    try { const s = await api.get(P('/statement-sets')); coopSets.value = (s && s.data) || []; }
    catch { /* keep the statements already loaded */ }
  }
  // The selected co-op cashbook's statements that are not posted in full, checked against their PDFs again (GET
  // /statement-sets/{id} runs the tie-out anew): the held banner says what the books say NOW — after an edit that
  // fixes a misread line, or one that breaks a statement that tied.
  async function checkHeld(){
    const my = ++heldSeq;
    const acct = accounts.value.find(a => a.id === sel.value);
    if(!acct || acct.kind !== 'supplier_trading'){ acctHeld.value = { accountId: 0, sets: [] }; return; }
    const held = [];
    for(const s of openSetsFor(coopSets.value, acct.id)){
      let d = null;
      try { d = await api.get(P(`/statement-sets/${s.id}`)); } catch { /* keep the status on file */ }
      if(my !== heldSeq) return;                  // another cashbook was opened meanwhile
      const status = (d && d.status) || s.status;
      if(d && d.status) noteSetStatus(s.id, d.status, true);
      if(status === 'held') held.push({ ...s, status, why: d ? heldWhy(d) : null });
    }
    acctHeld.value = { accountId: acct.id, sets: held };
  }
  // a set's status as a check found it: the cards' pills, and the open cashbook's held banner (quiet: the caller
  // sets the banner itself); why: what holds it (heldWhy), for the banner's words
  function noteSetStatus(id, status, quiet, why){
    if(!id || !status) return;
    if(coopSets.value.some(s => s.id === id && s.status !== status))
      coopSets.value = coopSets.value.map(s => s.id === id ? { ...s, status } : s);
    const h = acctHeld.value;
    if(quiet || !h.accountId || h.accountId !== sel.value) return;
    const set = coopSets.value.find(s => s.id === id);
    const mine = !!set && (set.statements || []).some(st => Number(st.bank_account_id) === h.accountId);
    const rest = h.sets.filter(s => s.id !== id);
    acctHeld.value = { accountId: h.accountId,
                       sets: mine && status === 'held' ? rest.concat([{ ...set, status, why: why || null }]) : rest };
  }
  // a co-op's header is open while the user wants it (kept per company for the session); opening a section cashbook
  // from elsewhere (the review, a new account) opens its co-op
  function saveCoopsOpen(){ try { sessionStorage.setItem(COOPS_OPEN_KEY, JSON.stringify([...coopsOpen.value])); } catch { /* storage off */ } }
  function toggleCoop(groupId){
    const next = new Set(coopsOpen.value);
    if(next.has(groupId)) next.delete(groupId); else next.add(groupId);
    coopsOpen.value = next; saveCoopsOpen();
  }
  function revealCard(id){
    const a = accounts.value.find(x => x.id === Number(id));
    if(!a || a.group_id === null || a.group_id === undefined || coopsOpen.value.has(Number(a.group_id))) return;
    const next = new Set(coopsOpen.value); next.add(Number(a.group_id));
    coopsOpen.value = next; saveCoopsOpen();
  }
  const review = coopReview(companyId, {
    onOpenCashbook: id => selectAccount(Number(id)),
    onChecked: d => noteSetStatus(d && d.statement_set_id, d && d.status, false, d ? heldWhy(d) : null),
    onReadAgain: (jobId, setId, label) => trackReadAgain(jobId, setId, label),
    dateFormat: () => (reference.value || {}).date_format });
  async function loadAccounts(keepSel){
    const [r] = await Promise.all([api.get(P('/bank-accounts')), loadCoops()]);
    accounts.value = r.data || [];
    const cur = sel.value && accounts.value.find(a => a.id === sel.value);
    // keepSel keeps the user's current card selected — but ONLY when that selection is still valid.
    // When nothing valid is selected (e.g. a drop just auto-created the first account into an empty
    // company), fall through and pick a default, or view() sticks on the "no accounts" placeholder
    // and the freshly-imported review never surfaces until a manual reload.
    if(keepSel && cur) return;
    // live default: the MOST-USED account (most staged lines), not the first
    const busiest = accounts.value.slice()
      .sort((x, y) => (y.staged_count || 0) - (x.staged_count || 0))[0];
    sel.value = cur ? cur.id : (busiest ? busiest.id : 0);
  }
  async function loadRef(){
    reference.value = await api.get(P('/bank-reference'));
    configureSleekDate(reference.value);   // date format + financial periods feed <sleek-date>
    // Statement extraction needs the user's own AI key (no platform fallback). Prompt for setup
    // up front so a keyless drop never just yields a silent 0-row review. Clear the key prompt once
    // a key is present, but leave any other (non-key) import error notice untouched.
    if(!reference.value.ai_configured)
      noKeyNotice.value = { needsKey: true, msg: NO_KEY_MSG };
    else if(noKeyNotice.value && noKeyNotice.value.needsKey)
      noKeyNotice.value = null;
  }
  // place (a row action — see placeOf, via refresh(place)): rebuild the register pages already on
  // screen instead of resetting to page 1, and put the user's row back where it was on screen
  async function loadLines(place){
    confirmClearGaps.value = false;               // an ask to clear the gap warnings is for the figures it was asked on
    if(!sel.value){ lines.value = []; balance.value = null; regRows.value = []; regTotal.value = 0; return; }
    const [ls, bal] = await Promise.all([
      api.get(P(`/bank-accounts/${sel.value}/entries`)),
      api.get(P(`/bank-accounts/${sel.value}/balance`))
    ]);
    lines.value = ls.data || [];
    balance.value = bal;
    // statements power the register "Jump to" nav — fetch in the background, never block the register
    api.get(P(`/bank-accounts/${sel.value}/statements`)).then(r => { regStmts.value = r.data || []; }).catch(() => {});
    checkHeld();                                  // a co-op cashbook: does its statement tie to its PDF now?
    await (place && place.keepPlace === true ? reloadKeepingView(place) : loadRegister(true));
    // live: the quick-add Ref starts EMPTY (type it once, Inc. Ref auto-increments after)
  }

  async function loadRegister(reset){
    if(!sel.value){ regRows.value = []; regTotal.value = 0; return; }
    if(reset){ regPage = 0; regDone = false; }
    if(regDone) return;
    if(regLoading.value && !reset) return;      // a scroll while a page is loading: skip
    // a reset (search / account switch) must NOT be dropped when a fetch is in flight — bump the
    // generation so the older request's result is discarded instead of overwriting the list with a
    // stale page (regPage was just reset to 0, so its late reply would REPLACE, not append).
    const gen = ++regGen;
    const page0 = regPage;                       // capture: don't trust regPage after the await
    regLoading.value = true;
    try {
      // All view caps at 50 with a "Load more" button; a posted/unposted selection infinite-scrolls at 100
      const p = new URLSearchParams({ page: String(page0 + 1), per_page: regView.value ? '100' : '50' });
      if(regQ.trim()) p.set('q', regQ.trim());
      if(regFrom) p.set('from', regFrom);                 // server-side txn-date range
      if(regTo) p.set('to', regTo);
      if(regView.value) p.set('posted', regView.value);   // server-side posted/unposted filter
      if(regGaps.value) p.set('gaps', '1');                // server-side gaps-only filter
      if(regTransfers.value) p.set('transfers', '1');      // server-side transfers-to-check filter
      p.set('sort', regSort.value.key); p.set('dir', regSort.value.dir);   // server-side column sort
      const r = await api.get(P(`/bank-accounts/${sel.value}/register?`) + p);
      if(gen !== regGen) return;                 // superseded by a newer reset → discard stale result
      regRows.value = page0 === 0 ? (r.data || []) : regRows.value.concat(r.data || []);
      regPage = r.page || page0 + 1;
      regDone = regPage >= (r.total_pages || 1);
      regTotal.value = r.total || 0;
      regPostedTotal.value = r.posted_total || 0;
      regChecks.value = r.transfer_checks || 0;
      regGapCount.value = r.gap_count || 0;
      if('opening_balance' in r){ regOpening.value = r.opening_balance; regOpeningDate.value = r.opening_balance_date || ''; }
      regMoves.value = r.moves || [];
    } catch { /* keep what is loaded */ }
    finally { if(gen === regGen) regLoading.value = false; }
    // chain-load: if the sentinel is still within view after this page rendered (rows don't yet
    // fill the viewport, or no scroll event fired), pull the next page too — including right after
    // the initial load so the first screen always fills.
    if(!regDone){
      setTimeout(() => {
        const end = document.getElementById('cbRegEnd');
        if(end && !regDone && !regLoading.value &&
           end.getBoundingClientRect().top < window.innerHeight + 600) loadRegister(false);
      }, 60);
    }
  }
  // The element that actually scrolls the register: the tenant shell scrolls .shell-main, NOT the
  // window (window.scrollY stays 0 there). Fall back to the page scroller wherever the window scrolls.
  function regScroller(){
    const main = document.querySelector('.shell-main');
    if(main){
      const oy = getComputedStyle(main).overflowY;
      if((oy === 'auto' || oy === 'scroll') && main.scrollHeight > main.clientHeight) return main;
    }
    return document.scrollingElement || document.documentElement;
  }
  // WHERE THE USER IS WORKING, captured BEFORE a row action changes anything (owner 2026-09-28: with
  // "Apply to all similar" too, the view "must stay exactly where the user chose to use it after it is
  // applied"): the row acted on, where it sits on screen, and the register rows in screen order — so
  // if a filter now hides that row (⚠ Unallocated, Show Unposted) the row after it takes its place.
  // keepPlace marks a real capture (loadLines ignores anything else, e.g. a stray callback argument).
  function placeOf(lineId){
    const scroller = regScroller();
    const rows = [...document.querySelectorAll('.cb-register-table tr[data-line-id]')];
    const id = String(lineId), row = rows.find(tr => tr.dataset.lineId === id);
    return { keepPlace: true, scroller, y: scroller.scrollTop, id,
             top: row ? row.getBoundingClientRect().top : null, ids: rows.map(tr => tr.dataset.lineId) };
  }
  // Put the view back: the acted-on row returns to the same spot on screen; if it is gone, the first
  // surviving row after it takes that spot (else the nearest one before it); if none of them survived,
  // the old scroll position.
  function restorePlace(place){
    if(!place.scroller.isConnected || !document.querySelector('.cb-register-table')) return;   // left the register
    const now = new Map([...document.querySelectorAll('.cb-register-table tr[data-line-id]')]
      .map(tr => [tr.dataset.lineId, tr]));
    const at = place.top === null ? -1 : place.ids.indexOf(place.id);
    const order = at < 0 ? [] : place.ids.slice(at).concat(place.ids.slice(0, at).reverse());
    const tr = order.map(id => now.get(id)).find(Boolean);
    if(tr) place.scroller.scrollTop += tr.getBoundingClientRect().top - place.top;
    else place.scroller.scrollTop = place.y;
  }
  // Reload the register AFTER a row action (allocate / re-allocate / unpost / confirm duplicate)
  // WITHOUT throwing the user out of their place (owner 2026-09-19: "user must always stay at the
  // transaction they are busy with", "do not mess with the UX when user is working"). Unlike
  // loadRegister(true) — which resets to page 0 and collapses an infinite-scrolled list back to one
  // page, so the scroll position no longer exists — this re-pulls exactly the pages that were already
  // loaded, then puts the user's row back where it was on screen (restorePlace). Reached through
  // refresh(place): it must run INSTEAD of loadRegister(true), never after it (after a collapse,
  // regPage and the position are already gone).
  async function reloadKeepingView(place){
    if(!sel.value) return;
    const pagesLoaded = Math.max(1, regPage);          // how many pages are currently on screen
    const gen = ++regGen;                              // supersede any in-flight page load
    regLoading.value = true;
    const per = regView.value ? '100' : '50';
    const acc = [];
    try {
      for(let pg = 0; pg < pagesLoaded; pg++){
        const p = new URLSearchParams({ page: String(pg + 1), per_page: per });
        if(regQ.trim()) p.set('q', regQ.trim());
        if(regFrom) p.set('from', regFrom);
        if(regTo) p.set('to', regTo);
        if(regView.value) p.set('posted', regView.value);
        if(regGaps.value) p.set('gaps', '1');
        if(regTransfers.value) p.set('transfers', '1');
        p.set('sort', regSort.value.key); p.set('dir', regSort.value.dir);
        const r = await api.get(P(`/bank-accounts/${sel.value}/register?`) + p);
        if(gen !== regGen) return;                     // a newer reset won — drop this stale rebuild
        acc.push(...(r.data || []));
        regTotal.value = r.total || 0;
        regPostedTotal.value = r.posted_total || 0;
        regChecks.value = r.transfer_checks || 0;
        regGapCount.value = r.gap_count || 0;
        if('opening_balance' in r){ regOpening.value = r.opening_balance; regOpeningDate.value = r.opening_balance_date || ''; }
        regMoves.value = r.moves || [];
        regPage = pg + 1;
        if(regPage >= (r.total_pages || 1)){ regDone = true; break; }
        regDone = false;
      }
      regRows.value = acc;
    } catch { /* keep what is loaded */ }
    finally { if(gen === regGen) regLoading.value = false; }
    // put the user's row back NOW: tina4 re-renders synchronously on a signal write outside a batch, so
    // the new rows are already laid out. Deliberately no deferred (rAF) second pass — a hidden tab
    // runs it late, after the user changed the list, and it then "restores" a stale place.
    restorePlace(place);
  }
  // infinite scroll: nearing the register sentinel pulls the next page. ONE document (capture)
  // listener (the previous screen instance's handler is replaced, never stacked).
  function onRegScroll(){
    // lazy-load: nearing the sentinel pulls the next page — every view, no "Load more" button
    // (Andre 2026-09-18: "lazy loading — make sure it just loads"). The sentinel test is
    // viewport-relative (getBoundingClientRect) so it holds whether the WINDOW or an inner
    // container is the thing scrolling — the listener is capture-phase on document for the same reason.
    const end = document.getElementById('cbRegEnd');
    if(!end || regDone || regLoading.value) return;
    if(end.getBoundingClientRect().top < window.innerHeight + 600) loadRegister(false);
  }
  // column-header sort: same column flips dir; a new column starts desc (dates/amounts) or asc (text).
  // Server-side (loadRegister re-queries), so the order holds across every infinite-scroll page.
  function setRegSort(key){
    regSort.value = nextSort(regSort.value, key, (key === 'ref' || key === 'description') ? 'asc' : 'desc');
    loadRegister(true);
  }
  // capture-phase on document: scroll events do NOT bubble, so a window-only listener misses the
  // case where an inner container (not the window) is what actually scrolls — capture catches both.
  if(window.__cbRegScroll) document.removeEventListener('scroll', window.__cbRegScroll, true);
  window.__cbRegScroll = onRegScroll;
  document.addEventListener('scroll', onRegScroll, { passive: true, capture: true });
  function defaultTax(){
    const ref = reference.value || {};
    if(!fTaxId){
      const none = (ref.tax_codes || []).find(t => t.code === '00') ||
                   (ref.tax_codes || []).find(t => !Number(t.rate));
      if(none) fTaxId = String(none.id);              // live defaults to "00 - No Tax"
    }
  }
  async function loadAll(){
    loading.value = true;
    try { await Promise.all([loadAccounts(), loadRef()]); defaultTax(); revealCard(sel.value); await loadLines(); }
    catch(err){ setToast((err.data && err.data.error) || t('cashbook.err_load', 'Could not load the cashbook'), 'err'); }
    loading.value = false;
    loadHeld();                              // statements held for their account or co-op wait, listed, across reloads
    // live restore: a still-running extraction resumes its progress card, and the
    // loaded-imports panel comes back from the server
    try {
      const res = await api.get(P('/bank-imports/active-job'));
      // jobs[] = every in-flight import (newer API); job = the newest only (older API fallback).
      // Restore a progress row for EACH so a reload mid-batch doesn't drop the queued ones.
      const restore = res.jobs || (res.job ? [res.job] : []);
      if(restore.length){
        importOpen.value = true;
        for(const j of restore){
          setJob(j.id, { id: j.id, stage: j.stage, progress: j.progress, lastProgressAt: Date.now() });
          pollJob(j.id);
        }
      }
    } catch { /* no active job */ }
    // NOTE: the server no longer holds a "review" list (imports flow straight into the register), so
    // there is nothing to load here — a completed import must NOT reopen a review panel.
    // live: auto-focus the date input on page load so capture starts hands-on-keyboard
    setTimeout(() => { const d = document.getElementById('cbDate'); d && d.focus(); }, 200);
  }
  loadAll();

  // The AI ask bar / reconcile-to-chat surface is OUT (Andre 2026-08-24: "no more
  // chatbot — focus on the programme working"). The API's ask endpoint stays dormant.

  function selectAccount(id){
    addOpen.value = false;                 // picking a card leaves the add-account flow
    revealCard(id);                        // a co-op's section cashbook shows under its open co-op
    try { sessionStorage.setItem(SEL_KEY, String(id)); } catch { /* storage off */ }
    if(sel.value === id) return;
    sel.value = id; onlyUnalloc.value = false; selected.value = new Set();
    editId.value = 0; fRef = '';
    // live selectCashbook: capture continues immediately — focus the GL search (no-op in Auto)
    loadLines().then(() => { const g = document.getElementById('cbGlSearch'); g && g.focus(); });
  }
  // place (from placeOf): a ROW action reloads WITHOUT collapsing the lazy register and puts the view
  // back exactly where the user acted (see reloadKeepingView). No place → the normal page-1 reload.
  async function refresh(place){ await Promise.all([loadAccounts(true), loadLines(place)]); }

  function copyIntake(){
    try { navigator.clipboard.writeText(intakeAddress()); setToast(t('cashbook.address_copied', 'Address copied')); }
    catch { setToast(t('cashbook.copy_failed', 'Could not copy'), 'warn'); }
  }

  // ── floating layer (typeahead dropdown + allocation popover) — imperative ──
  const floatRoot = mountFloatRoot();   // shared layer — ONE document listener app-wide
  const closeFloat = closeFloatRoot;

  function positionNear(el, anchor, width){
    const r = anchor.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) + 'px';
    el.style.zIndex = 300;
    floatRoot.appendChild(el);
    const h = el.offsetHeight, below = window.innerHeight - r.bottom - 8;
    el.style.top = (below >= h ? r.bottom + 4 : Math.max(8, r.top - h - 4)) + 'px';
  }

  // Searchable "Move to…" cashbook picker — replaces the native <select> so a long bank list is
  // type-to-filter, not scroll-to-find (owner 2026-08-26: "that dropdown must be searchable"). Mirrors
  // showGlDrop: a [data-float-anchor] text input drives a floating cb-dropdown. `pool` is the candidate
  // accounts, `onPick(account)` commits the id; the label is restored from the id on re-render/blur.
  const acctName = id => { const a = accounts.value.find(x => String(x.id) === String(id)); return a ? a.name : ''; };
  // Lines move only to an account in the SAME currency (no currency = the company base): a moved line
  // keeps its figure, so € 100 moved to a rand account would read R 100 (owner 2026-10-19).
  const sameCurrency = (a, b) => {
    const base = (reference.value || {}).base_currency_id;
    const cur = x => String((x && x.currency_id !== null && x.currency_id !== undefined && x.currency_id !== '')
      ? x.currency_id : (base ?? ''));
    return cur(a) === cur(b);
  };
  const moveTargets = acct => accounts.value.filter(a => a.id !== acct.id && sameCurrency(a, acct));
  // A redraw of view() rebuilds the box a dropdown was opened from, so a pick made after one goes into
  // the box on the page by its id, not the removed one the dropdown still holds (it would never show).
  const onPage = box => box.isConnected ? box : (document.getElementById(box.id) || box);
  function showMoveDrop(anchor, pool, onPick, showAll){
    const qy = showAll ? '' : anchor.value.trim().toLowerCase();
    const matches = (qy ? pool.filter(a => (a.name || '').toLowerCase().includes(qy)) : pool.slice()).slice(0, 40);
    closeFloat();
    const drop = document.createElement('div');
    drop.className = 'cb-dropdown';
    if(!matches.length){ drop.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>'; }
    matches.forEach(a => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.textContent = a.name;
      item.onclick = () => { onPick(a); onPage(anchor).value = a.name; closeFloat(); };
      drop.appendChild(item);
    });
    positionNear(drop, anchor, 300);
    if(qy) highlightFirst(drop);   // pre-highlight top match only when filtering, so Enter is instant
  }
  const moveDropKeydown = e => dropdownKeydown(e, floatRoot.querySelector('.cb-dropdown'), { wrap: false, onEscape: closeFloat });

  // quick-add GL / contact typeahead (live filterGl: local, balances shown)
  function glCandidates(q){
    const ref = reference.value || {}; q = q.toLowerCase();
    if(fDest === 'D' || fDest === 'C'){
      return (ref.contacts || []).filter(c =>
        (fDest === 'D' ? c.is_customer : c.is_supplier) &&
        (c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))).slice(0, 12);
    }
    return (ref.accounts || []).filter(a => {
      if(a.is_bank || !a.is_postable) return false;
      if(a.code === '0000.000' || a.code === '0000') return false;   // live: the unallocated placeholder is never pickable
      if(fDest === 'G' && a.control_type) return false;
      if(fDest === 'V' && a.id !== ref.vat_control_account_id) return false;
      return a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q);
    }).slice(0, 12);
  }
  let glMatches = [];
  const glCats = signal(null);                 // lazy-loaded account categories for inline create
  async function ensureGlCats(){
    if(glCats.value) return glCats.value;
    try { const r = await api.get(P('/account-categories')); glCats.value = r.data || []; }
    catch { glCats.value = []; }
    return glCats.value;
  }
  function showGlDrop(anchor){
    const q = anchor.value.trim();
    closeFloat();
    if(!q) { glMatches = []; return; }
    glMatches = glCandidates(q);
    const ref = reference.value || {};
    const drop = document.createElement('div');
    drop.className = 'cb-dropdown';
    if(!glMatches.length){ drop.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>'; }
    glMatches.forEach(m => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';   // live: no pre-highlight — arrows opt in
      if(fDest === 'D' || fDest === 'C'){
        // live: signed balance on every contact row; archived contacts render
        // dimmed with a Disabled tag but stay selectable
        if(m.is_active === false) item.className += ' cb-contact-off';
        item.innerHTML = `<span><span class="mono">${m.code}</span> ${m.name}` +
          (m.is_active === false ? ' <span class="cb-off-tag">' + t('common.disabled', 'Disabled') + '</span>' : '') +
          `</span><span class="mono cb-drop-bal ${Number(m.balance || 0) < 0 ? 't-out' : 't-in'}">${fmtRS(m.balance || 0)}</span>`;
        item.onclick = () => pickContact(m, anchor);
      } else {
        const bal = (ref.balances || {})[String(m.id)] || 0;
        item.innerHTML = `<span><span class="mono">${gcode(m.code)}</span> ${m.name}</span>` +
          `<span class="mono cb-drop-bal">${fmtRS(bal)}</span>`;
        item.onclick = () => pickGl(m, anchor);
      }
      drop.appendChild(item);
    });
    // GL allocation: always offer an inline "create account" (the fix for a code that has no match,
    // e.g. 6120) — the account picker is the natural place to add the missing GL account.
    if(fDest === 'G'){
      const add = document.createElement('div');
      add.className = 'cb-drop-item cb-drop-add';
      add.innerHTML = '<span>＋ ' + t('cashbook.create_account', 'Create account') +
        (q ? ' “' + q.replace(/[<>]/g, '') + '”' : '') + '</span>';
      add.onclick = () => openCreateAccount(q, anchor);
      drop.appendChild(add);
    }
    positionNear(drop, anchor, 350);
  }
  const _esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  async function openCreateAccount(qRaw, anchor){
    const q = (qRaw || '').trim();
    const cats = await ensureGlCats();
    if(!cats.length){ setToast(t('cashbook.no_cats', 'No account categories available'), 'warn'); return; }
    closeFloat();
    const isCode = /^[0-9A-Za-z]{1,4}$/.test(q);
    // default to an expense (debit) group — the common case when allocating a bank payment; the
    // user picks another category from the grouped list when it's income/asset/etc.
    const defCat = (cats.find(c => c.normal_side === 'debit') || cats[0]).id;
    const byRoot = {};
    cats.forEach(c => { (byRoot[c.root_name] = byRoot[c.root_name] || []).push(c); });
    const optHtml = Object.keys(byRoot).map(rn =>
      '<optgroup label="' + _esc(rn) + '">' + byRoot[rn].map(c =>
        '<option value="' + c.id + '"' + (c.id === defCat ? ' selected' : '') + '>' +
        _esc(c.code) + ' — ' + _esc(c.name) + '</option>').join('') + '</optgroup>').join('');
    const box = document.createElement('div');
    box.className = 'cb-dropdown cb-create-acc';
    box.innerHTML =
      '<div class="cb-create-row"><label>' + t('cashbook.acc_code', 'Code') + '</label>' +
      '<input class="form-control cb-in" id="cbNewCode" maxlength="4" value="' + (isCode ? _esc(q) : '') + '" placeholder="e.g. 6120"></div>' +
      '<div class="cb-create-row"><label>' + t('cashbook.acc_name', 'Name') + '</label>' +
      '<input class="form-control cb-in" id="cbNewName" value="' + (isCode ? '' : _esc(q)) + '" placeholder="e.g. Cell Phone"></div>' +
      '<div class="cb-create-row"><label>' + t('cashbook.acc_cat', 'Category') + '</label>' +
      '<select class="form-control cb-in" id="cbNewCat">' + optHtml + '</select></div>' +
      '<div class="cb-create-actions"><button class="btn btn-sm btn-primary" id="cbNewSave">' + t('common.create', 'Create') + '</button>' +
      '<button class="btn btn-sm btn-outline" id="cbNewCancel">' + t('common.cancel', 'Cancel') + '</button></div>';
    positionNear(box, anchor, 300);
    const nameEl = box.querySelector('#cbNewName'); if(nameEl && !isCode) nameEl.focus();
    else { const codeEl = box.querySelector('#cbNewCode'); codeEl && codeEl.focus(); }
    box.querySelector('#cbNewCancel').onclick = () => { closeFloat(); anchor && anchor.focus(); };
    box.querySelector('#cbNewSave').onclick = async () => {
      const code = box.querySelector('#cbNewCode').value.trim();
      const name = box.querySelector('#cbNewName').value.trim();
      const category_id = box.querySelector('#cbNewCat').value;
      if(!name){ setToast(t('cashbook.acc_name_req', 'Enter an account name'), 'warn'); return; }
      try {
        const res = await api.post(P('/accounts'), { name, category_id, code: code || undefined });
        setToast(t('cashbook.acc_created', 'Account created'), 'ok');
        glCats.value = null;                                        // categories unchanged, but drop the cache lazily
        try { reference.value = await api.get(P('/bank-reference')); } catch { /* keep old ref */ }
        pickGl({ id: res.id, code: res.code, name: res.name, default_tax_code_id: null }, anchor);
      } catch(e){
        setToast((e.data && e.data.error) || e.message || t('cashbook.acc_create_err', 'Could not create the account'), 'err');
      }
    };
  }
  function pickGl(account, anchor){
    fGlId = account.id; fContactId = 0;
    fGlLabel = gcode(account.code) + ' — ' + account.name;
    onPage(anchor).value = fGlLabel;
    if(account.default_tax_code_id){ fTaxId = String(account.default_tax_code_id); syncTaxSelect(); }
    closeFloat(); updateVatPreview();
    const refIn = document.getElementById('cbRef');
    const descIn = document.getElementById('cbDesc');
    const next = fRef && descIn ? descIn : refIn;
    next && next.focus();
  }
  function pickContact(contact, anchor){
    const ref = reference.value || {};
    fContactId = contact.id;
    fGlId = fDest === 'D' ? ref.ar_control_account_id : ref.ap_control_account_id;
    fGlLabel = contact.code + ' — ' + contact.name;
    onPage(anchor).value = fGlLabel;
    closeFloat();
    const refIn = document.getElementById('cbRef'); refIn && refIn.focus();
  }
  function glKeydown(e){
    const items = floatRoot.querySelectorAll('.cb-drop-item');
    const current = floatRoot.querySelector('.cb-drop-item.highlighted');
    // live: Tab or Enter auto-selects ONLY when exactly one match — never a silent
    // first-of-many pick; with several matches Tab moves on and Enter waits for arrows
    if((e.key === 'Tab' || e.key === 'Enter') && glMatches.length === 1 && items.length){
      e.preventDefault(); items[0].click(); return;
    }
    if(e.key === 'ArrowDown' || e.key === 'ArrowUp'){
      e.preventDefault();
      if(!items.length) return;
      let idx = Array.prototype.indexOf.call(items, current);
      if(current) current.classList.remove('highlighted');
      idx = e.key === 'ArrowDown' ? (idx + 1) % items.length : (idx <= 0 ? items.length - 1 : idx - 1);
      items[idx].classList.add('highlighted');
      items[idx].scrollIntoView({ block: 'nearest' });
    } else if(e.key === 'Enter'){
      if(current){ e.preventDefault(); current.click(); }
    } else if(e.key === 'Escape'){ closeFloat(); }
  }
  function syncTaxSelect(){ const el = document.getElementById('cbTax'); if(el) el.value = fTaxId; updateVatPreview(); }
  function updateVatPreview(){
    const ref = reference.value || {};
    const tc = (ref.tax_codes || []).find(t => String(t.id) === String(fTaxId));
    const typed = parseAmount(fAmount, openMarks()) || 0;
    const vat = fDest === 'V' ? typed : inclusiveTax(typed, tc ? tc.rate : 0);
    const el = document.getElementById('cbVatLine');
    if(el){ el.style.display = vat > 0 ? '' : 'none'; el.querySelector('span').textContent = fmtAmountOnly(vat); }
  }

  function setDest(dest){
    fDest = dest; fGlId = 0; fContactId = 0; fGlLabel = '';
    const ref = reference.value || {};
    const search = document.getElementById('cbGlSearch');
    document.querySelectorAll('.cb-form .cb-dest-pill').forEach(p => p.classList.toggle('active', p.dataset.dest === dest));
    if(search){
      search.value = '';
      search.placeholder = { G: t('cashbook.gl_ph', 'GL account… e.g. Rent'), D: t('cashbook.debtors_ph', 'Search debtors… e.g. Mountain'),
                            C: t('cashbook.creditors_ph', 'Search creditors… e.g. Telkom'), V: t('cashbook.vat_control_ph', 'VAT control') }[dest];
    }
    if(dest === 'V' && ref.vat_control_account_id){
      const vat = (ref.accounts || []).find(a => a.id === ref.vat_control_account_id);
      if(vat && search){ pickGl(vat, search); return; }
    }
    search && search.focus();
  }

  // ── quick-add ──────────────────────────────────────────────────────────────
  async function addEntry(){
    if(!sel.value) return setToast(t('cashbook.select_bank', 'Select a bank account first'), 'warn');
    // live: every validation failure puts the cursor ON the offending field
    const scold = (msg, id) => {
      setToast(msg, 'warn');
      const el = document.getElementById(id); el && el.focus();
    };
    if(!fGlId && (fDest === 'D' || fDest === 'C')) return scold(t('cashbook.select_contact', 'Select a contact'), 'cbGlSearch');
    if(!fGlId && fDest === 'V') return scold(t('cashbook.no_vat_account', 'No VAT control account'), 'cbGlSearch');
    if(!fGlId && fDest === 'G') return scold(t('cashbook.select_gl', 'Select a GL account'), 'cbGlSearch');
    if(!fDate) return scold(t('cashbook.date_required', 'Date is required'), 'cbDate');
    if(!fDesc.trim()) return scold(t('cashbook.desc_required', 'Description is required'), 'cbDesc');
    // the SHARED parseAmount, like the inline edit: "1 000,00", "R 1,000.00" and "€ 12,50" all read as typed — a
    // browser number box read "1000,00" as 100000 in an English browser (owner 2026-10-19: euro figures read right)
    const amount = parseAmount(fAmount, openMarks());
    if(!(amount > 0)) return scold(t('cashbook.amount_positive', 'Amount must be greater than zero'), 'cbAmount');
    busy.value = 'add';
    let added = false;
    try {
      const body = { entry_type: tab.value, txn_date: fDate, description: fDesc.trim(),
                     amount, reference: fRef.trim() || null, dest: fDest === 'V' ? 'V' : null,
                     tax_code_id: fTaxId ? Number(fTaxId) : null,
                     cost_centre_id: fCcId ? Number(fCcId) : null,
                     diesel_litres: dieselOn.value && fDiesel ? Number(fDiesel) : null };
      if(fDest === 'D' || fDest === 'C') body.contact_id = fContactId;
      else if(fDest !== 'V') body.allocated_account_id = fGlId;
      const res = await api.post(P(`/bank-accounts/${sel.value}/entries`), body);
      // the toast names the amount in the ACCOUNT's currency (the API's own text assumes the rand)
      const acctNow = accounts.value.find(a => a.id === sel.value);
      const amtTxt = fmtMoney(amount, (acctNow && (acctNow.currency_symbol || acctNow.currency_code)) || baseCurrencySymbol());
      setToast(res && res.success !== false
        ? (tab.value === 'deposit' ? t('cashbook.deposit_staged', 'Deposit of {amt} staged') : t('cashbook.payment_staged', 'Payment of {amt} staged')).replace('{amt}', amtTxt)
        : t('cashbook.entry_staged', 'Entry staged'));
      if(fIncRef && fRef) fRef = incrementRef(fRef);
      if(!fKeepDesc) fDesc = '';
      fGlId = 0; fContactId = 0; fGlLabel = ''; fAmount = ''; fDiesel = '';
      added = true;
      await refresh();
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.add_failed', 'Add failed'), 'err'); }
    busy.value = '';
    // focus AFTER the busy re-render replaced the form's DOM, or it lands on a dead node
    if(added){ const dateIn = document.getElementById('cbDate'); dateIn && dateIn.focus(); }
  }

  // ── row actions ────────────────────────────────────────────────────────────
  async function flipLine(id){
    try { await api.post(P(`/bank-lines/${id}/flip`), {}); setToast(t('cashbook.entry_flipped', 'Entry flipped')); await refresh(); }
    catch(err){ setToast((err.data && err.data.error) || t('cashbook.flip_failed', 'Flip failed'), 'err'); }
  }
  // Register "Jump to" nav (owner 2026-09-21: quickly move around the endless register). Selecting a
  // statement sets the From/To date window to that statement's period and reloads — the register
  // jumps to that point; the clear option removes the window and returns to the full list.
  function jumpToStatement(sid){
    if(sid === '__all__' || !sid){ regFrom = ''; regTo = ''; loadRegister(true); }
    else {
      const s = (regStmts.value || []).find(x => String(x.id) === String(sid));
      if(!s) return;
      regFrom = String(s.period_from || '').slice(0, 10);
      regTo = String(s.period_to || '').slice(0, 10);
      loadRegister(true);
    }
    try { window.scrollTo(0, 0); } catch { /* noop */ }
  }
  // unpost a POSTED (unlocked) line so it returns to unallocated and can be re-allocated. The server
  // refuses when the period is closed (locked lines keep the red lock, no button). Reversible → no confirm.
  async function unpostLine(id){
    const place = placeOf(id);                         // stay on the row being unlocked
    busy.value = 'unpost' + id;
    try {
      await api.post(P(`/bank-lines/${id}/unpost`), {});
      setToast(t('cashbook.entry_unposted', 'Entry unposted — re-allocate it'));
      await refresh(place);
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.unpost_failed', 'Unpost failed'), 'err'); }
    finally { busy.value = ''; }
  }
  async function deleteLine(id){
    busy.value = 'del' + id;
    try {
      const res = await api.del(P(`/bank-lines/${id}`));
      confirmDelId.value = 0;
      await refresh();
      // v2 restore-entry parity: the delete hands back the row, so offer an 8s Undo that re-stages it
      if(res && res.entry) setActionToast(t('cashbook.entry_deleted', 'Entry deleted'), t('common.undo', 'Undo'), () => restoreLine(res.entry));
      else setToast(t('cashbook.entry_deleted', 'Entry deleted'));
    }
    catch(err){ setToast((err.data && err.data.error) || t('cashbook.delete_failed', 'Delete failed'), 'err'); }
    busy.value = '';
  }
  async function restoreLine(entry){
    try { await api.post(P('/bank-lines/restore'), { entry }); setToast(t('cashbook.entry_restored', 'Entry restored')); await refresh(); }
    catch(err){ setToast((err.data && err.data.error) || t('cashbook.restore_failed', 'Could not restore the entry'), 'err'); }
  }
  // v2 is_gap "gap — verify" → verified: clear the balance-gap flag once the user has checked the row
  async function clearGap(id){
    try { await api.post(P('/bank-lines/clear-gap'), { id }); setToast(t('cashbook.gap_verified', 'Gap verified')); await refresh(); }
    catch(err){ setToast((err.data && err.data.error) || t('cashbook.gap_clear_failed', 'Could not clear the gap flag'), 'err'); }
  }
  // every gap warning on the account at once — offered only while it reconciles; the API checks that again and clears
  // the warnings only, never a line (owner 2026-10-28: thousands of warnings on an import that reconciled)
  async function clearAllGaps(){
    busy.value = 'cleargaps';
    try {
      const res = await api.post(P(`/bank-accounts/${sel.value}/clear-gaps`), {});
      const n = (res && res.cleared) || 0;
      setToast(n === 1 ? t('cashbook.gap_cleared_one', '1 gap warning cleared — the line is as it was')
        : fill(t('cashbook.gaps_cleared', '{n} gap warnings cleared — the lines are as they were'), { n }));
      await refresh();
    } catch(err){
      confirmClearGaps.value = false;
      setToast((err.data && err.data.error) || t('cashbook.gaps_clear_failed', 'Could not clear the gap warnings'), 'err');
    }
    finally { busy.value = ''; }
  }
  // confirm a flagged duplicate → clears is_duplicate so the line rejoins the flow and can post
  async function confirmDup(id){
    const place = placeOf(id);
    try { await api.post(P('/bank-lines/confirm-duplicate'), { line_id: id }); setToast(t('cashbook.dup_confirmed', 'Confirmed — no longer a duplicate')); await refresh(place); }
    catch(err){ setToast((err.data && err.data.error) || t('cashbook.dup_confirm_failed', 'Could not confirm the entry'), 'err'); }
  }
  async function clearAllocation(line){
    try {
      const res = await api.post(P('/bank-lines/undo-allocation'), { line_id: line.id });
      setToast(res.restored > 1 ? `Allocation cleared (${res.restored} entries)` : t('cashbook.allocation_cleared', 'Allocation cleared'));
      await refresh();
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.clear_failed', 'Clear failed'), 'err'); }
  }
  // the edit amount is a text field so it can SHOW money (commas + 2 decimals);
  // fmtAmountInput makes the display string; the SHARED parseAmount (formats.js) reads it
  // back — locale-tolerant, so a ZA comma decimal "50,00" is fifty rand, never five thousand
  function fmtAmountInput(v){
    const n = Number(v || 0);
    return Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function startEdit(line){
    editVals = { txn_date: String(line.txn_date || '').slice(0, 10), reference: line.reference || '',
                 description: line.description, amount: fmtAmountInput(line.amount) };
    editId.value = line.id;
  }
  async function saveEdit(id){
    const amt = parseAmount(editVals.amount, openMarks());
    if(amt === null || amt <= 0){ setToast(t('cashbook.valid_amount', 'Enter a valid amount')); return; }
    busy.value = 'edit';
    try {
      // send a clean number (the input carries a formatted string with commas)
      await api.put(P(`/bank-lines/${id}`), { ...editVals, amount: amt });
      editId.value = 0; setToast(t('cashbook.entry_updated', 'Entry updated')); await refresh();
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.update_failed', 'Update failed'), 'err'); }
    busy.value = '';
  }

  // bulk bar
  function toggleSel(id, on){
    const s = new Set(selected.value); on ? s.add(id) : s.delete(id); selected.value = s;
    confirmBulkDel.value = false;         // the selection changed — re-confirm any delete
  }
  function selectAllVisible(rows, on){
    const s = new Set(selected.value);
    rows.forEach(l => on ? s.add(l.id) : s.delete(l.id));
    selected.value = s;
    confirmBulkDel.value = false;
  }
  async function bulkMove(){
    const ids = [...selected.value];
    if(!bulkTarget) return setToast(t('cashbook.select_cashbook', 'Select a cashbook'), 'warn');
    if(Number(bulkTarget) === sel.value) return setToast(t('cashbook.already_here', 'Already in this cashbook'), 'warn');
    const from = accounts.value.find(a => a.id === sel.value), to = accounts.value.find(a => String(a.id) === String(bulkTarget));
    if(from && to && !sameCurrency(from, to))
      return setToast(t('cashbook.move_other_currency', 'Lines can only move to an account in the same currency'), 'warn');
    busy.value = 'bulk';
    try {
      const res = await api.post(P('/bank-lines/move'), { ids, target_bank_account_id: Number(bulkTarget) });
      setToast(res.message || t('cashbook.moved', 'Moved')); selected.value = new Set(); await refresh();
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.move_failed', 'Move failed'), 'err'); }
    busy.value = '';
  }
  async function bulkFlip(){
    const ids = [...selected.value]; busy.value = 'bulk';
    try {
      for(const id of ids) await api.post(P(`/bank-lines/${id}/flip`), {});
      setToast(ids.length + ' entries flipped');
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.flip_failed', 'Flip failed'), 'err'); }
    selected.value = new Set(); await refresh();
    busy.value = '';
  }
  async function bulkDelete(){
    const ids = [...selected.value]; busy.value = 'bulk'; confirmBulkDel.value = false;
    try {
      for(const id of ids) await api.del(P(`/bank-lines/${id}`));
      setToast(ids.length + ' entries deleted');
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.delete_failed', 'Delete failed'), 'err'); }
    selected.value = new Set(); await refresh();
    busy.value = '';
  }

  // ── allocation popover (imperative, live parity) ──────────────────────────
  // Register rows only now (the import-review preview flow that used a local onPick callback is gone).
  let rulesCache = null;                   // learned rules feed the popover's rule pill
  function openAllocPop(line, anchor, initState){
    closeFloat();
    const ref = reference.value || {};
    // a POSTED line re-allocates through the reallocate-posted path (v2 parity) — General GL only;
    // moving it to a debtor/creditor/VAT changes control accounts + aging and needs an unpost first.
    const isPosted = !!(line && line.status === 'posted');
    const pop = document.createElement('div');
    pop.className = 'alloc-pop';
    // both toggles default ON, but must SURVIVE a Debtor/Creditor → ← back round-trip: the back
    // button re-opens this popover, so thread the current checkbox state through initState instead
    // of silently re-ticking an untick (owner 2026-09-08). popDest still returns to General on back.
    const st = initState || {};
    let saveRule = typeof st.saveRule === 'boolean' ? st.saveRule : true;
    // "Apply to all similar" is OPT-IN (default OFF). Allocating one entry allocates ONE — otherwise,
    // now that matching is accurate, one click silently sweeps every same-payee sibling and they all
    // vanish from the Unallocated filter at once (owner 2026-09-19: "allocate one → all of them
    // disappear"). The checkbox is right there when a bulk sweep IS wanted; a saved rule still
    // auto-allocates future imports either way.
    let applySimilar = typeof st.applySimilar === 'boolean' ? st.applySimilar : false;
    let popDest = 'G';

    const header = document.createElement('div');
    header.className = 'alloc-pop-head';
    header.textContent = isPosted
      ? t('cashbook.reallocate_posted', 'Re-allocate posted entry')
      : t('cashbook.allocate_gl', 'Allocate GL Account');
    pop.appendChild(header);

    const destRow = document.createElement('div');
    destRow.className = 'alloc-dest-row';
    pop.appendChild(destRow);

    const sugg = document.createElement('div');
    sugg.className = 'alloc-sugg';
    pop.appendChild(sugg);

    const searchWrap = document.createElement('div');
    searchWrap.innerHTML = '<input type="text" class="form-control cb-in" placeholder="' + t('cashbook.gl_search_ph', 'Search GL account… e.g. Rent') + '" autocomplete="off">';
    const search = searchWrap.firstChild;
    pop.appendChild(searchWrap);
    const results = document.createElement('div');
    results.className = 'alloc-results';
    pop.appendChild(results);

    const opts = document.createElement('div');
    opts.className = 'alloc-opts';
    // Allocating a GL now ALWAYS learns a rule (owner 2026-09-14: "it must apply the rule if the
    // user allocates a GL code") — no Save-as-rule checkbox to forget to tick. The API skips a stem
    // that was allocated more than one way and reports it. "Apply to all similar" stays opt-in.
    opts.innerHTML = '<label class="check-line"><input type="checkbox" ' + (applySimilar ? 'checked' : '') + '> ' + t('cashbook.apply_all_similar', 'Apply to all similar') + '</label>';
    const similarBox = opts.querySelector('input[type="checkbox"]');
    similarBox.onchange = () => { applySimilar = similarBox.checked; };
    pop.appendChild(opts);

    async function allocate(body){
      const place = placeOf(line.id);      // where the user chose to act — the view comes back here after
      closeFloat();
      if(isPosted){                        // v2 reallocate-posted: General GL only, in place
        if(body.contact_id || body.dest === 'V' || !body.account_id){
          setToast(t('cashbook.reallocate_posted_general', 'A posted entry can only move to a General GL — unpost it first to use a debtor, creditor or VAT.'), 'warn');
          return;
        }
        try {
          const res = await api.post(P(`/bank-lines/${line.id}/reallocate-posted`),
            { account_id: body.account_id, apply_to_similar: applySimilar });
          setToast(res.updated > 1 ? `Re-allocated ${res.updated} posted entries` : t('cashbook.reallocated', 'Re-allocated'));
          await refresh(place);            // posted rows live in the register — keep the user's place
        } catch(err){ setToast((err.data && err.data.error) || t('cashbook.allocation_failed', 'Allocation failed'), 'err'); }
        return;
      }
      try {
        // Auto mode: the seamless flow posts the line to the ledger the moment it is allocated
        // (post:true → server posts this line in the same call). Manual capture stays post:false —
        // those lines are posted in a batch via the "Post to Ledger" button.
        const auto = mode.value === 'auto';
        const res = await api.post(P(`/bank-lines/${line.id}/allocate`),
          { ...body, save_as_rule: saveRule, apply_to_similar: applySimilar, post: auto });
        let msg = res.updated > 1 ? `Allocated ${res.updated} similar entries` : t('cashbook.allocated', 'Allocated');
        if(res.rule_learned) msg += ' · rule saved';
        else if(res.rule_conflict) msg += ` — no rule: "${res.rule_conflict}" allocated more than one way`;
        if(res.rule_demoted) msg += ' · a conflicting rule was disabled';
        if(res.posted) msg += res.posted > 1 ? ` · ${res.posted} posted` : ' · posted';
        setToast(msg);
        if(res.post_error) setToast(res.post_error, 'warn');   // allocated, but the ledger post failed
        await refresh(auto ? place : null);   // Auto: the register is the list on screen — keep the user's place
      } catch(err){ setToast((err.data && err.data.error) || t('cashbook.allocation_failed', 'Allocation failed'), 'err'); }
    }

    function contactStep(kind){
      pop.innerHTML = '';
      const head = document.createElement('div');
      head.className = 'alloc-pop-head';
      head.innerHTML = '<button type="button" class="alloc-back">←</button> ' +
        (kind === 'D' ? t('bank_import.select_debtor', 'Select Debtor') : t('bank_import.select_creditor', 'Select Creditor'));
      head.querySelector('button').onclick = e => {
        e.stopPropagation();               // the bubble would hit the document closer with a detached target
        closeFloat(); openAllocPop(line, anchor, { saveRule, applySimilar });
      };
      pop.appendChild(head);
      const cin = document.createElement('input');
      cin.type = 'text'; cin.className = 'form-control cb-in';
      cin.placeholder = t('cashbook.contact_search_ph', 'Search contact… e.g. Mountain'); cin.autocomplete = 'off';
      pop.appendChild(cin);
      const list = document.createElement('div');
      list.className = 'alloc-results';
      pop.appendChild(list);
      const renderContacts = q => {
        list.innerHTML = '';
        (ref.contacts || [])
          .filter(c => (kind === 'D' ? c.is_customer : c.is_supplier) &&
                       (!q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)))
          .slice(0, 8).forEach(c => {
            const item = document.createElement('div');
            item.className = 'cb-drop-item' + (c.is_active === false ? ' cb-contact-off' : '');
            item.innerHTML = `<span><span class="mono">${c.code}</span> ${c.name}` +
              (c.is_active === false ? ' <span class="cb-off-tag">' + t('common.disabled', 'Disabled') + '</span>' : '') +
              `</span><span class="mono cb-drop-bal ${Number(c.balance || 0) < 0 ? 't-out' : 't-in'}">${fmtRS(c.balance || 0)}</span>`;
            item.onclick = () => allocate({ contact_id: c.id });
            list.appendChild(item);
          });
        if(!list.children.length) list.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
        // v2 "➕ NEW debtor/creditor" parity: a typed name that isn't a contact yet can be created
        // here and allocated in one step.
        const raw = cin.value.trim();
        if(raw){
          const add = document.createElement('div');
          add.className = 'cb-drop-item cb-drop-new';
          add.innerHTML = `<span>➕ ${t('cashbook.create_named', 'Create')} ${kind === 'D'
            ? t('cashbook.debtor', 'Debtor') : t('cashbook.creditor', 'Creditor')} “<b>${raw.replace(/</g, '&lt;')}</b>”</span>`;
          add.onclick = () => allocate({ new_contact_name: raw, dest: kind });
          list.appendChild(add);
        }
        highlightFirst(list);   // top match pre-highlighted so Enter selects instantly
      };
      cin.oninput = () => renderContacts(cin.value.trim().toLowerCase());
      cin.addEventListener('keydown', e => dropdownKeydown(e, list, { wrap: false, onEscape: closeFloat }));
      renderContacts('');
      setTimeout(() => cin.focus(), 30);
    }

    function renderDests(){
      destRow.innerHTML = '<span class="alloc-dest-label">' + t('cashbook.dest', 'Destination') + ':</span>';
      const dests = [['G', t('bank_import.general', 'General')]];
      // a posted line moves to a General GL only (see allocate()); D/C/V need an unpost first
      if(!isPosted && ref.ar_control_account_id) dests.push(['D', t('cashbook.debtor', 'Debtor')]);
      if(!isPosted && ref.ap_control_account_id) dests.push(['C', t('cashbook.creditor', 'Creditor')]);
      if(!isPosted && ref.vat_registered && ref.vat_control_account_id) dests.push(['V', t('common.vat', 'VAT')]);
      dests.forEach(([code, label]) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cb-dest-pill' + (popDest === code ? ' active' : '');
        b.dataset.dest = code; b.textContent = label;
        b.onclick = e => {
          e.stopPropagation(); popDest = code;
          if(code === 'D' || code === 'C') contactStep(code);
          else if(code === 'V') allocate({ dest: 'V' });
          else renderDests();
        };
        destRow.appendChild(b);
      });
    }
    renderDests();

    const glItem = account => {
      const bal = (ref.balances || {})[String(account.id)] || 0;
      const tag = controlTag(account);
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.innerHTML = `<span><span class="mono">${gcode(account.code)}</span> ${account.name}` +
        (tag ? ` <span class="cb-ctl-tag cb-ctl-${tag.toLowerCase()}">${tag}</span>` : '') +
        `</span><span class="mono cb-drop-bal">${fmtRS(bal)}</span>`;
      // live: a D/C control pick advances to the contact step, V allocates as VAT,
      // anything else (incl. another BANK account — a transfer) allocates directly.
      // stopPropagation: the rebuild detaches this row mid-bubble and the document
      // click-closer would kill the popover (the ← back-button trap, same cause)
      item.onclick = e => {
        e.stopPropagation();
        if(tag === 'D' || tag === 'C') contactStep(tag);
        else if(tag === 'V') allocate({ dest: 'V' });
        else allocate({ account_id: account.id });
      };
      return item;
    };

    // live pools: everything postable except the unallocated placeholder — bank
    // accounts (transfers) and control accounts included, tagged [D]/[C]/[S]/[V]
    const candidates = (ref.accounts || []).filter(a =>
      a.is_postable && a.code !== '0000.000' && a.code !== '0000');
    const renderSuggestions = () => {
      sugg.innerHTML = '';
      const desc = String(line.description || '').toUpperCase();
      const rule = (rulesCache || []).find(r => r.match_stem &&
        desc.includes(String(r.match_stem).toUpperCase()));
      const ruleAccount = rule && rule.account_id
        ? candidates.find(a => a.id === rule.account_id) : null;
      if(ruleAccount){
        const item = glItem(ruleAccount);
        item.title = `Matches your rule "${rule.match_stem}"`;
        item.insertAdjacentHTML('beforeend', '<span class="alloc-rule-check t-in">✓</span>');
        sugg.appendChild(item);
      } else if(rule && rule.contact_id){
        // live: a contact-target rule surfaces first too, with the D/C control hint
        const kind = (ref.contacts || []).some(c => c.id === rule.contact_id && c.is_customer) ? 'D' : 'C';
        const item = document.createElement('div');
        item.className = 'cb-drop-item';
        item.title = `Matches your rule "${rule.match_stem}"`;
        item.innerHTML = `<span>${rule.contact_name || t('cashbook.contact', 'Contact')} ` +
          `<span class="cb-ctl-tag cb-ctl-${kind.toLowerCase()}">${kind}</span>` +
          `<span class="cb-gl-desc"> ${kind === 'D' ? t('cashbook.debtor_control', 'Debtor control') : t('cashbook.creditor_control', 'Creditor control')}</span></span>` +
          '<span class="alloc-rule-check t-in">✓</span>';
        item.onclick = () => allocate({ contact_id: rule.contact_id });
        sugg.appendChild(item);
      }
      scoreGL(line.description, candidates, ref.gl_usage)
        .filter(s => !ruleAccount || s.account.id !== ruleAccount.id).slice(0, 3)
        .forEach(s => sugg.appendChild(glItem(s.account)));
      if(!sugg.children.length) sugg.innerHTML = '<div class="cb-drop-muted alloc-nosugg">' + t('cashbook.no_suggestions', 'No suggestions') + '</div>';
    };
    renderSuggestions();
    if(!rulesCache) api.get(P('/bank-rules'))
      .then(r => { rulesCache = r.data || []; renderSuggestions(); })
      .catch(() => { rulesCache = []; });

    search.oninput = () => {
      const q = search.value.trim().toLowerCase();
      results.innerHTML = '';
      if(!q) return;
      candidates.filter(a => a.code.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
        .slice(0, 8).forEach(a => results.appendChild(glItem(a)));
      if(!results.children.length) results.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
      highlightFirst(results);   // live: top match pre-highlighted so Enter selects it instantly
    };
    // live parity: arrow keys move through the results, Enter picks the highlighted one
    search.addEventListener('keydown', e => dropdownKeydown(e, results, { wrap: false, onEscape: closeFloat }));

    positionNear(pop, anchor, 340);
    setTimeout(() => search.focus(), 30);
  }

  // ── header strip actions ───────────────────────────────────────────────────
  async function rerunAllocations(){
    busy.value = 'rerun';
    try {
      const res = await api.post(P('/bank-lines/reallocate'), { bank_account_id: sel.value });
      // lines a rule was not applied to (the AI rule helper said another payee's, or nobody checked them) get a note
      // in the register: refresh to show it, and show the API's message, which says how many were left open and why
      const leftOpen = (res.held_back || 0) + (res.unchecked || 0);
      if(res.updated > 0 || leftOpen > 0){
        setToast(res.message || `Allocated ${res.updated} of ${res.total} entries`, leftOpen ? 'warn' : 'ok');
        await refresh();
      }
      else if(res.total && reference.value && reference.value.ai_configured === false)
        setToast(t('cashbook.no_match_no_key', 'No rule or contact matched. Add an AI API key to auto-allocate the rest with AI.'), 'warn');
      else setToast(t('cashbook.no_new_matches', 'No new matches found'), 'warn');
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.rerun_failed', 'Re-run failed'), 'err'); }
    busy.value = '';
  }
  async function fetchEmailImports(){
    busy.value = 'fetchmail';
    try {
      const res = await api.post(P('/bank-imports/fetch-email'), {});
      const jobIds = (res.jobs || []).filter(Boolean);
      if(jobIds.length){
        setToast(t('cashbook.email_importing', 'Found {n} — importing…').replace('{n}', jobIds.length));
        importOpen.value = true;                       // reveal the progress card
        // Track each job the SAME way a drag-drop import does (setJob + pollJob), so the screen
        // shows progress and the register refreshes itself when extraction finishes — the earlier
        // single immediate refresh() fired before the async job completed, so nothing appeared.
        jobIds.forEach(id => {
          setJob(id, { id, stage: 'extracting', progress: 0, lastProgressAt: Date.now(),
                       label: t('cashbook.email_import', 'Email import') });
          pollJob(id);
        });
      }
      else if(res.needs_ai_key) setToast(t('cashbook.email_needs_key', 'Statement email found — add an AI API key (Settings ▸ API Keys) to process it.'), 'warn');
      else if((res.duplicates || []).length) setToast(t('cashbook.email_dupes', 'Those statements were already imported.'), 'warn');
      else setToast(t('cashbook.email_none', 'No new import emails for this company.'), 'warn');
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.email_fetch_failed', 'Could not check email — try again shortly.'), 'err'); }
    busy.value = '';
  }
  async function postToLedger(){
    busy.value = 'post';
    try {
      const res = await api.post(P(`/bank-accounts/${sel.value}/post-to-ledger`), {});
      confirmPost.value = false;
      // a co-op statement that does not tie keeps its lines back (the API says how many): a warning, not a plain done
      setToast(res.message || `Posted ${res.count} entries to the ledger`, res.held ? 'warn' : undefined);
      await refresh();
    } catch(err){
      confirmPost.value = false;
      setToast((err.data && err.data.error) || t('cashbook.posting_failed', 'Posting failed'), 'err');
    }
    busy.value = '';
  }
  async function savePdfPassword(accountId){
    busy.value = 'pwd';                    // the row's ✓ disables on this flag
    try {
      await api.post(P(`/bank-accounts/${accountId}/pdf-password`), { password: pwdVal });
      pwdOpenId.value = 0; pwdVal = ''; setToast(t('common.saved', 'Saved')); await loadAccounts(true);
    } catch(err){ setToast((err.data && err.data.error) || t('common.save_failed', 'Save failed'), 'err'); }
    busy.value = '';
  }
  // Open the guided add-account wizard. `prefill` (optional) carries a dropped statement's header
  // {account_holder, account_number, bank_name} so a statement-triggered add starts pre-filled.
  // `importAfter` (optional): the HELD needs_account job id — once the wizard creates the account,
  // addAccount imports that job's already-extracted statement into it (no re-extraction).
  async function openAddWizard(prefill, importAfter){
    aName = (prefill && (prefill.account_holder || prefill.bank_name)) || '';
    aNo = (prefill && prefill.account_number) || '';
    aBank = (prefill && prefill.bank_name) || '';
    aCur = '';
    // A held statement names its currency: start the wizard on it, so a foreign statement does not
    // land in a base-currency account and read as base (owner 2026-10-19: no currency bleeds).
    const stCode = String((prefill && prefill.currency) || '').trim().toUpperCase();
    if(stCode){
      const ref = reference.value || {};
      const c = (ref.currencies || []).find(x => String(x.code || '').toUpperCase() === stCode);
      if(c){ if(String(c.id) !== String(ref.base_currency_id)) aCur = String(c.id); }
      else setToast(t('cashbook.stmt_currency_off', 'This statement is in {code}, which is not one of your company’s currencies. Turn it on under Settings ▸ Currencies before you create its account.').replace('{code}', stCode), 'warn');
    }
    // pre-fill the opening balance + as-at date from the statement header when we have them
    aOpening = (prefill && prefill.opening_balance != null && prefill.opening_balance !== '')
      ? String(prefill.opening_balance) : '';
    aOpeningDate = (prefill && prefill.opening_balance_date) || todayIso();
    addImportJobId = importAfter || 0;
    aGlMode.value = 'new'; aGlId = 0; aCode = '';
    aKind.value = 'bank'; coopHeld.value = null; resetCoopForm();
    addStep.value = 1; addNextCode.value = '';
    addOpen.value = true;
    try {
      const r = await api.get(P('/bank-accounts/next-code'));   // suggested unique 9500-series code
      aCode = (r && r.code) || ''; addNextCode.value = aCode;
    } catch { /* suggestion is best-effort; the field stays editable */ }
  }
  function closeAddWizard(){ addOpen.value = false; addStep.value = 1; addImportJobId = 0; coopHeld.value = null; aKind.value = 'bank'; }  // a held statement it asked about keeps waiting in heldImports
  // advance/retreat the wizard; step 1 requires a name before moving on
  function addStepNext(){
    if(addStep.value === 1 && !aName.trim()){ setToast(t('cashbook.name_required', 'Name is required'), 'warn'); return; }
    addStep.value = Math.min(4, addStep.value + 1);
  }

  // ── the new-account form's co-op side (plan §9: "kind (bank vs supplier trading) + co-op group fields") ──
  // A co-op cashbook is one sub-account of a co-op: an existing co-op, or a new one (its co-op, its name, the company's
  // account number there); the sub-account is picked from the ones that co-op's statements carry (never typed); its
  // GL is minted on the liability side under the co-op's header account, so there is no GL step. Three steps: co-op +
  // sub-account · opening balance · confirm.
  // the form's plain fields are written BEFORE its signals: a signal write draws the form, and draws it with the plain
  // fields as they are at that moment
  function resetCoopForm(){
    cName = ''; cRef = ''; cSection = '';
    cMode.value = coops.value.length ? 'existing' : 'new';
    cGroupId.value = coops.value.length === 1 ? String(coops.value[0].id) : '';
    cProvider.value = '';
  }
  const providerName = p => ((coopProviders.value.find(x => x.provider === p) || {}).name) || String(p || '').toUpperCase();
  // a co-op as the form names it: its name, its co-op's when that is not its name already, the account number there
  const coopLabel = c => {
    const prov = c.provider ? providerName(c.provider) : '';
    return [c.name, prov.toLowerCase() === String(c.name || '').trim().toLowerCase() ? '' : prov, c.client_ref]
      .filter(Boolean).join(' · ');
  };
  // the co-op the form is for: {id?, provider, name, client_ref, cashbooks} — the chosen one (of those it may be: a
  // held statement only goes to a co-op of its own), or the new one typed in; "a co-op I have" with none to have is new
  function coopChoice(){
    const held = coopHeld.value;
    const list = held ? linkableCoops(coops.value, (held.read || {}).provider) : coops.value;
    if(cMode.value === 'existing' && list.length){
      const c = list.find(x => String(x.id) === String(cGroupId.value));
      return c ? { id: c.id, provider: c.provider || (held ? (held.read || {}).provider || '' : ''), name: c.name,
                   client_ref: c.client_ref || '', cashbooks: c.cashbooks || [] } : null;
    }
    const provider = held ? ((held.read || {}).provider || '') : cProvider.value;
    return provider ? { id: null, provider, name: cName.trim() || providerName(provider), client_ref: cRef.trim(), cashbooks: [] } : null;
  }
  function coopStepNext(){
    const c = coopChoice();
    if(!c){ setToast(cMode.value === 'existing' ? t('coop.choose_coop', 'Choose a supplier') : t('coop.choose_provider', 'Choose the supplier'), 'warn'); return; }
    if(addStep.value === 1 && !sectionChoices(coopProviders.value, c.provider, c.cashbooks.map(x => x.section_code)).some(s => s.code === cSection)){
      setToast(t('coop.choose_section', 'Choose the sub-account'), 'warn'); return;
    }
    addStep.value = Math.min(3, addStep.value + 1);
  }
  // a new co-op is made first (POST /coops finds the company's own when it has it), then its id
  async function ensureCoopFor(c){
    if(c.id) return c.id;
    const g = await api.post(P('/coops'), { provider: c.provider, client_ref: c.client_ref || null, name: c.name });
    return g && g.id;
  }
  async function addCoopAccount(){
    const c = coopChoice();
    const sec = c && sectionChoices(coopProviders.value, c.provider, c.cashbooks.map(x => x.section_code)).find(s => s.code === cSection);
    if(!c || !sec){ addStep.value = 1; setToast(t('coop.choose_section', 'Choose the sub-account'), 'warn'); return; }
    busy.value = 'addacct';
    try {
      const opening = parseAmount(aOpening, curMarks(''));
      const groupId = await ensureCoopFor(c);
      if(!groupId) throw new Error(t('coop.coop_not_made', 'The supplier could not be made'));
      const res = await api.post(P('/bank-accounts'), {
        name: `${c.name} — ${sec.label}`, kind: 'supplier_trading', group_id: groupId,
        section_code: sec.code, section_label: sec.label, currency_id: (reference.value || {}).base_currency_id || null,
        opening_balance: opening || 0, opening_balance_date: aOpeningDate || todayIso() });
      closeAddWizard(); aOpening = '';
      if(res && res.opening_journal_error) setToast(t('coop.account_added', 'Supplier account added') + ' — ' + res.opening_journal_error, 'warn');
      else setToast(res && res.opening_journal_posted
        ? t('coop.account_added_opening', 'Supplier account added — opening balance of {amt} posted to the ledger').replace('{amt}', fmtMoney(res.opening_journal ? res.opening_journal.amount : opening, curSym(''), true))
        : t('coop.account_added', 'Supplier account added'));
      await loadAccounts(true); if(res && res.id){ selectAccount(res.id); await loadLines(); }
    } catch(err){ setToast((err && err.data && err.data.error) || (err && err.message) || t('cashbook.account_add_failed', 'Could not add the account'), 'err'); }
    busy.value = '';
  }
  // a statement held for its co-op (needs_coop, plan §5.2): the form links it to one of the company's co-ops of the
  // same co-op, or makes the co-op; then the held job imports what it read into that co-op — no second AI read — and
  // the import's review opens
  async function openCoopLink(read, jobId){
    await loadCoops();
    openAddWizard(null, 0);
    cName = (read && read.coop_name) || providerName((read || {}).provider);    // plain fields first (resetCoopForm)
    cRef = (read && read.client_ref) || '';
    const start = coopLinkStart(coops.value, read);          // a co-op of another account number is never chosen for it
    cMode.value = start.mode;
    cGroupId.value = start.groupId;
    coopHeld.value = { read: read || {}, jobId };
    aKind.value = 'coop';
  }
  async function linkHeldCoop(){
    const held = coopHeld.value, c = coopChoice();
    if(!held || !c){ setToast(cMode.value === 'existing' ? t('coop.choose_coop', 'Choose a supplier') : t('coop.choose_provider', 'Choose the supplier'), 'warn'); return; }
    busy.value = 'coopLink';
    try {
      const groupId = await ensureCoopFor(c);
      if(!groupId) throw new Error(t('coop.coop_not_made', 'The supplier could not be made'));
      const imp = await api.post(P(`/bank-imports/jobs/${held.jobId}/assign-account`), { group_id: groupId });
      closeAddWizard();
      const errs = (imp && imp.errors) || [];
      if(errs.length) setToast(errs.join(' · '), 'err');
      else if(imp && imp.auto_message) setToast(imp.auto_message, /HELD/.test(imp.auto_message) ? 'warn' : 'ok');
      await loadAccounts(true); await loadLines();
      const sets = (imp && imp.statement_set_ids) || [];
      if(sets.length) review.open(sets[0]);
      askNextHeld(imp);
    } catch(err){ setToast((err && err.data && err.data.error) || (err && err.message) || t('cashbook.import_failed', 'Could not import the statement'), 'err'); }
    busy.value = '';
    loadHeld();
  }
  function addStepBack(){ addStep.value = Math.max(1, addStep.value - 1); }

  // ── statements held for their account or co-op (GET /bank-imports/held) ──
  async function loadHeld(){
    try { const r = await api.get(P('/bank-imports/held')); heldImports.value = (r && r.data) || []; }
    catch { /* the list stays as it was shown; the next load brings it */ }
  }
  // the add-account form's pre-fill from a statement held for its bank account (needs_account)
  const accountPrefill = na => ({ account_holder: na.account_holder, account_number: na.account_number,
    bank_name: na.bank_name, currency: na.currency, opening_balance: na.opening_balance,
    opening_balance_date: na.opening_balance_date });
  // a statement held for its bank account carries the number it prints — none when its number was not read (2026-10-27):
  // then its account is chosen on the list, from the company's own (or made with "New account"), never guessed
  const numberRead = na => !!String((na || {}).account_number || '').trim();
  // the form asks about a held statement: its co-op (needs_coop) or its bank account (needs_account) — imported, once
  // answered, from what was read (no second AI read). One whose number was not read opens no form: it waits on the list
  // for its account to be chosen, and the user is told
  function askHeld(h){
    heldDiscardId.value = 0;
    if(h.needs_coop) openCoopLink(h.needs_coop, h.id);
    else if(h.needs_account && !numberRead(h.needs_account)) noNumberWaits(h.files);
    else if(h.needs_account) openAddWizard(accountPrefill(h.needs_account), h.id);
  }
  function noNumberWaits(files){
    loadHeld();
    setToast(fill(t('cashbook.held_no_number_waits', '{file}: its account number was not read — choose its bank account under “Waiting for you”.'),
      { file: (Array.isArray(files) ? files.join(', ') : files) || t('cashbook.the_statement', 'the statement') }), 'warn');
  }
  // a statement whose number was not read, imported into the bank account chosen for it on the list — from what was
  // read (POST …/assign-account): that statement only
  async function importHeldInto(h){
    const id = Number(heldPick[h.id] || 0);
    if(!id){ setToast(t('cashbook.held_pick_first', 'Choose its bank account first'), 'warn'); return; }
    heldDiscardId.value = 0;
    busy.value = 'heldpick';
    let imp = null;
    try {
      imp = await api.post(P(`/bank-imports/jobs/${h.id}/assign-account`), { bank_account_id: id });
      delete heldPick[h.id];
      const errs = (imp && imp.errors) || [];
      if(errs.length) setToast(errs.join(' · '), 'warn');
      else setToast((imp && imp.auto_message) || t('cashbook.held_imported', 'Imported into the account you chose'));
      await loadAccounts(true); selectAccount(id); await loadLines();
    } catch(err){ setToast((err && err.data && err.data.error) || t('cashbook.import_failed', 'Could not import the statement'), 'err'); }
    busy.value = '';
    await loadHeld();
    askNextHeld(imp);
  }
  // a card drop held for "import it here anyway?" (account_mismatch), answered yes — from its question or the list: the
  // files the drop kept are imported into its card (POST …/confirm — nothing is read again) and the card's badge
  // follows that import, as a drop's
  async function importHeldOnCard(jobId, accountId){
    if(!jobId) return;
    heldDiscardId.value = 0;
    if(mismatch.value && mismatch.value.jobId === jobId) mismatch.value = null;   // answered on the list: its question goes
    busy.value = 'heldcard';
    try {
      const r = await api.post(P(`/bank-imports/jobs/${jobId}/confirm`), {});
      const acc = (r && r.bank_account_id) || accountId;
      if(r && r.job_id){ setCardJob(acc, { stage: 'extracting', progress: 0 }); pollCardJob(acc, r.job_id); }
    } catch(err){ setToast((err && err.data && err.data.error) || t('cashbook.import_failed', 'Could not import the statement'), 'err'); }
    busy.value = '';
    loadHeld();
  }
  // a held statement's answer goes to that statement only (and another of the same account): a statement dropped with
  // it that waits for another answer comes back held apart (imp.held) — the form asks about it next
  function askNextHeld(imp){
    const next = imp && imp.held;
    if(next && next.job_id) askHeld({ id: next.job_id, needs_account: next.needs_account, needs_coop: next.needs_coop,
                                      files: next.files });
  }
  // a statement held when the form is already open (asking about another, or an account being added) never takes the
  // form's place: it waits in the list, and the user is told
  function heldArrived(jobId, files){
    loadHeld();
    if(!addOpen.value) return false;
    setToast(fill(t('cashbook.held_waits', '{file} waits for you — see “Waiting for you” above the cards.'),
      { file: files || t('cashbook.the_statement', 'the statement') }), 'warn');
    return true;
  }
  // the ✎ on a co-op cashbook's card: its credit limit, terms and interest, edited where the card sits (golden rule #7)
  function openLimitEdit(a){
    limVal = a.credit_limit === null || a.credit_limit === undefined ? '' : String(a.credit_limit);   // plain first
    termsVal = a.terms || '';
    rateVal = a.interest_rate === null || a.interest_rate === undefined ? '' : String(a.interest_rate);
    limitErr.value = '';
    limitEditId.value = limitEditId.value === a.id ? 0 : a.id;
  }
  function closeLimitEdit(){ limitEditId.value = 0; limitErr.value = ''; }
  async function saveLimit(a){
    const r = limitBody(a, { limit: limVal, terms: termsVal, rate: rateVal }, curMarks(''), t);
    if(r.error){ limitErr.value = r.error; return; }
    busy.value = 'limit';
    try {
      await api.put(P(`/bank-accounts/${a.id}`), r.body);
      closeLimitEdit();
      setToast(t('common.saved', 'Saved'));
      await loadAccounts(true);
    } catch(err){ limitErr.value = (err && err.data && err.data.error) || t('common.save_failed', 'Save failed'); }
    busy.value = '';
  }
  async function discardHeld(id){
    busy.value = 'discard';
    try {
      await api.post(P(`/bank-imports/jobs/${id}/discard`), {});
      heldDiscardId.value = 0;
      delete heldPick[id];
      if(addImportJobId === id || (coopHeld.value && coopHeld.value.jobId === id)) closeAddWizard();
      if(mismatch.value && mismatch.value.jobId === id) mismatch.value = null;   // its question goes with it
      setToast(t('cashbook.held_discarded', 'Discarded — nothing of it was imported.'), 'warn');
    } catch(err){ setToast((err && err.data && err.data.error) || t('cashbook.held_discard_failed', 'Could not discard it'), 'err'); }
    busy.value = '';
    await loadHeld();
  }

  async function addAccount(e){
    if(e && e.preventDefault) e.preventDefault();
    if(!aName.trim()) { addStep.value = 1; setToast(t('cashbook.name_required', 'Name is required'), 'warn'); return; }
    busy.value = 'addacct';
    try {
      const baseCur = (reference.value || {}).base_currency_id || null;
      const useExisting = aGlMode.value === 'existing' && aGlId;
      const opening = parseAmount(aOpening, curMarks(aCur));
      const body = {
        name: aName.trim(),
        gl_account_id: useExisting ? Number(aGlId) : null,   // link an existing GL, or mint a new unique code
        account_no: aNo.trim() || null, bank_name: aBank.trim() || null,
        currency_id: aCur ? Number(aCur) : baseCur,          // each account declares its own currency (#24)
        opening_balance: opening || 0,                       // posted as a real opening-balance journal on create
        opening_balance_date: aOpeningDate || todayIso() };
      if(!useExisting && aCode.trim()) body.code = aCode.trim();   // caller-edited unique GL code
      const pendingJobId = addImportJobId;         // capture before closeAddWizard clears it
      const openingSym = curSym(aCur);             // the opening is in the ACCOUNT's currency — capture before aCur clears
      const res = await api.post(P('/bank-accounts'), body);
      closeAddWizard(); aName = aNo = aBank = aCur = ''; aOpening = ''; aGlId = 0;
      // the toast reports the opening-balance journal outcome so the user sees the ledger was set
      const posted = res && res.opening_journal_posted;
      const amt = posted && res.opening_journal ? res.opening_journal.amount : opening;
      if(res && res.opening_journal_error)
        // the account exists, but its opening did not post (e.g. no exchange rate for a foreign account)
        setToast(t('cashbook.account_added', 'Bank account added') + ' — ' + res.opening_journal_error, 'warn');
      else setToast(posted
        ? t('cashbook.account_added_opening', 'Bank account added — opening balance of {amt} posted to the ledger').replace('{amt}', fmtMoney(amt, openingSym, true))
        : t('cashbook.account_added', 'Bank account added'));
      // a held statement (needs_account) imports into the account the user just confirmed —
      // reusing the ALREADY-extracted data (no second AI extraction)
      let imp = null;
      if(pendingJobId && res && res.id){
        try {
          imp = await api.post(P(`/bank-imports/jobs/${pendingJobId}/assign-account`),
            { bank_account_id: res.id });
          const errs = (imp && imp.errors) || [];
          if(errs.length) setToast(errs.join(' · '), 'warn');
          else if(imp && imp.auto_message) setToast(imp.auto_message);
        } catch(err2){ setToast((err2.data && err2.data.error) || t('cashbook.import_failed', 'Could not import the statement'), 'err'); }
      }
      await loadAccounts(true); if(res && res.id) selectAccount(res.id);
      if(res && res.id) await loadLines();
      askNextHeld(imp);                              // after the card is selected, which closes the form
    } catch(err){ setToast((err.data && err.data.error) || t('cashbook.account_add_failed', 'Could not add the account'), 'err'); }
    busy.value = '';
    loadHeld();
  }

  // ── the live /bank-import preview flow (extract → review/edit → stage) ─────
  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;   // 20 MB per file (owner 2026-09-23) — see nginx / TINA4_MAX_UPLOAD_SIZE
  function readImportFile(file){          // PDFs, images (png/jpg), ZIPs and Excel travel as base64; CSV/TSV as text
    if(file.size > MAX_UPLOAD_BYTES){
      return Promise.reject(new Error(t('bank_import.too_large',
        '{name} is {size} MB — the upload limit is 20 MB per file. Split the statement or export a smaller date range.')
        .replace('{name}', file.name)
        .replace('{size}', (file.size / (1024 * 1024)).toFixed(1))));
    }
    return new Promise((resolve, reject) => {
      const asB64 = /\.(pdf|zip|png|jpe?g|xlsx?|xlt|xlsm|xltx|xltm)$/i.test(file.name);
      const fr = new FileReader();
      fr.onerror = () => reject(new Error('Could not read ' + file.name));
      fr.onload = () => resolve(asB64
        ? { filename: file.name, content_b64: String(fr.result).split(',')[1] }
        : { filename: file.name, content: String(fr.result) });
      asB64 ? fr.readAsDataURL(file) : fr.readAsText(file);
    });
  }
  // Only statement-shaped files reach the extractor: reject a stray file (a Word doc, a video, a
  // random download) up front with a clear toast, instead of wasting an extraction that comes back
  // "couldn't read this" (owner 2026-09-08: junk "must not be allowed"). A photo IS a valid image
  // type, so a pot-plant .jpg still passes here — the AI self-check then rejects non-statement
  // CONTENT ("this doesn't look like a bank statement"). The two gates are complementary.
  const BANK_OK_RE = /\.(pdf|png|jpe?g|gif|webp|heic|heif|bmp|tiff?|csv|tsv|txt|zip|xlsx?|xlt|xlsm|xltx|xltm)$/i;
  function bankFilesRejected(files){
    const bad = (files || []).filter(f => !BANK_OK_RE.test(f.name || ''));
    if(!bad.length) return null;
    return bad.length === 1
      ? t('cashbook.bad_stmt_file', '“{name}” isn’t a statement — drop a PDF, image, CSV or Excel bank statement.').replace('{name}', bad[0].name)
      : t('cashbook.bad_stmt_files', 'Those aren’t statements — drop PDF, image, CSV or Excel bank statements.');
  }
  // The import-review/preview machinery is RETIRED (Andre 2026-09-16): an import now flows straight
  // into the ONE register — matched lines auto-post, the rest land UNPOSTED. There is no staging
  // "review" step, no server-side pending list to load, and no editable preview table. The drop →
  // extract → progress path (readImportFile / importBodies / pollJob / cardDrop / addTileDrop) and
  // the register itself carry the whole flow now.

  // ── the background extraction jobs (live queue + progress stages) ───────────
  // Several imports can be in flight at once (dropped one after another; a single worker
  // drains the queue). Each keeps its OWN entry + poll timer, so a 2nd +Add drop adds a
  // progress row instead of overwriting the 1st's (owner 2026-09-08 "the import status resets").
  const jobTimers = {};                    // jobId → setTimeout handle (was one shared pollTimer)
  // live retry-panel parity: a job whose progress hasn't moved for 3 minutes offers
  // Retry Import (cancel + re-send the remembered files) — tracked per job now. Never a job the
  // worker still holds (the API's `held`): a co-op statement's read on 3.8 Flash can stand still
  // for 15 minutes, and a retry would cancel it and read it all again (2026-10-25)
  const STALL_MS = 3 * 60 * 1000;
  function setJob(id, v){
    const next = { ...jobStates.value };
    if(v) next[id] = v;
    else { delete next[id]; clearTimeout(jobTimers[id]); delete jobTimers[id]; }
    jobStates.value = next;
  }
  const activeJobs = () => Object.values(jobStates.value).sort((a, b) => a.id - b.id);
  // the extraction stages, in order (key · i18n key · fallback) — shared by the progress UI
  const IMPORT_STAGES = [
    ['extracting', 'bank_import.stage_extracting', 'Extracting transactions'],
    ['matching_accounts', 'bank_import.stage_matching', 'Matching bank accounts'],
    ['applying_rules', 'bank_import.stage_rules', 'Applying your allocation rules'],
    ['ai_matching', 'bank_import.stage_ai', 'AI matching'],
    ['checking_duplicates', 'bank_import.stage_duplicates', 'Checking for duplicates'],
    ['saving', 'bank_import.stage_saving', 'Saving'],
  ];
  // Progress UI shared by the empty-company (!acct) branch and the review panel: ONE row per
  // in-flight import (label · % · Cancel · stall→Retry), so concurrent +Add drops each stay
  // visible. The detailed stage checklist shows only for a SINGLE job (N×6 lines would swamp it).
  function jobProgressUI(jobs){
    if(!jobs.length) return [];
    const many = jobs.length > 1;
    return html`<div class="cb-stagelist">
      ${many ? html`<div class="cb-stage-multi">⏳ ${t('cashbook.imports_running', 'Imports in progress')} <span class="mono">${jobs.length}</span> — ${t('cashbook.imports_running_hint', 'each statement is read in turn; none is cancelled')}</div>` : []}
      ${jobs.map(j => {
        const stageIndex = Math.max(0, IMPORT_STAGES.findIndex(([k]) => k === j.stage));
        return html`<div class=${'cb-stage-job' + (many ? ' cb-stage-job-multi' : '')}>
          <div class="cb-stage-run">⏳ ${j.label ? html`<span class="cb-stage-label" title=${j.label}>${j.label}</span> · ` : []}${t('cashbook.working_stmt', 'Working through the statement…')} <span class="mono">${j.progress || 0}%</span>
            <button class="btn btn-sm btn-outline cb-stage-cancel" ?disabled=${!!j.cancelling} @click=${() => cancelExtract(j.id)}>${j.cancelling ? t('cashbook.cancelling', 'Cancelling…') : t('common.cancel', 'Cancel')}</button></div>
          ${!many ? IMPORT_STAGES.map(([key, k, fb], i) => html`<div class=${'cb-stage-item' + (i < stageIndex ? ' cb-stage-done' : (i === stageIndex ? ' cb-stage-current' : ''))}>
            ${i < stageIndex ? '✓' : (i === stageIndex ? '▸' : '•')} ${t(k, fb)}</div>`) : []}
          ${j.stalled ? html`<div class="cb-stage-stall">
            ${t('cashbook.stall_msg', 'This is taking longer than it should.')}
            <button class="btn btn-sm btn-outline" ?disabled=${!!j.cancelling} @click=${() => retryImport(j.id)}>${t('cashbook.retry_import', 'Retry Import')}</button>
          </div>` : []}
        </div>`;
      })}
    </div>`;
  }
  // A poll that fails (the API restarting under a deploy, a dropped connection) keeps the import's row and asks again:
  // only the job's own answer ends it. A job not there, or POLL_TRIES failed polls in a row, gives the row up — saying
  // so, never silently (2026-10-26: a failed import's row went without a word).
  const POLL_TRIES = 20, POLL_AGAIN_MS = 3000;
  const lostTouch = what => setToast(fill(t('cashbook.import_lost',
    'Lost touch with the import of {what} — reload the page to see how it ended.'), { what }), 'warn');
  const givesUp = (err, misses) => (err && err.status === 404) || misses + 1 >= POLL_TRIES;
  // a held co-op statement read again from its PDF (the review's Read again): its job shows and is polled as an
  // import's — when done the review opens again on the statement (jobSets); a read that ends otherwise says why, in the
  // review too when it is open on the statement (readAgainEnded)
  function trackReadAgain(jobId, setId, label){
    importOpen.value = true;
    setJob(jobId, { id: jobId, stage: 'extracting', progress: 0, lastProgressAt: Date.now(),
                    label: fill(t('coop.ra_job', '{label} · read again'), { label }), readAgain: { setId, label } });
    pollJob(jobId);
  }
  function pollJob(jobId, misses = 0){
    clearTimeout(jobTimers[jobId]);
    jobTimers[jobId] = setTimeout(async () => {
      let job;
      try { job = await api.get(P(`/bank-imports/jobs/${jobId}`)); }
      catch(err){
        const was = jobStates.value[jobId];
        if(!was) return;                               // cancelled meanwhile
        if(!givesUp(err, misses)){ pollJob(jobId, misses + 1); return; }
        setJob(jobId, null);
        lostTouch(was.label || t('cashbook.the_statement', 'the statement'));
        if(was.readAgain) review.readAgainEnded(was.readAgain.setId, '');
        return;
      }
      const prev = jobStates.value[jobId];
      if(!prev) return;                                // its cancel was taken while this poll was out: the row is gone
      // still running — or ended while its cancel is asked: the cancel's answer comes first (taken, the row goes as it
      // does; refused, the API's words), then how the import ended — never the other way round, which put "Job already
      // finished" over why it failed
      if(job.status === 'running' || job.status === 'queued' || prev.cancelling){
        const now = Date.now();
        const moved = job.progress !== prev.progress;
        const lastAt = (moved || !prev.lastProgressAt) ? now : prev.lastProgressAt;
        setJob(jobId, { id: jobId, stage: job.stage, progress: job.progress,
                        lastProgressAt: lastAt, stalled: !job.held && now - lastAt > STALL_MS,
                        label: prev.label, bodies: prev.bodies, readAgain: prev.readAgain, cancelling: prev.cancelling });
        pollJob(jobId);
        return;
      }
      setJob(jobId, null);
      if(job.status === 'done'){
        // A co-op statement of a co-op the company has not linked yet (plan §5.2 needs_coop): HELD — nothing made or
        // imported — until the form links it to one of the company's co-ops or makes the co-op.
        const nc = job.needs_coop || ((job.result || {}).needs_coop);
        if(nc){
          if(!activeJobs().length) importOpen.value = false;
          if(!heldArrived(jobId, job.filenames)) openCoopLink(nc, jobId);
          return;
        }
        // New-account confirmation: the statement matched no existing account, so the backend HELD
        // it (nothing created/imported) and returned a needs_account pre-fill. Open the onboarding
        // wizard pre-filled; on confirm the account is created and this statement imports into it.
        const na = job.needs_account || ((job.result || {}).needs_account);
        if(na){
          if(!activeJobs().length) importOpen.value = false;
          // reuse THIS job's extracted data after the account is created — or, the form being open, it waits listed;
          // one whose number was not read waits listed for its account to be chosen
          if(!heldArrived(jobId, job.filenames)){
            if(numberRead(na)) openAddWizard(accountPrefill(na), jobId);
            else noNumberWaits(job.filenames);
          }
          return;
        }
        // A per-file error (esp. "no AI key") becomes a PERSISTENT notice (shown above the drop
        // targets) — not a 2.6s toast that vanishes.
        const notice = importErrorNotice((job.result || {}).files);
        if(notice) noKeyNotice.value = notice;
        const created = ((job.result || {}).files || [])
          .filter(f => f.bank_account && f.bank_account.auto_created);
        if(created.length) setToast(`Created ${created.map(f => f.bank_account.name).join(', ')} from the statement`, 'warn');
        // Balance-reconciliation alert: a line whose amount doesn't match the statement's running
        // balance (e.g. an extractor mis-split that duplicated an amount) is flagged "gap" and held
        // from posting. Warn the user so they open the register and verify — the ⚠ gap badge is the
        // durable, per-line pointer. (owner 2026-09-21, Thaba BS3 R11,198.)
        const r = job.result || {};
        const bm = r.balance_mismatches || (r.files || []).reduce((n, f) => n + ((f.result || f).balance_mismatches || 0), 0);
        if(bm) setToast(t('cashbook.balance_gap_alert',
          `⚠ ${bm} line${bm !== 1 ? 's' : ''} don’t match the statement balance — parked as “gap”. Open the register and verify.`), 'warn');
        // The import flowed straight into the register (matched → auto-posted, the rest unposted).
        // Refresh the accounts first (a statement may have auto-created one, which loadAccounts then
        // selects) THEN the selected account's lines — loadLines re-pulls the register (loadRegister)
        // so the freshly-imported rows appear inline without a manual reload.
        await loadAccounts(true);
        await loadLines();
        if(!activeJobs().length) importOpen.value = false;   // last job done → drop the progress card
        // a co-op statement fans out to its sub-accounts' cashbooks: its review shows where each went and whether it
        // ties to its PDF (plan §9)
        const sets = jobSets(job.result);
        if(sets.length) review.open(sets[0]);
      } else if(job.status === 'failed'){
        const why = job.error || t('bank_import.extraction_failed', 'Extraction failed');
        setToast(why, 'err');
        if(prev.readAgain) review.readAgainEnded(prev.readAgain.setId, why);
      } else if(job.status === 'cancelled'){
        setToast(t('cashbook.extraction_cancelled', 'Extraction cancelled'), 'warn');
        if(prev.readAgain) review.readAgainEnded(prev.readAgain.setId, t('coop.ra_cancelled', 'The read again was cancelled.'));
      }
    }, misses ? POLL_AGAIN_MS : 600);
  }
  // Extraction reads a statement with AI, which needs the user's OWN API key (there is no platform
  // key — users bring their own). With no key we do NOT start an import at all: no /extract call,
  // no background job, no empty 0-transaction pending — just the persistent prompt to set a key up.
  // (owner 2026-09-08: "that functionality should only run … if there is a key setup".)
  function importBlockedNoKey(){
    if(reference.value && reference.value.ai_configured === false){
      noKeyNotice.value = { needsKey: true, msg: NO_KEY_MSG };
      return true;
    }
    return false;
  }
  // accountId: explicit target passed BY THE CALLER (card drops) — never read shared
  // state at post time, or two simultaneous drops route both statements to the last
  // card touched. undefined → the import panel's own account select (importAccount).
  // silent: true = a card drop; progress lives on the card badge, not the global panel.
  // A wrong-account drop's "import it here anyway" is answered by its held job (importHeldOnCard), never by sending the
  // files again.
  async function importBodies(bodies, accountId, { silent = false, label = '' } = {}){
    if(!bodies || !bodies.length) return null;
    if(importBlockedNoKey()){ if(!silent) importOpen.value = false; return null; }  // no key → never hit the extractor
    const target = accountId !== undefined ? accountId : importAccount;
    if(!silent) busy.value = 'extract';
    let jobId = null;
    try {
      const res = await api.post(P('/bank-imports/extract'), {
        files: bodies, bank_account_id: target ? Number(target) : undefined });
      if(res.status === 'duplicate'){          // same-file guard (v2 SHA-256 parity)
        setToast(res.error || t('cashbook.already_imported', 'Already imported'), 'warn');
      } else {
        jobId = res.job_id;
        if(!silent){
          // register THIS drop's own progress slot (keyed by job_id) so a second concurrent
          // +Add drop adds another row rather than overwriting the first; keep the files for Retry
          setJob(res.job_id, { id: res.job_id, stage: 'extracting', progress: 0,
                               lastProgressAt: Date.now(), label, bodies });
          pollJob(res.job_id);
          if(!importRef){
            const lastRef = await api.get(P(`/bank-accounts/${sel.value}/last-reference`)).catch(() => null);
            importRef = (lastRef && lastRef.next) || '';
          }
        }
      }
    } catch(err){ setToast((err.data && err.data.error) || t('bank_import.extraction_failed', 'Extraction failed'), 'err'); }
    if(!silent) busy.value = '';
    return jobId;
  }
  // per-card import badge (v2 _setCardImport/_pollCardJob): each drop polls its own job
  function setCardJob(accId, v){
    const next = { ...cardJobs.value };
    if(v) next[accId] = v; else delete next[accId];
    cardJobs.value = next;
  }
  function pollCardJob(accId, jobId, misses = 0){
    setTimeout(async () => {
      let job;
      try { job = await api.get(P(`/bank-imports/jobs/${jobId}`)); }
      catch(err){
        if(!cardJobs.value[accId]) return;
        if(!givesUp(err, misses)){ pollCardJob(accId, jobId, misses + 1); return; }
        setCardJob(accId, null);
        const a = accounts.value.find(x => x.id === Number(accId));
        setToast(fill(t('cashbook.import_lost_card', 'Lost touch with the import into {account} — reload the page to see how it ended.'),
          { account: a ? a.name : '' }), 'warn');
        return;
      }
      if(job.status === 'running' || job.status === 'queued'){
        setCardJob(accId, { stage: job.stage, progress: job.progress });
        pollCardJob(accId, jobId);
        return;
      }
      setCardJob(accId, null);
      if(job.status === 'done'){
        // wrong-account-drop guard: the API held the import because the statement's account number
        // doesn't match this card — warn and let the user confirm before anything is imported. The held drop is
        // listed under "Waiting for you" too (2026-10-27): a reload, or a question closed unanswered, never loses it.
        const mm = job.account_mismatch || ((job.result || {}).account_mismatch);
        if(mm){
          const a = accounts.value.find(x => x.id === Number(accId));
          loadHeld();
          mismatch.value = { accountId: accId, accountName: mm.account_name || (a ? a.name : ''),
                             detected: mm.detected, expected: mm.expected, jobId, files: job.filenames || '' };
          return;   // nothing imported yet — the confirm modal drives the next step
        }
        // a co-op's statement of account dropped on this card goes to its co-op, as at the general drop: held for the
        // co-op form when the company has not linked that co-op yet (needs_coop) — nothing made or imported
        const nc = job.needs_coop || ((job.result || {}).needs_coop);
        if(nc){ if(!heldArrived(jobId, job.filenames)) openCoopLink(nc, jobId); return; }
        const notice = importErrorNotice((job.result || {}).files);   // persistent, not a fleeting toast
        if(notice) noKeyNotice.value = notice;
        // refresh the card (balance) and the selected account's register — the imported lines
        // (matched auto-posted, the rest unposted) now show inline, no review step.
        await loadAccounts(true);
        await loadLines();
        const moved = ((job.result || {}).files || []).find(f => f.is_coop && f.dropped_on && f.bank_account);
        if(moved) setToast(fill(t('cashbook.coop_dropped_on_card', '{file} is {coop}’s statement of account, not {card}’s: it went to {coop}’s accounts.'),
          { file: moved.filename, coop: moved.bank_account.name, card: moved.dropped_on.name }), 'warn');
        const sets = jobSets(job.result);          // a co-op statement: where it went, and whether it ties (plan §9)
        if(sets.length) review.open(sets[0]);
      } else if(job.status === 'failed'){
        setToast(job.error || t('bank_import.extraction_failed', 'Extraction failed'), 'err');
      }
    }, misses ? POLL_AGAIN_MS : 600);
  }
  // drop a statement straight onto an account card → the extract is routed to THAT
  // account, and progress shows as a badge ON the card (v2 parity: several cards can
  // import at once, each with its own badge — no shared state between drops)
  // Shared import path for a statement chosen for an account — used by BOTH drag-drop and the
  // click-to-browse file picker (owner 2026-09-19: "clicking the bank account pill should open a
  // file upload dialog as well").
  function beginImport(account, files){
    files = Array.from(files || []);
    if(!files.length) return;
    const badMsg = bankFilesRejected(files);
    if(badMsg){ setToast(badMsg, 'err'); return; }   // not a statement file → never reaches the AI
    if(importBlockedNoKey()) return;         // no AI key → don't read/extract; the banner says why
    setCardJob(account.id, { stage: 'extracting', progress: 0 });
    Promise.all(files.map(readImportFile))
      .then(bodies => importBodies(bodies, String(account.id), { silent: true }))
      .then(jobId => { if(jobId) pollCardJob(account.id, jobId); else setCardJob(account.id, null); })
      .catch(err => { setCardJob(account.id, null); setToast((err && err.message) || t('cashbook.read_failed', 'Could not read the dropped file'), 'err'); });
  }
  function cardDrop(account, e){
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('cb-card-drag');
    beginImport(account, (e.dataTransfer && e.dataTransfer.files) || []);
  }
  // Click-to-browse: remember which account was clicked, then open the hidden statement file input.
  let cardPickTarget = null;
  function pickStatementFor(account){
    cardPickTarget = account;
    const el = document.getElementById('cbCardFile');
    if(el){ el.value = ''; el.click(); }     // clear so the same file can be re-picked
  }
  function onCardFilePicked(e){
    const acct = cardPickTarget; cardPickTarget = null;
    if(acct) beginImport(acct, e.target.files);
    e.target.value = '';
  }
  // drop a statement onto the "+ Add account" tile or the "Drop any statement" tile → no target, so the extract
  // pipeline auto-detects/creates the bank account from the statement (live match_or_create_cashbook) — or, for a
  // co-op's statement, routes it to its co-op (plan §8/§9: the general drop point). The picker comes here too.
  function generalDrop(e){
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('cb-card-drag');
    generalImport((e.dataTransfer && e.dataTransfer.files) || []);
  }
  function pickAnyStatement(){
    const el = document.getElementById('cbAnyFile');
    if(el){ el.value = ''; el.click(); }     // clear so the same file can be re-picked
  }
  function onAnyFilePicked(e){
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    generalImport(files);
  }
  // a co-op's header takes a statement for that co-op: dropped on one of its cashbooks, it is routed to the co-op (the
  // API resolves the card's co-op); a co-op with no cashbook yet takes it at the general drop point
  function coopDrop(group, e){
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.classList.remove('cb-card-drag');
    const files = (e.dataTransfer && e.dataTransfer.files) || [];
    if(group.cards.length) beginImport(group.cards[0], files);
    else generalImport(files);
  }
  function generalImport(fileList){
    const files = Array.from(fileList || []);
    if(!files.length) return;
    const badMsg = bankFilesRejected(files);
    if(badMsg){ setToast(badMsg, 'err'); return; }   // not a statement file → never reaches the AI
    if(importBlockedNoKey()) return;       // no AI key → don't open an empty review / start extraction
    importAccount = '';                    // auto-detect/create
    importOpen.value = true;
    // name the row by the dropped file(s) so several concurrent +Add imports are told apart
    const label = files.length === 1 ? files[0].name : `${files[0].name} +${files.length - 1} more`;
    Promise.all(files.map(readImportFile)).then(bodies => importBodies(bodies, '', { label }))
      .catch(err => setToast((err && err.message) || t('cashbook.read_failed', 'Could not read the dropped file'), 'err'));
  }
  async function retryImport(jobId){
    const j = jobStates.value[jobId] || {};
    const bodies = j.bodies, label = j.label, again = j.readAgain;
    if(!await cancelExtract(jobId)) return;   // not cancelled (it saves what it read, or it ended): never read twice
    if(again){                                // a read again is asked again: its PDF is kept with its import
      try {
        const r = await api.post(P(`/statement-sets/${again.setId}/read-again`), {});
        trackReadAgain(r.job_id, again.setId, again.label);
      } catch(err){ setToast((err.data && err.data.error) || t('coop.ra_failed', 'Could not read it again.'), 'err'); }
    }
    else if(bodies) await importBodies(bodies, '', { label });
    else setToast(t('cashbook.retry_pick', 'Pick the statement file again to retry'), 'warn');
  }
  // The cancel is asked with the import's row kept ("Cancelling…") and still polled: only a cancel the API takes ends the
  // row. One it refuses leaves the import as it was — one a worker is saving goes on into the books (409), one that ended
  // meanwhile ended as it did (400) — so the poll follows it to its end like any import's, and the page says why in the
  // API's words (2026-10-26: the row went at the click and the page stopped asking, so an import saved after its
  // "cancel" never refreshed the register). Returns whether the cancel was taken.
  async function cancelExtract(jobId){
    const j = jobStates.value[jobId];
    if(!j || j.cancelling) return false;
    setJob(jobId, { ...j, cancelling: true });
    try { await api.post(P(`/bank-imports/jobs/${j.id}/cancel`), {}); }
    catch(err){
      const now = jobStates.value[jobId];
      if(now) setJob(jobId, { ...now, cancelling: false });
      setToast((err.data && err.data.error) || t('cashbook.cancel_failed', 'Could not cancel'),
               err.status === 409 || err.status === 400 ? 'warn' : 'err');
      return false;
    }
    setJob(jobId, null);   // taken: the row and its poll timer go — the server marked it cancelled
    return true;
  }

  // (retired) applyPickToPreview / acceptAi / acceptAllAi / flipPreview / prevToggle / prevBulkFlip /
  // prevBulkMove / stagePreview lived here — the import-review/staging step is gone; imports flow
  // straight into the register (matched auto-posted, the rest unposted), so there is nothing to stage.

  // Auto ↔ Manual play the pane-in fade on
  // the working area; paneFx self-clears so other re-renders never replay it.
  const pane = paneFx();
  function switchMode(v){
    if(mode.value === v) return;
    mode.value = v; selected.value = new Set();
    pane();
  }

  // ── page head: title + Auto|Manual top-right, then the email-intake row ────
  const headRow = () => {
    const m = mode.value;
    return html`<div class="cb-head-row">
      <h1 class="page-title">${t('common.bank_accounts', 'Bank Accounts')}</h1>
      <div class="cb-head-actions">
        <button class="btn btn-sm btn-outline" @click=${() => navigate(`/co/${companyId}/bank-rules`)}>${t('bank_rules.title', 'Allocation rules')}</button>
        <div class="cb-mode-toggle">
          <button class=${'cb-mode-btn' + (m === 'auto' ? ' active' : '')} @click=${() => switchMode('auto')}>${t('cashbook.mode_auto', 'Auto')}</button>
          <button class=${'cb-mode-btn' + (m === 'manual' ? ' active' : '')} @click=${() => switchMode('manual')}>${t('cashbook.mode_manual', 'Manual')}</button>
        </div>
      </div>
    </div>
    <div class="cb-intake-row">
      <span class="cb-intake-label">✉ ${t('cashbook.import_email_label', 'Email statements to import:')}</span>
      <span class="cb-intake-addr mono">${intakeAddress()}</span>
      <button class="icon-btn" title=${t('cashbook.copy_address', 'Copy address')} @click=${copyIntake}>⧉</button>
    </div>`;
  };

  // ── account cards strip ────────────────────────────────────────────────────
  const cards = () => {
    const list = accounts.value, active = sel.value, pwdOpen = pwdOpenId.value;
    const isLoading = loading.value, adding = addOpen.value, b = busy.value;
    const step = addStep.value, suggestedCode = addNextCode.value;   // read so the wizard re-renders per step
    const ref = reference.value || {}, cj = cardJobs.value;
    const notice = noKeyNotice.value;   // persistent import notice (no-AI-key / extraction error)
    const held = heldImports.value, discardAsk = heldDiscardId.value;   // statements held for their account or co-op
    const limEdit = limitEditId.value, limErr = limitErr.value;         // the co-op cashbook card edited in place
    if(isLoading) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    // A statement can't be read without an AI key (users bring their own — no platform fallback),
    // so surface it right above the drop targets, persistently, with a one-click path to set it up.
    const keyBanner = notice ? html`<div class=${'cb-keynotice' + (notice.needsKey ? ' cb-keynotice-key' : '')}>
      <span class="cb-keynotice-icon">${notice.needsKey ? '🔑' : '⚠'}</span>
      <span class="cb-keynotice-msg">${notice.msg}${notice.needsKey
        ? ' ' + t('cashbook.ai_key_why', 'Statements are read by AI, using a key you provide.') : ''}</span>
      ${notice.needsKey ? html`<button class="btn btn-sm btn-primary cb-keynotice-cta"
        @click=${() => { location.href = `/co/${companyId}/settings?tab=apikeys`; }}>${t('cashbook.set_up_key', 'Set up API key')}</button>` : []}
      <button class="cb-keynotice-x" title=${t('common.dismiss', 'Dismiss')} @click=${() => { noKeyNotice.value = null; }}>✕</button>
    </div>` : [];
    // statements held for their account or co-op: each waits here, with its question, until it is imported or
    // discarded — the form asks about one at a time (askHeld). Two ask here, on the list (2026-10-27): a statement
    // whose account number was not read (its account chosen from the company's own, or made), and a card drop held for
    // "import it here anyway?" (imported into its card, or discarded)
    const cardOf = mm => list.find(x => x.id === Number(mm.bank_account_id));
    const heldAsk = h => h.needs_coop
      ? fill(t('cashbook.held_needs_coop', 'waits for its supplier: {coop}'),
          { coop: [h.needs_coop.coop_name || providerName(h.needs_coop.provider), h.needs_coop.client_ref].filter(Boolean).join(' · ') })
      : h.account_mismatch
        ? fill(t('cashbook.held_mismatch', 'dropped on {card}, but it is the statement of account {detected}'),
            { card: h.account_mismatch.account_name || (cardOf(h.account_mismatch) || {}).name || t('common.this_account', 'this account'),
              detected: h.account_mismatch.detected || t('common.unknown', 'unknown') })
        : !numberRead(h.needs_account)
          ? t('cashbook.held_no_number', 'its account number was not read — choose its bank account')
          : fill(t('cashbook.held_needs_account', 'waits for its bank account: {account}'),
              { account: [(h.needs_account || {}).account_number, (h.needs_account || {}).account_holder].filter(Boolean).join(' · ') });
    // the bank accounts a statement whose number was not read may go into: the company's banks, never a co-op's cashbook
    const banks = list.filter(a => a.kind !== 'supplier_trading' && a.is_active !== false);
    const heldActs = h => {
      if(h.account_mismatch){
        const mm = h.account_mismatch, c = cardOf(mm);
        return html`<button type="button" class="btn btn-sm btn-primary cb-held-import-here" ?disabled=${b === 'heldcard'}
          @click=${() => importHeldOnCard(h.id, mm.bank_account_id)}>${fill(t('cashbook.held_import_here', 'Import into {card}'),
            { card: mm.account_name || (c || {}).name || t('common.this_account', 'this account') })}</button>`;
      }
      if(h.needs_account && !numberRead(h.needs_account)) return html`
        <select class="form-control cb-in cb-held-pick" aria-label=${t('cashbook.held_pick_label', 'The bank account for this statement')}
          .value=${String(heldPick[h.id] || '')} @change=${e => { heldPick[h.id] = e.target.value; }}>
          <option value="">${t('cashbook.held_pick_ph', '— Select bank account —')}</option>
          ${banks.map(a => html`<option value=${String(a.id)}>${[a.name, a.account_no].filter(Boolean).join(' · ')}</option>`)}
        </select>
        <button type="button" class="btn btn-sm btn-primary cb-held-import-into" ?disabled=${b === 'heldpick'}
          @click=${() => importHeldInto(h)}>${t('cashbook.held_import', 'Import')}</button>
        <button type="button" class="btn btn-sm btn-outline cb-held-new-account"
          @click=${() => { heldDiscardId.value = 0; openAddWizard(accountPrefill(h.needs_account), h.id); }}>${t('cashbook.held_new_account', 'New account')}</button>`;
      return html`<button type="button" class="btn btn-sm btn-primary cb-held-import-go" @click=${() => askHeld(h)}>${h.needs_coop
        ? t('cashbook.held_link_coop', 'Link supplier') : t('cashbook.held_add_account', 'Add account')}</button>`;
    };
    const heldBlock = held.length ? html`<div class="cb-held-imports" role="status">
      <div class="cb-held-imports-head">⏳ ${held.length === 1 ? t('cashbook.held_one', 'Waiting for you: 1 statement')
        : fill(t('cashbook.held_many', 'Waiting for you: {n} statements'), { n: held.length })}</div>
      ${held.map(h => html`<div class="cb-held-import">
        <span class="cb-held-import-what"><span class="cb-held-import-files">${(h.files || []).join(', ')}</span>
          <span class="cb-held-import-ask">${heldAsk(h)}</span></span>
        <span class="cb-held-import-acts">
          ${heldActs(h)}
          ${rowDelete({ armed: discardAsk === h.id, busy: b === 'discard', label: t('cashbook.held_discard', 'Discard this statement'),
                        onArm: () => { heldDiscardId.value = h.id; }, onCancel: () => { heldDiscardId.value = 0; },
                        onDelete: () => discardHeld(h.id) })}
        </span>
      </div>`)}
    </div>` : [];
    // bank-GL candidates the user can LINK instead of minting a new one (step 2 "existing")
    const glCandidates = (ref.accounts || []).filter(a => a.is_bank && a.is_postable);
    // a co-op's section cashbooks under one header card (plan §9), after the banks; each co-op open or closed
    const grouped = groupCards(list, coops.value, a => figsOf(a, a).balance);
    const openCoops = coopsOpen.value, sets = coopSets.value;
    const coopBlock = g => {
      const open = openCoops.has(g.id), holdsActive = g.cards.some(a => a.id === active);
      const last = latestSet(sets, g.id), st = last ? setStatus(last.status) : null;
      const importing = g.cards.find(a => cj[a.id]);
      const n = g.cards.length;
      return html`<div class=${'cb-coop' + (open ? ' is-open' : '')}>
        <div class=${'cb-card cb-coop-head' + (holdsActive && !open ? ' active' : '')} role="button"
          aria-expanded=${open ? 'true' : 'false'} @click=${() => toggleCoop(g.id)}
          @dragover=${e => { e.preventDefault(); e.currentTarget.classList.add('cb-card-drag'); }}
          @dragleave=${e => { e.currentTarget.classList.remove('cb-card-drag'); }}
          @drop=${e => coopDrop(g, e)} title=${t('coop.head_hint', 'Show or hide this supplier’s cashbooks — drop its statement here to import it')}>
          <div class="cb-card-top">
            <div class="cb-card-name">${g.name || providerName(g.provider)}</div>
            <span class="cb-coop-chev" aria-hidden="true">${open ? '▾' : '▸'}</span>
          </div>
          <div class="cb-card-acct mono">${[providerName(g.provider), g.client_ref].filter(Boolean).join(' · ')}</div>
          <div class=${'cb-card-bal mono ' + (g.balance >= 0 ? 't-in' : 't-out')}>${fmtMoney(g.balance, baseCurrencySymbol(), true)}</div>
          ${g.funds !== null ? html`<div class="cb-coop-funds mono" title=${t('coop.own_funds_hint', 'Your own funds at the supplier — shareholder loans, shares, a deposit or wallet: your money, never netted against what you owe')}>
            ${t('coop.own_funds_total', 'Own funds {amount}').replace('{amount}', fmtMoney(g.funds, baseCurrencySymbol(), true))}</div>` : []}
          <div class="cb-coop-meta">
            <span>${n === 0 ? t('coop.no_cashbooks', 'No cashbooks yet') : n === 1 ? t('coop.one_cashbook', '1 cashbook') : t('coop.n_cashbooks', '{n} cashbooks').replace('{n}', n)}</span>
            ${st ? html`<button type="button" class=${'coop-pill coop-pill-' + st.tone} title=${t('coop.review_hint', 'Review the latest statement against its PDF')}
              @click=${e => { e.stopPropagation(); review.open(last.id); }}>${st.tone === 'held' ? '⚠ ' : (st.tone === 'ok' ? '✓ ' : '')}${t(st.key, st.text)}</button>` : []}
          </div>
          ${importing ? html`<span class="cb-imp-badge" title=${t('cashbook.importing_hint', 'Statement import running on this account')}>⏳ ${t('cashbook.importing', 'Importing')} · ${Number(cj[importing.id].progress) || 0}%</span>` : []}
        </div>
        ${open ? g.cards.map(a => card(a, true)) : []}
      </div>`;
    };
    // a co-op cashbook's own limit (what is left, or how far over it owes — in red), its terms and interest, and the
    // farmer's own funds marked as such (plan §1, §9, §12.5)
    const coopLines = a => {
      const lim = limitFigures(a), terms = termsText(a, t), sym = figsOf(a, a).symbol;
      return html`${a.own_funds ? html`<span class="cb-card-own">${t('coop.own_funds', 'Your own funds')}</span>` : []}
        ${lim ? html`<div class=${'cb-card-limit mono' + (lim.over ? ' is-over' : '')}>${lim.over
          ? t('coop.limit_over', 'Limit {limit} · over by {over}').replace('{limit}', fmtMoney(lim.limit, sym, true)).replace('{over}', fmtMoney(lim.over, sym, true))
          : t('coop.limit_left', 'Limit {limit} · {left} left').replace('{limit}', fmtMoney(lim.limit, sym, true)).replace('{left}', fmtMoney(lim.left, sym, true))}</div>` : []}
        ${terms ? html`<div class="cb-card-terms">${terms}</div>` : []}`;
    };
    // the ✎ form, in the card it edits: Save changes and Cancel to the right (golden rule #7); Enter saves, Esc closes
    const limitForm = a => html`<div class="cb-limit-edit" @click=${e => e.stopPropagation()}
        @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); saveLimit(a); } else if(e.key === 'Escape') closeLimitEdit(); }}>
      ${a.own_funds ? [] : html`<label class="cb-limit-lbl">${t('coop.limit', 'Credit limit')}
        <input class="form-control cb-in cb-limit-in" inputmode="decimal" placeholder=${t('coop.limit_ph', 'e.g. 500000')}
          .value=${limVal} @input=${e => { limVal = e.target.value; }}></label>`}
      <label class="cb-limit-lbl">${t('coop.terms', 'Terms')}
        <input class="form-control cb-in cb-terms-in" maxlength="200" placeholder=${t('coop.terms_ph', 'e.g. 30 days from statement')}
          .value=${termsVal} @input=${e => { termsVal = e.target.value; }}></label>
      <label class="cb-limit-lbl">${t('coop.rate', 'Interest, % a year')}
        <input class="form-control cb-in cb-rate-in" inputmode="decimal" placeholder=${t('coop.rate_ph', 'e.g. 11.5')}
          .value=${rateVal} @input=${e => { rateVal = e.target.value; }}></label>
      ${limErr ? html`<div class="cb-limit-err" role="alert">⚠ ${limErr}</div>` : []}
      <div class="cb-limit-edit-actions">
        <button type="button" class="btn btn-sm btn-outline cb-limit-cancel" @click=${closeLimitEdit}>${t('common.cancel', 'Cancel')}</button>
        <button type="button" class="btn btn-sm btn-primary cb-limit-save" ?disabled=${b === 'limit'} @click=${() => saveLimit(a)}>${t('common.save_changes', 'Save changes')}</button>
      </div>
    </div>`;
    const card = (a, section) => { const f = figsOf(a, a); return html`
        <div class=${'cb-card' + (section ? ' cb-card-section' : '') + (a.id === active ? ' active' : '') + (limEdit === a.id ? ' is-editing' : '')}
          @click=${() => {
            // Clicking a card selects it; clicking the card you are ALREADY on opens the statement
            // file picker for that account (owner 2026-09-19: "clicking the bank account pill should
            // open a file upload dialog as well") — so switching accounts doesn't pop a dialog.
            if(a.id === active) pickStatementFor(a); else selectAccount(a.id);
            // keep a running import's progress card visible when switching cards — there is no
            // per-account "review" to reopen any more (imports flow straight into the register)
            importOpen.value = activeJobs().length > 0; }}
          @dragover=${e => { e.preventDefault(); e.currentTarget.classList.add('cb-card-drag'); }}
          @dragleave=${e => { e.currentTarget.classList.remove('cb-card-drag'); }}
          @drop=${e => cardDrop(a, e)} title=${t('cashbook.drop_hint_card', 'Drop a statement here to import it to this account')}>
          <div class="cb-card-top">
            <div class="cb-card-name">${section ? sectionName(a) : a.name}</div>
            <span class="cb-card-icons">
              <button class="cb-card-icon" title=${t('cashbook.rules_hint', 'Allocation rules for this account')} @click=${e => { e.stopPropagation(); navigate(`/co/${companyId}/bank-rules`); }}>⚙</button>
              <button class="cb-card-icon" title=${t('cashbook.pdf_password', 'PDF password')} data-float-anchor
                @click=${e => { e.stopPropagation(); pwdVal = ''; pwdOpenId.value = pwdOpenId.value === a.id ? 0 : a.id; }}>🔒</button>
              ${a.kind === 'supplier_trading' ? html`<button class="cb-card-icon cb-card-limit-edit"
                title=${a.own_funds ? t('coop.edit_terms_hint', 'Terms and interest') : t('coop.edit_limit_hint', 'Credit limit, terms and interest')}
                @click=${e => { e.stopPropagation(); openLimitEdit(a); }}>✎</button>` : []}
            </span>
          </div>
          <div class="cb-card-acct mono">${(section ? [a.section_code, gcode(a.gl_code)] : [gcode(a.gl_code), a.account_no]).filter(Boolean).join(section ? ' · ' : ' - ')}${f.foreign && f.code ? html` <span class="cb-card-cur">${f.code}</span>` : []}</div>
          <div class=${'cb-card-bal mono ' + (f.balance >= 0 ? 't-in' : 't-out')}>${fmtMoney(f.balance, f.symbol, true)}</div>
          ${a.kind === 'supplier_trading' ? coopLines(a) : []}
          ${limEdit === a.id ? limitForm(a) : []}
          ${a.staged_count > 0 ? html`<span class="cb-card-badge">${a.staged_count}</span>` : []}
          ${cj[a.id] ? html`<span class="cb-imp-badge" title=${t('cashbook.importing_hint', 'Statement import running on this account')}>⏳ ${t('cashbook.importing', 'Importing')} · ${Number(cj[a.id].progress) || 0}%</span>` : []}
          ${pwdOpen === a.id ? html`
            <div class="cb-pwd-row" @click=${e => e.stopPropagation()}>
              <input type="password" class="cb-pwd-input"
                placeholder=${a.has_pdf_password ? t('cashbook.pwd_saved_ph', 'Saved — type to replace, save empty to clear') : t('cashbook.pwd_ph', 'Statement PDF password e.g. 8001015009087')}
                .value=${pwdVal} @input=${e => { pwdVal = e.target.value; }}>
              <button class="cb-pwd-save" ?disabled=${b === 'pwd'} @click=${() => savePdfPassword(a.id)}>✓</button>
            </div>` : []}
        </div>`; };
    return html`${keyBanner}${heldBlock}
      <input type="file" id="cbCardFile" class="cb-card-file"
        accept="application/pdf,image/png,image/jpeg,image/webp,text/csv,.csv,.ofx,.qif,.xls,.xlt,.xlsx,.xlsm,.xltx,.xltm" multiple
        @change=${onCardFilePicked}>
      <input type="file" id="cbAnyFile" class="cb-card-file"
        accept="application/pdf,image/png,image/jpeg,image/webp,text/csv,.csv,.ofx,.qif,.xls,.xlt,.xlsx,.xlsm,.xltx,.xltm" multiple
        @change=${onAnyFilePicked}>
      <div class="cb-cards">
      ${grouped.banks.map(a => card(a, false))}
      ${grouped.coops.map(coopBlock)}
      <div class="cb-card cb-card-drop" @click=${pickAnyStatement}
        @dragover=${e => { e.preventDefault(); e.currentTarget.classList.add('cb-card-drag'); }}
        @dragleave=${e => { e.currentTarget.classList.remove('cb-card-drag'); }}
        @drop=${generalDrop} title=${t('cashbook.drop_any_hint', 'Drop any statement — a bank’s or a supplier’s — or click to pick one: it is read and routed to its account')}>
        <div class="cb-card-name">⇣ ${t('cashbook.drop_any', 'Drop any statement')}</div>
        <div class="cb-card-acct">${t('cashbook.drop_any_sub', 'bank or supplier — routed for you')}</div>
      </div>
      <div class=${'cb-card cb-card-add' + (adding ? ' active' : '')}
        @click=${() => { if(adding) closeAddWizard(); else openAddWizard(); }}
        @dragover=${e => { e.preventDefault(); e.currentTarget.classList.add('cb-card-drag'); }}
        @dragleave=${e => { e.currentTarget.classList.remove('cb-card-drag'); }}
        @drop=${generalDrop} title=${t('cashbook.drop_hint_new', 'Drop a statement here to create a new account from it')}>
        <div class="cb-card-name">+ ${t('cashbook.add_account', 'Add account')}</div>
        <div class="cb-card-acct">${t('cashbook.add_account_sub', 'link, create, or drop a statement')}</div>
      </div>
    </div>
    ${adding ? addWizard(ref, glCandidates, step, suggestedCode, b) : []}`;
  };

  // ── the guided "new bank account" wizard (4 steps) ─────────────────────────
  // 1 details · 2 GL (mint a new unique code / link an existing) · 3 opening balance + date ·
  // 4 confirm (lists what will be created). The final Confirm posts create with opening_balance,
  // which the API posts as a real opening-balance journal (Dr bank GL / Cr Opening Balance Equity).
  function addWizard(ref, glCandidates, step, suggestedCode, b){
    if(aKind.value === 'coop') return coopWizard(step, b);
    const curCode = id => { const c = (ref.currencies || []).find(x => String(x.id) === String(id)); return c ? c.code : ''; };
    const openingAmt = parseAmount(aOpening, curMarks(aCur));
    const steps = html`<div class="cb-wiz-steps">
      ${[1, 2, 3, 4].map(n => html`<span class=${'cb-wiz-dot' + (n === step ? ' active' : (n < step ? ' done' : ''))}>${n}</span>`)}
    </div>`;
    const nav = (extra) => html`<div class="cb-wiz-nav">
      ${step > 1 ? html`<button type="button" class="btn btn-sm btn-outline cb-wiz-back" @click=${addStepBack}>${t('common.back', 'Back')}</button>` : []}
      ${extra}
    </div>`;
    let bodyHtml;
    if(step === 1){
      // the kind (plan §9): a bank account, or a co-op (supplier trading) one — not for a held bank statement
      bodyHtml = html`
        ${addImportJobId ? [] : kindPills()}
        <label class="cb-wiz-lbl">${t('cashbook.account_name_lbl', 'Account name')}
          <input type="text" class="form-control cb-in" placeholder=${t('cashbook.account_name_ph', 'Account name e.g. FNB Cheque')}
            .value=${aName} @input=${e => { aName = e.target.value; }}></label>
        <label class="cb-wiz-lbl">${t('cashbook.bank_lbl', 'Bank')}
          <input type="text" class="form-control cb-in" placeholder=${t('cashbook.bank_ph', 'Bank e.g. FNB')}
            .value=${aBank} @input=${e => { aBank = e.target.value; }}></label>
        <label class="cb-wiz-lbl">${t('cashbook.account_no_lbl', 'Account number')}
          <input type="text" class="form-control cb-in" placeholder=${t('cashbook.account_no_ph', 'Bank account no e.g. 62001234567')}
            .value=${aNo} @input=${e => { aNo = e.target.value; }}></label>
        <label class="cb-wiz-lbl">${t('cashbook.currency_lbl', 'Account currency')}
          <select class="form-control cb-in" .value=${aCur} @change=${e => { aCur = e.target.value; }}>
            <option value="">${t('cashbook.currency_base', '— Currency (company base) —')}</option>
            ${(ref.currencies || []).map(c => html`<option value=${String(c.id)}>${c.code}${c.symbol ? ' · ' + c.symbol : ''}</option>`)}
          </select></label>
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-next" @click=${addStepNext}>${t('common.next', 'Next')}</button>`)}`;
    } else if(step === 2){
      bodyHtml = html`
        <div class="cb-wiz-pills" role="radiogroup" aria-label=${t('cashbook.gl_mode_lbl', 'GL account')}>
          <button type="button" role="radio" aria-checked=${aGlMode.value === 'new'}
            class="cb-pill ${aGlMode.value === 'new' ? 'is-on' : ''}"
            @click=${() => { aGlMode.value = 'new'; }}>${t('cashbook.gl_new', 'Create a new GL account')}</button>
          <button type="button" role="radio" aria-checked=${aGlMode.value === 'existing'}
            class="cb-pill ${aGlMode.value === 'existing' ? 'is-on' : ''}"
            @click=${() => { aGlMode.value = 'existing'; }}>${t('cashbook.gl_existing', 'Use an existing GL account')}</button>
        </div>
        ${aGlMode.value === 'new'
          ? html`<label class="cb-wiz-lbl">${t('cashbook.gl_code_lbl', 'GL code (unique — 9500 series)')}
              <input type="text" class="form-control cb-in cb-in-gl mono cb-wiz-code" .value=${aCode || suggestedCode}
                @input=${e => { aCode = e.target.value; }}
                title=${t('cashbook.gl_code_hint', 'Each bank account gets its own unique code — edit if you prefer another')}></label>`
          : html`<label class="cb-wiz-lbl">${t('cashbook.gl_pick_lbl', 'Existing GL account')}
              <select class="form-control cb-in cb-wiz-glpick" .value=${String(aGlId || '')} @change=${e => { aGlId = e.target.value; }}>
                <option value="">${t('cashbook.gl_pick_ph', '— Choose a bank GL account —')}</option>
                ${glCandidates.map(a => html`<option value=${String(a.id)}>${gcode(a.code)} · ${a.name}</option>`)}
              </select></label>`}
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-next" @click=${addStepNext}>${t('common.next', 'Next')}</button>`)}`;
    } else if(step === 3){
      bodyHtml = html`
        <label class="cb-wiz-lbl">${t('cashbook.opening_date_lbl', 'As at date')}
          <sleek-date class="cb-in cb-wiz-opening-date"
            .value=${aOpeningDate} @change=${e => { aOpeningDate = e.target.value; }}></sleek-date></label>
        <label class="cb-wiz-lbl">${t('cashbook.opening_balance_lbl', 'Opening balance')}
          <input type="text" inputmode="decimal" class="form-control cb-in cb-wiz-opening" placeholder="0.00"
            .value=${aOpening} @input=${e => { aOpening = e.target.value; }}></label>
        <div class="cb-wiz-hint">${t('cashbook.opening_hint', 'A non-zero opening balance is posted to the ledger as an opening-balance journal so the account balance is correct from day one.')}</div>
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-next" @click=${addStepNext}>${t('common.next', 'Next')}</button>`)}`;
    } else {
      const glSummary = aGlMode.value === 'existing'
        ? (() => { const g = glCandidates.find(a => String(a.id) === String(aGlId)); return g ? `${gcode(g.code)} · ${g.name}` : t('cashbook.gl_none', 'no GL chosen'); })()
        : t('cashbook.gl_new_code', 'new GL {code}').replace('{code}', gcode(aCode || suggestedCode));
      bodyHtml = html`
        <div class="cb-wiz-confirm">
          <div class="cb-wiz-crow"><span>${t('cashbook.account_name_lbl', 'Account name')}</span><b>${aName || '—'}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.bank_lbl', 'Bank')}</span><b>${aBank || '—'}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.account_no_lbl', 'Account number')}</span><b class="mono">${aNo || '—'}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.currency_lbl', 'Account currency')}</span><b>${curCode(aCur) || t('cashbook.currency_base_short', 'Company base')}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.gl_account_lbl', 'GL account')}</span><b class="mono">${glSummary}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.opening_balance_lbl', 'Opening balance')}</span><b class="mono">${openingAmt ? fmtMoney(openingAmt, curSym(aCur), true) + ' · ' + aOpeningDate : t('common.none', 'None')}</b></div>
          ${openingAmt ? html`<div class="cb-wiz-note">${t('cashbook.will_post_opening', 'An opening-balance journal will be posted to the ledger.')}</div>` : []}
        </div>
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-confirm-btn" ?disabled=${b === 'addacct'} @click=${addAccount}>${b === 'addacct' ? t('common.creating', 'Creating…') : t('cashbook.create_account', 'Create account')}</button>`)}`;
    }
    return html`<form class="cb-addform cb-wizard" @submit=${e => e.preventDefault()}>
      <div class="cb-wiz-head">${steps}
        <button type="button" class="cb-wiz-x" title=${t('common.cancel', 'Cancel')} @click=${closeAddWizard}>✕</button></div>
      <div class="cb-wiz-body">${bodyHtml}</div>
    </form>`;
  }

  // the account's kind, first on the form (plan §9): a bank account, or a co-op (supplier trading) one
  function kindPills(){
    const k = aKind.value;
    return html`<div class="cb-wiz-pills cb-kind-pills" role="radiogroup" aria-label=${t('coop.kind_lbl', 'Account kind')}>
      <button type="button" role="radio" aria-checked=${k === 'bank' ? 'true' : 'false'} class=${'cb-pill cb-kind-bank' + (k === 'bank' ? ' is-on' : '')}
        @click=${() => { if(aKind.value !== 'bank'){ aKind.value = 'bank'; addStep.value = 1; } }}>${t('coop.kind_bank', 'Bank account')}</button>
      <button type="button" role="radio" aria-checked=${k === 'coop' ? 'true' : 'false'} class=${'cb-pill cb-kind-coop' + (k === 'coop' ? ' is-on' : '')}
        @click=${() => { if(aKind.value !== 'coop'){ resetCoopForm(); aKind.value = 'coop'; addStep.value = 1; } }}>${t('coop.kind_coop', 'Supplier account')}</button>
    </div>`;
  }

  // The co-op side of the form. A statement held for its co-op (needs_coop): ONE step — what the statement is, then
  // link it to a co-op the company has (of the same co-op) or make the co-op, and import it. By hand: co-op +
  // sub-account · opening balance · confirm.
  function coopWizard(step, b){
    const held = coopHeld.value, mode = cMode.value, gid = cGroupId.value, prov = cProvider.value;
    const read = held ? (held.read || {}) : null;
    const list = held ? linkableCoops(coops.value, read.provider) : coops.value;
    const c = coopChoice();
    const sections = c ? sectionChoices(coopProviders.value, c.provider, c.cashbooks.map(x => x.section_code)) : [];
    // the farmer's own funds at the co-op (plan §12.5) are a cashbook of their own: an asset, its opening in their favour
    const sec = sections.find(s => s.code === cSection), funds = !!sec && sec.nature === 'funds';
    const head = top => html`<div class="cb-wiz-head">${top}
      <button type="button" class="cb-wiz-x" title=${t('common.cancel', 'Cancel')} @click=${closeAddWizard}>✕</button></div>`;
    const modePills = list.length ? html`<div class="cb-wiz-pills" role="radiogroup" aria-label=${t('coop.coop_lbl', 'Supplier')}>
        <button type="button" role="radio" aria-checked=${mode === 'existing' ? 'true' : 'false'} class=${'cb-pill cb-coop-existing' + (mode === 'existing' ? ' is-on' : '')}
          @click=${() => { cSection = ''; cMode.value = 'existing'; }}>${t('coop.existing', 'A supplier I have')}</button>
        <button type="button" role="radio" aria-checked=${mode === 'new' ? 'true' : 'false'} class=${'cb-pill cb-coop-new' + (mode === 'new' ? ' is-on' : '')}
          @click=${() => { cSection = ''; cMode.value = 'new'; }}>${t('coop.new', 'A new supplier')}</button>
      </div>` : [];
    const coopFields = mode === 'existing' && list.length
      ? html`<label class="cb-wiz-lbl">${t('coop.coop_lbl', 'Supplier')}
          <select class="form-control cb-in cb-coop-pick" .value=${String(gid)} @change=${e => { cSection = ''; cGroupId.value = e.target.value; }}>
            <option value="">${t('coop.choose_coop_opt', '— Choose a supplier —')}</option>
            ${list.map(g => html`<option value=${String(g.id)}>${coopLabel(g)}</option>`)}
          </select></label>`
      : html`${held ? [] : html`<div class="cb-wiz-pills cb-coop-providers" role="radiogroup" aria-label=${t('coop.which_coop', 'Which supplier')}>
            ${coopProviders.value.map(p => html`<button type="button" role="radio" aria-checked=${prov === p.provider ? 'true' : 'false'}
              class=${'cb-pill' + (prov === p.provider ? ' is-on' : '')}
              @click=${() => { cSection = ''; if(!cName.trim()) cName = p.name; cProvider.value = p.provider; }}>${p.name}</button>`)}
          </div>`}
          <label class="cb-wiz-lbl">${t('coop.name_lbl', 'Name')}
            <input type="text" class="form-control cb-in cb-coop-name" placeholder=${t('coop.name_ph', 'Supplier name e.g. VKB')}
              .value=${cName} @input=${e => { cName = e.target.value; }}></label>
          <label class="cb-wiz-lbl">${t('coop.ref_lbl', 'Your account number at the supplier')}
            <input type="text" class="form-control cb-in cb-coop-ref" placeholder=${t('coop.ref_ph', 'Account no. e.g. L1234567')}
              .value=${cRef} @input=${e => { cRef = e.target.value; }}></label>`;

    if(held){
      const what = [providerName(read.provider), read.client_ref ? t('coop.account_no', 'account {ref}').replace('{ref}', read.client_ref) : '',
                    read.account_holder].filter(Boolean).join(' · ');
      const period = [read.period_start, read.period_end].filter(Boolean).map(dmy).join(' – ');
      return html`<form class="cb-addform cb-wizard cb-coop-wizard cb-coop-link" @submit=${e => e.preventDefault()}>
        ${head(html`<span class="cb-wiz-title">${t('coop.link_title', 'Link this supplier statement')}</span>`)}
        <div class="cb-wiz-body">
          <div class="cb-wiz-hint">${what}${period ? ' · ' + period : ''} · ${t('coop.n_lines', '{n} lines').replace('{n}', read.line_count || 0)}</div>
          ${(read.sections || []).length ? html`<ul class="coop-read-sections">${(read.sections || []).map(s => html`<li>
            <span class="mono">${s.code}</span> <span class="coop-read-label">${s.label}</span>
            ${s.imported === false ? html`<span class="muted">${t('coop.not_imported', 'Not imported')}</span>`
              : html`<span class="mono">${s.closing === null || s.closing === undefined ? '' : fmtMoney(s.closing, curSym(''), true)}</span>`}</li>`)}</ul>` : []}
          ${modePills}
          ${coopFields}
          <div class="cb-wiz-nav">
            <button type="button" class="btn btn-sm btn-primary cb-coop-link-go" ?disabled=${b === 'coopLink'} @click=${linkHeldCoop}>${b === 'coopLink' ? t('coop.importing', 'Importing…') : t('coop.import_into', 'Import into this supplier')}</button>
          </div>
        </div>
      </form>`;
    }

    const dots = html`<div class="cb-wiz-steps">
      ${[1, 2, 3].map(n => html`<span class=${'cb-wiz-dot' + (n === step ? ' active' : (n < step ? ' done' : ''))}>${n}</span>`)}
    </div>`;
    const nav = extra => html`<div class="cb-wiz-nav">
      ${step > 1 ? html`<button type="button" class="btn btn-sm btn-outline cb-wiz-back" @click=${addStepBack}>${t('common.back', 'Back')}</button>` : []}
      ${extra}
    </div>`;
    let bodyHtml;
    if(step === 1){
      bodyHtml = html`${kindPills()}${modePills}${coopFields}
        <label class="cb-wiz-lbl">${t('coop.section_lbl', 'Sub-account')}
          <select class="form-control cb-in cb-coop-section" .value=${cSection} @change=${e => { cSection = e.target.value; }}>
            <option value="">${c ? t('coop.choose_section_opt', '— Choose a sub-account —') : t('coop.choose_coop_first', '— Choose the supplier first —')}</option>
            ${sections.map(s => html`<option value=${s.code}>${s.code} — ${s.label}</option>`)}
          </select></label>
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-next" @click=${coopStepNext}>${t('common.next', 'Next')}</button>`)}`;
    } else if(step === 2){
      bodyHtml = html`
        <label class="cb-wiz-lbl">${t('cashbook.opening_date_lbl', 'As at date')}
          <sleek-date class="cb-in cb-wiz-opening-date"
            .value=${aOpeningDate} @change=${e => { aOpeningDate = e.target.value; }}></sleek-date></label>
        <label class="cb-wiz-lbl">${t('cashbook.opening_balance_lbl', 'Opening balance')}
          <input type="text" inputmode="decimal" class="form-control cb-in cb-wiz-opening"
            placeholder=${funds ? t('coop.opening_funds_ph', 'Held for you e.g. 2 000.00') : t('coop.opening_ph', 'Owed e.g. -12 500.00')}
            .value=${aOpening} @input=${e => { aOpening = e.target.value; }}></label>
        <div class="cb-wiz-hint">${t('coop.opening_hint', 'What you owe the supplier is negative (e.g. -12 500.00); money it holds for you is positive. It posts to the ledger as an opening-balance journal.')}</div>
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-next" @click=${coopStepNext}>${t('common.next', 'Next')}</button>`)}`;
    } else {
      const openingAmt = parseAmount(aOpening, curMarks(''));
      bodyHtml = html`
        <div class="cb-wiz-confirm">
          <div class="cb-wiz-crow"><span>${t('coop.coop_lbl', 'Supplier')}</span><b>${c ? coopLabel(c) + (c.id ? '' : ' (' + t('coop.new_short', 'new') + ')') : '—'}</b></div>
          <div class="cb-wiz-crow"><span>${t('coop.section_lbl', 'Sub-account')}</span><b>${sec ? sec.code + ' — ' + sec.label : '—'}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.gl_account_lbl', 'GL account')}</span><b>${funds ? t('coop.gl_funds', 'a new account for your own funds at the supplier (an asset)')
            : sec && sec.nature === 'loan' ? t('coop.gl_loan', 'a new loan account under the supplier') : t('coop.gl_creditor', 'a new creditor account under the supplier')}</b></div>
          <div class="cb-wiz-crow"><span>${t('cashbook.opening_balance_lbl', 'Opening balance')}</span><b class="mono">${openingAmt ? fmtMoney(openingAmt, curSym(''), true) + ' · ' + aOpeningDate : t('common.none', 'None')}</b></div>
          ${openingAmt ? html`<div class="cb-wiz-note">${t('cashbook.will_post_opening', 'An opening-balance journal will be posted to the ledger.')}</div>` : []}
        </div>
        ${nav(html`<button type="button" class="btn btn-sm btn-primary cb-wiz-confirm-btn" ?disabled=${b === 'addacct'} @click=${addCoopAccount}>${b === 'addacct' ? t('common.creating', 'Creating…') : t('cashbook.create_account', 'Create account')}</button>`)}`;
    }
    return html`<form class="cb-addform cb-wizard cb-coop-wizard" @submit=${e => e.preventDefault()}>
      ${head(dots)}
      <div class="cb-wiz-body">${bodyHtml}</div>
    </form>`;
  }

  // ── the register search box — ONE element for the life of the screen ──────
  // Built once here and placed into view(), never rebuilt by it. view() re-renders when a search
  // starts AND when its answer lands (regLoading / regRows / regTotal), so a box written inside it
  // was torn out while the request was at the server — every key typed then went to <body> (lost,
  // or a bare n/q/g fired a global shortcut) — and was rebuilt from the query that STARTED the
  // request, wiping anything typed after it. (Raymond 2026-10-07: typed letters "missed"; at three
  // keys a second "woolworths" came out "wowrh".) The same element keeps its text and caret; typing
  // writes nothing reactive (no re-render mid-word); the debounce commits regQ and reloads.
  const autoSearch = html`<input id="cbAutoSearch" type="search" class="form-control cb-in cb-auto-search"
    placeholder=${t('cashbook.search_txns_ph', 'Search — payee, amount, date (2026-05), GL code…')}
    @input=${e => {
      const v = e.target.value;
      clearTimeout(autoQTimer);
      autoQTimer = setTimeout(() => { regQ = v; loadRegister(true); }, 250);
    }}>`.firstElementChild;
  // A re-render MOVES the box into the new tree, and a moved element drops focus to <body>; keepFocus()
  // below hands it back like any other field (same element, so its text and caret are its own).

  // ── the field being typed in keeps its focus through a redraw ─────────────
  // Every signal view() reads redraws it (an import's progress, polled every 600 ms; a register page;
  // the balance after a row action; the pane fade clearing; the person's own date or tax pick), and a
  // redraw builds every field in it anew: the field being typed in was torn out, focus fell to <body>
  // and the next keys were lost. Most fields came back with their text (their handlers keep it in a
  // variable the redraw reads); the typeaheads (the typed filter is kept nowhere else) and a half-typed
  // date (it commits '' until whole) came back empty.
  // So: before the redraw, note the focused field by its id (a date by its <sleek-date>'s id) with its
  // text and caret. In a microtask (the new tree is in, the next key is not), if focus is still on
  // <body> (the person has not moved on; a field the redraw did not tear out still has it), focus the
  // field built under that id, give a typeahead or a date its text back, and put the caret back. The
  // person never left the field, so neither its leave nor its arrive behaviour runs: Chrome fires
  // 'blur' on a field it removes (the row-edit amount would reformat mid-number), and focus() runs a
  // field's on-focus work (a date selects all, "Move to…" lists every cashbook). Both events are muted
  // for this hand-back only, and it does not scroll the page back to a field the person scrolled away from.
  const TYPED_ONLY = ['cbGlSearch', 'cbMoveTo'];
  function keepFocus(){
    const el = document.activeElement;
    if(!el || !/^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName)) return;   // a field — never a date's calendar button
    const date = el.closest('sleek-date'), id = (date || el).id;
    if(!id) return;
    const text = el.value, from = el.selectionStart, to = el.selectionEnd;
    const mute = e => e.stopImmediatePropagation();
    window.addEventListener('blur', mute, true);
    queueMicrotask(() => {
      window.removeEventListener('blur', mute, true);
      if(document.activeElement && document.activeElement !== document.body) return;   // moved on
      const host = document.getElementById(id);
      const field = host && date ? host.querySelector('input') : host;
      if(!field) return;                                   // this redraw closed it (a saved row edit)
      if(field.value !== text && (date || TYPED_ONLY.includes(id))) field.value = text;
      window.addEventListener('focus', mute, true);
      try { field.focus({ preventScroll: true }); } finally { window.removeEventListener('focus', mute, true); }
      try { field.setSelectionRange(from, to); } catch { /* a select or a number field has no caret */ }
    });
  }

  // ── the working area — ONE reactive body ──────────────────────────────────
  const view = () => {
    keepFocus();                              // before this render tears out (or moves) the focused field
    const isLoading = loading.value, acct = accounts.value.find(a => a.id === sel.value);
    const bal = balance.value, ref = reference.value || {}, all = lines.value;
    const m = mode.value, tabv = tab.value, filterUn = onlyUnalloc.value, q = autoQ.value.toLowerCase();
    const rv = regView.value;                 // always '' — the register is one continuous list (kept for the divider/Load-more checks)
    const rsort = regSort.value;              // active column sort (read here — nested-slot rule)
    const selSet = selected.value, b = busy.value, editing = editId.value;
    const postAsk = confirmPost.value, delAsk = confirmDelId.value, bulkAsk = confirmBulkDel.value;
    const gapN = regGapCount.value, clearGapsAsk = confirmClearGaps.value;
    const imp = importOpen.value;
    const jobs = activeJobs();
    const adding = addOpen.value;
    const pin = pane.on.value ? ' pane-in' : '';

    if(isLoading) return html``;
    // "+ Add account" clears the work grid — the previous account's rows must not linger; an import still running keeps
    // its progress row, with its Cancel (2026-10-26: a 240-line statement was read and ended unseen behind the form)
    if(adding) return html`<div class="cb-work">${jobs.length ? html`<div class="cb-panel">${jobProgressUI(jobs)}</div>` : []}<div class="muted-block">${aKind.value === 'coop'
      ? (coopHeld.value
        ? t('coop.linking_block', 'Link the supplier statement in the form above — it imports once its supplier is chosen. Click a card to go back.')
        : t('coop.adding_block', 'Adding a supplier account — fill in the form above. Click a card or “+ Add account” again to go back.'))
      : t('cashbook.adding_block', 'Adding a bank account — fill in the form above (or drop a statement on the tile). Click a card or “+ Add account” again to go back.')}</div></div>`;
    if(!acct){
      // Empty company: a statement dropped on "+ Add account" runs BEFORE any account exists, so
      // show the extraction progress here instead of a bare "no accounts" (owner: a long import
      // looked like nothing was happening). When it finishes, loadAccounts selects the new account.
      if(jobs.length) return html`<div class="cb-work"><div class="cb-panel">${jobProgressUI(jobs)}</div></div>`;
      return html`<div class="muted-block">${t('cashbook.no_accounts', 'No bank accounts yet — add one above to start capturing.')}</div>`;
    }

    const staged = all.filter(l => l.status === 'unallocated' || l.status === 'allocated');
    const deposits = staged.filter(l => l.amount >= 0), payments = staged.filter(l => l.amount < 0);
    // totals come from the SERVER balance block — it sums over ALL of the account's lines,
    // where the loaded list is capped, so client sums under-count big imports (live parity:
    // v2's get_cashbook_balance computes ledger balance + staged in/out server-side too).
    // They are the account's OWN figures (own_*) with the account's mark: the block's plain balance /
    // projected are BASE (owner 2026-10-19: a rand account in a euro company read "R 100" for €100).
    // An older API without own_* leaves a foreign account's base figures with the BASE mark.
    const fig = figsOf(bal || {}, acct);
    const unalloc = bal ? bal.unallocated : staged.filter(l => l.status === 'unallocated').length;
    const ledger = fig.balance;
    const projected = fig.projected;
    // Server-authoritative count of allocated-but-unposted lines — works in BOTH the manual capture
    // view AND the auto register (imported lines you allocated but that never auto-posted, because
    // the on-import post refuses when ANY line still needs a GL). Using `bal` (the account balance
    // block: staged_count = unposted total, unallocated = still-unallocated) not the page-capped
    // `staged` is what lets the register carry a correct post control — the #2 fix (owner 2026-09-10:
    // "how are unposted ledgers posted if it is not in manual but in auto?").
    const postableN = bal ? Math.max(0, bal.staged_count - bal.unallocated) : Math.max(0, staged.length - unalloc);
    const hasStaged = bal ? bal.staged_count > 0 : staged.length > 0;
    const gate = postGateMessage(unalloc, postableN);
    // a co-op cashbook whose statement does not tie to its PDF: none of that statement's lines post (the API holds them
    // — plan §7); the banner says so, with the way to its review
    const heldNow = acctHeld.value;
    const held = (acct.kind === 'supplier_trading' && heldNow.accountId === acct.id) ? heldNow.sets : [];
    // what holds them, in words — its PDF, the co-op's statement before, a locked year (a set whose check did not
    // answer: its PDF, as the banner always said)
    const heldBy = held.reduce((w, h) => { const y = h.why || { pdf: true };
      return { pdf: w.pdf || !!y.pdf, carry: w.carry || !!y.carry, lock: w.lock || !!y.lock, stale: w.stale || !!y.stale }; },
    { pdf: false, carry: false, lock: false, stale: false });
    const heldMsg = !heldBy.carry && !heldBy.lock && !heldBy.stale
      ? t('coop.held_banner', 'This cashbook’s supplier statement does not tie to its PDF yet — nothing of it posts until it does.')
      : t('coop.held_banner_why', 'This cashbook’s supplier statement is held: {why} — nothing of it posts until it is put right.')
        .replace('{why}', heldReasons(heldBy, t).join('; '));

    let rows = m === 'auto' ? staged : (tabv === 'deposit' ? deposits : payments);
    if(filterUn) rows = rows.filter(l => l.status === 'unallocated');
    // the REGISTER (auto mode) — the bank statement forever: server-paged, every
    // status, searched server-side (regQ), newest first; grey/red locks per row
    let feed = m === 'auto' ? regRows.value : [];
    if(m === 'auto' && filterUn) feed = feed.filter(l => l.status === 'unallocated');
    const regN = regTotal.value, regBusy = regLoading.value;
    const feedStaged = feed.filter(l => l.status === 'unallocated' || l.status === 'allocated');
    // a co-op cashbook's balance moves between the co-op's sub-accounts (journals with no line on any statement) as
    // rows of their own among the lines, by date — in date order with nothing searched or filtered (a search or a
    // filter shows the lines it finds); within a From/To window, the moves in it
    const moving = m === 'auto' && rsort.key === 'date' && !regQ.trim() && !filterUn && !rv && !regGaps.value
      && !regTransfers.value;
    const moves = moving ? regMoves.value.filter(mv => (!regFrom || mv.date >= regFrom) && (!regTo || mv.date <= regTo)) : [];
    const shown = withMoves(feed, moves, { dir: rsort.dir, complete: feed.length >= regN });
    const selectable = m === 'auto' ? feedStaged : rows;
    const allChecked = selectable.length > 0 && selectable.every(l => selSet.has(l.id));
    // the account's own currency (per-bank-account currencies — R / $ / €): line amounts, running
    // balances and the statement balance are all in it
    // (a currency without a symbol shows its code — never the base mark on the account's figures)
    const sym = (acct && (acct.currency_symbol || acct.currency_code)) || baseCurrencySymbol();
    const fmtM = v => fmtMoney(v, sym), fmtMS = v => fmtMoney(v, sym, true);
    // the book / projected figures carry THEIR currency's mark (fig.symbol — see above)
    const fmtF = v => fmtMoney(v, fig.symbol), fmtFS = v => fmtMoney(v, fig.symbol, true);
    // the opening balance is a ledger journal: BASE — the account's mark only on a base account
    const openSym = (fig.own && !fig.foreign) ? sym : baseCurrencySymbol();
    const fmtO = v => fmtMoney(v, openSym), fmtOS = v => fmtMoney(v, openSym, true);
    // a balance the co-op moved between its sub-accounts: a journal like the opening (BASE), a row of its own — what it
    // moved and with which of the co-op's cashbooks, the balance after it; its 🔎 opens the statement that opens them so.
    // A difference no move explains, booked from that statement's review, says the account it went to (booked_to).
    const moveRow = mv => {
      const others = (mv.others || []).map(o => o.name).filter(Boolean).join(', ');
      const coopName = acct.group_name || t('coop.coop', 'Supplier');
      const to = mv.booked_to ? `${mv.booked_to.code || ''} ${mv.booked_to.name || ''}`.trim() : '';
      return html`<tr class="cb-line cb-row-move" data-move-id=${mv.journal_id}>
        <td class="cb-th-sel"></td>
        <td class="mono cb-td-date" data-label=${t('common.date', 'Date')}>${dmy(mv.date)}</td>
        <td class="cb-td-ref mono" data-label=${t('common.ref', 'Ref')}>${mv.reference || ''}</td>
        <td class="cb-td-desc" data-label=${t('common.description', 'Description')}><b>${to
          ? t('cashbook.coop_difference', 'A difference {coop}’s statement opens with, booked to {account}').replace('{coop}', coopName).replace('{account}', to)
          : t('cashbook.coop_move', 'Moved between {coop}’s sub-accounts').replace('{coop}', coopName)}</b>${others ? html`<br><span class="muted">${' ' + t('cashbook.coop_move_with', 'with {cashbooks}')
          .replace('{cashbooks}', others)}</span>` : []}</td>
        <td class="cb-td-act2" data-label=${t('common.actions', 'Actions')}><span class="cb-row-actions">${mv.statement_set_id
          ? html`<button class="cb-locate cb-move-review" title=${t('cashbook.coop_move_review', 'Review the statement that opens the sub-accounts so')}
              @click=${() => review.open(mv.statement_set_id)}>🔎</button>` : []}${mv.locked
          ? html`<span class="row-lock row-lock-red" title=${t('cashbook.locked_hint', 'This post is locked — see Financial Years (top-right corner)')}>🔒</span>` : []}</span></td>
        <td class="mono text-right t-in cb-td-received" data-label=${t('cashbook.received', 'Received')}>${mv.amount >= 0 ? fmtO(mv.amount) : ''}</td>
        <td class="mono text-right t-out cb-td-paid" data-label=${t('cashbook.paid', 'Paid')}>${mv.amount < 0 ? fmtO(mv.amount) : ''}</td>
        <td class="cb-td-source" data-label=${t('common.source', 'Source')}><span class="cb-src cb-src-coop">${t('coop.coop', 'Supplier')}</span></td>
        <td class="mono text-right cb-td-balance" data-label=${t('common.balance', 'Balance')}>${mv.balance != null
          ? html`<span class=${mv.balance >= 0 ? 't-in' : 't-out'}>${fmtMS(mv.balance)}</span>` : ''}</td>
      </tr>`;
    };
    // reconciles indicator: the newest statement balance vs the projected book figure. The
    // statement balance is a per-ACCOUNT fact from the balance endpoint (the latest bank-stated
    // running balance over ALL lines) — NOT scanned off the currently-loaded register rows, so it
    // no longer shifts when the register is filtered (owner 2026-09-21: "why does Off by change
    // when I filter?"). Both sides in the account's own currency, or no Off by at all.
    const newestBal = (bal && bal.statement_balance !== null && bal.statement_balance !== undefined)
      ? bal.statement_balance : undefined;
    const reconDelta = (bal && newestBal !== undefined) ? reconOffBy(fig, newestBal) : null;

    const taxCodes = ref.tax_codes || [], costCentres = ref.cost_centres || [];
    const dests = [['G', t('bank_import.general', 'General')]];
    if(ref.ar_control_account_id) dests.push(['D', t('cashbook.debtor', 'Debtor')]);
    if(ref.ap_control_account_id) dests.push(['C', t('cashbook.creditor', 'Creditor')]);
    if(ref.vat_registered && ref.vat_control_account_id) dests.push(['V', t('common.vat', 'VAT')]);
    const vatOn = !!ref.vat_registered;   // the VAT flag gates all tax UI in the cashbook
    const glCandidatePool = (ref.accounts || []).filter(a =>
      a.is_postable && !a.is_bank && !a.control_type && a.code !== '0000.000' && a.code !== '0000');

    // a payment between two of the company's own accounts that needs the user (the API's bank_transfer_checks): what is
    // wrong, and how to put it right
    const transferNote = c => {
      if(!c) return [];
      const text = {
        twice: t('cashbook.transfer_twice', 'In the books twice: {other}’s statement has this payment too ({date}). Unpost it (🔓) and post it again — it then goes through {via}.'),
        opening: t('cashbook.transfer_opening', 'In the books twice: {other}’s opening balance ({date}) already holds this payment. Unpost it (🔓) and post it again — it then goes through {via}, which {other}’s opening clears.'),
        supplier: t('cashbook.transfer_supplier', '{coop}’s statement has this payment too ({other}, {date}), but it is booked to {coop} as a supplier, on {control}. Unpost it (🔓) and post it again — it then goes through {via}.'),
        supplier_opening: t('cashbook.transfer_supplier_opening', '{coop}’s opening balance ({date}) already holds this payment, but it is booked to {coop} as a supplier, on {control}. Unpost it (🔓) and post it again — it then goes through {via}, which {coop}’s opening clears.'),
        elsewhere: t('cashbook.transfer_elsewhere', '{other}’s line for this payment ({date}) is booked to {booked}, so {via} holds the payment. Book that line to this account.'),
        arrival: t('cashbook.transfer_arrival', 'This looks like the other side of {other}’s line of {date}, which went through {via}. Booked to {booked}, it leaves {via} holding the payment: book it to {other}.'),
        missing: t('cashbook.transfer_missing', '{other}’s statements show no line for this payment, so {via} holds it. Check the amount and date on {other}’s statement.'),
      }[c.kind];
      return text ? html`<div class="cb-transfer-note">⚠ ${fill(text, { other: c.other, date: c.other_date ? dmy(c.other_date) : '',
        via: c.via, booked: c.booked_to, coop: c.coop, control: c.control })}</div>` : [];
    };
    // a co-op line that moved money to an account at the co-op the books do not keep (the API's moved_to), until posted
    const movedNote = l => l.moved_to ? html`<div class="cb-moved-note">↗ ${movedOutText(l.moved_to, t)}</div>` : [];

    // the allocate chip (+ AI suggestion) shown UNDER the description — live anatomy
    const allocCell = l => {
      // a BATCH (one line paying several invoices) expands to its payments; a line that pays one
      // invoice keeps the ordinary chip, with reallocate and clear, exactly as before
      if(l.status === 'posted' && l.match_type === 'document' && Number(l.settlement_count) > 1)
        return settlementDetails(companyId, sel.value, l, sym, settlementCache);
      if(l.status !== 'unallocated'){
        return html`<span class="cb-alloc-line">
          <button class="alloc-btn-matched mono" data-float-anchor title=${t('cashbook.click_reallocate', 'Click to reallocate')}
            @click=${e => { e.stopPropagation(); openAllocPop(l, e.target); }}>${l.contact_id ? (l.contact_name || '') : gcode(l.allocated_code || '')}</button>
          <span class="cb-gl-desc">${l.contact_id ? '' : (l.allocated_name || '')}</span>
          ${l.transfer_via ? html`<span class="cb-via-tag" title=${fill(t('cashbook.via_hint', 'A payment between two of your own accounts: each statement’s line of it is booked through {via}, so the payment is in the books once'), { via: l.transfer_via })}>${t('cashbook.via', 'via')} ${String(l.transfer_via).split(' ')[0]}</span>` : []}
          ${l.match_type === 'ai' && l.ai_confidence ? html`<span class="cb-ai-tag" title=${t('cashbook.ai_allocated', 'Auto-allocated by AI')}>AI ${confPct(l.ai_confidence)}%</span>` : []}
          <button class="cb-clear-btn" title=${t('cashbook.clear_allocation', 'Clear allocation')} @click=${e => { e.stopPropagation(); clearAllocation(l); }}>✕</button>
        </span>${transferNote(l.transfer_check)}${movedNote(l)}`;
      }
      // the learner's suggestion (how this company booked the same merchant + branch) when it has one;
      // the name-based guess only when there's no history (plan/allocation-learner.md)
      const guess = l.learned ? null : scoreGL(l.description, glCandidatePool)[0];
      const sg = suggestionFor(l, guess);
      return html`<span class="cb-alloc-line">
        <button class="alloc-btn" data-float-anchor @click=${e => { e.stopPropagation(); openAllocPop(l, e.target); }}>+ ${t('cashbook.allocate_gl', 'Allocate GL Account')}</button>
        ${sg ? html`<button class=${'cb-ai-chip' + (sg.rule ? ' cb-rule-chip' : sg.learned ? ' cb-learned-chip' : '')}
          title=${sg.learned ? sg.reason : t('cashbook.suggested_hint', 'Suggested — click to allocate')}
          @click=${e => { e.stopPropagation();
            const auto = mode.value === 'auto';   // Auto mode posts the line the moment it is allocated
            const place = placeOf(l.id);          // where the user clicked — the view comes back here after
            api.post(P(`/bank-lines/${l.id}/allocate`), { account_id: sg.account_id, save_as_rule: true, apply_to_similar: false, post: auto })
              .then(res => { let msg = t('cashbook.allocated', 'Allocated');
                if(res && res.posted) msg += res.posted > 1 ? ` · ${res.posted} posted` : ' · posted';
                setToast(msg);
                if(res && res.post_error) setToast(res.post_error, 'warn');
                return refresh(auto ? place : null); })
              .catch(err => setToast((err.data && err.data.error) || t('cashbook.allocation_failed', 'Allocation failed'), 'err')); }}>
          <span class="cb-ai-tag">${sg.rule ? t('bank.rule_tag', 'Rule') : sg.learned ? '✓ ' + sg.evidence : 'AI ' + confPct(l.match_type === 'ai_suggest' ? l.ai_confidence : null, guess.score) + '%'}</span> <span class="mono">${gcode(sg.code)}</span> ${sg.name}</button>` : []}
        ${l.match_type === 'rule_declined' && l.match_note ? html`<span class="cb-rule-note">⚠ ${t('cashbook.rule_helper', 'AI rule helper')}: ${l.match_note}</span>` : []}
      </span>${movedNote(l)}`;
    };

    const taxTitleOf = l => { const tc = taxCodes.find(x => x.id === l.tax_code_id); return tc ? t('common.code', 'Code') + ': ' + tc.code : ''; };
    // VAT code on the register row (owner 2026-09-19): a small badge so the tax implication of an
    // allocation is visible WITHOUT opening the line — shown whenever a line carries a tax code,
    // even when the tax amount is zero (zero-rated/exempt still tell a story on the VAT report).
    const vatBadge = l => {
      if(!l.tax_code_id) return '';
      const tc = taxCodes.find(x => x.id === l.tax_code_id);
      const code = tc ? tc.code : (l.tax_code || '');
      if(!code) return '';
      const amt = l.tax_amount ? ' · ' + fmtM(Math.abs(l.tax_amount)).replace(sym + ' ', '') : '';
      return html`<span class="cb-vat-badge" title=${t('common.vat', 'VAT') + ' ' + code + (l.tax_amount ? ' ' + fmtM(Math.abs(l.tax_amount)) : '')}>${t('common.vat', 'VAT')} ${code}${amt}</span>`;
    };
    const destTag = l => {
      let code = 'G';
      if(l.contact_id){
        const c = (ref.contacts || []).find(x => x.id === l.contact_id);
        code = (c && c.is_supplier && !c.is_customer) ? 'C' : 'D';
      } else if(l.allocated_account_id && l.allocated_account_id === ref.vat_control_account_id){
        code = 'V';
      }
      return html`<span class=${'cb-dest-tag cb-dest-' + code.toLowerCase()}>${code}</span>`;
    };

    const deleteControl = l => delAsk === l.id
      ? html`<span class="confirm-inline"><span class="confirm-q">${t('common.delete_confirm', 'Delete?')}</span>
          <button class="btn-confirm-yes" ?disabled=${b === 'del' + l.id} @click=${() => deleteLine(l.id)}>${t('common.yes', 'Yes')}</button>
          <button class="btn-confirm-no" @click=${() => { confirmDelId.value = 0; }}>${t('common.no', 'No')}</button></span>`
      : html`<button class="del-btn" title=${t('common.delete', 'Delete')} @click=${() => { confirmDelId.value = l.id; }}>✕</button>`;

    return html`<div class=${'cb-work' + pin}>
      <!-- sticky summary strip -->
      <div class="cb-summary">
        <div class="cb-summary-bal">
          <span class="cb-summary-label">${t('cashbook.ledger_balance', 'Ledger Balance')}</span>
          <span class=${'cb-summary-value mono ' + (ledger >= 0 ? 't-in' : 't-out')}>${fmtFS(ledger)}</span>
        </div>
        ${reconDelta !== null ? (Math.abs(reconDelta) < 0.005
          ? html`<span class="cb-recon cb-recon-ok" title=${t('cashbook.reconciles_hint', 'The newest statement balance equals the projected book balance')}>✓ ${t('cashbook.reconciles', 'Reconciles')}</span>`
          : html`<span class="cb-recon cb-recon-off" title=${'Projected ' + fmtFS(projected) + ' vs statement ' + fmtMS(newestBal)}>△ Off by <span class="cb-recon-amt">${fmtF(reconDelta)}</span></span>`) : []}
        ${unalloc > 0 ? html`
          <button class=${'cb-unalloc-badge' + (filterUn ? ' on' : '')} title=${unalloc + ' ' + t('cashbook.entries_need_gl', 'entries still need GL allocation')} @click=${() => { onlyUnalloc.value = !onlyUnalloc.value; }}>⚠ ${unalloc} ${t('cashbook.unallocated', 'Unallocated')}</button>
          <button class="btn btn-sm btn-outline" title=${t('cashbook.reallocate_hint', 'Re-run matching rules on unallocated entries')} ?disabled=${b === 'rerun'} @click=${rerunAllocations}>${b === 'rerun' ? t('cashbook.rerunning', 'Re-running…') : '⟳ ' + t('cashbook.reallocate', 'Re-run Allocations')}</button>` : []}
        ${(m === 'manual' && hasStaged) ? (postAsk
          ? html`<span class="confirm-inline"><span class="confirm-q">Post ${postableN} entries?</span>
              <button class="btn-confirm-yes" ?disabled=${b === 'post'} @click=${postToLedger}>${b === 'post' ? t('cashbook.posting', 'Posting…') : t('common.yes', 'Yes')}</button>
              <button class="btn-confirm-no" @click=${() => { confirmPost.value = false; }}>${t('common.no', 'No')}</button></span>`
          : html`<button class="btn btn-sm btn-primary" title=${gate || ''} @click=${() => { gate ? setToast(gate, 'warn') : confirmPost.value = true; }}>${t('common.post_to_ledger', 'Post to Ledger')}</button>`) : []}
        ${regStmts.value.length ? html`<select id="cbJump" class="form-control cb-in cb-jump" title=${t('cashbook.jump_hint', 'Jump the register to a statement')}
          @change=${e => { jumpToStatement(e.target.value); e.target.value = ''; }}>
          <option value="">${t('cashbook.jump_to', 'Jump to…')}</option>
          <option value="__all__">${t('cashbook.jump_all', 'All (clear filter)')}</option>
          ${regStmts.value.map(s => html`<option value=${s.id}>${(s.reference || 'BS')} · ${dmy(s.period_from)} – ${dmy(s.period_to)}</option>`)}
        </select>` : []}
        <button class="btn btn-sm btn-outline cb-fetch-mail" title=${t('cashbook.fetch_mail_hint', 'Check email for new statement imports for this company')} ?disabled=${b === 'fetchmail'} @click=${fetchEmailImports}>${b === 'fetchmail' ? t('cashbook.fetching_mail', 'Checking…') : '✉ ' + t('cashbook.fetch_mail', 'Fetch mail')}</button>
        <button class="btn btn-sm btn-outline cb-statements" title=${t('cashbook.statements_hint', 'View imported statements with reconciliation for this account')} ?disabled=${!sel.value} @click=${() => { if(sel.value) navigate(`/co/${companyId}/bank/${sel.value}/statements`); }}>${'▤ ' + t('cashbook.statements', 'Statements')}</button>
      </div>

      <!-- the account reconciles yet lines carry the gap warning: clear them all. Its own line, as the strip above is ONE
           line on a desktop with no room to spare (the offer in it pushed Statements out of reach at a 1280px window). -->
      ${canClearGaps(reconDelta, gapN) ? html`<div class="cb-gaps-banner" role="status">
        <span class="cb-gaps-msg">✓ ${gapN === 1 ? t('cashbook.gaps_reconcile_1', 'The account reconciles, yet 1 line carries a gap warning.')
          : fill(t('cashbook.gaps_reconcile', 'The account reconciles, yet {n} lines carry a gap warning.'), { n: gapN })}</span>
        ${clearGapsAsk
          ? html`<span class="confirm-inline cb-clear-gaps-ask"><span class="confirm-q">${gapN === 1
              ? t('cashbook.clear_gap_q', 'Clear the gap warning? The line stays as it is.')
              : fill(t('cashbook.clear_gaps_q', 'Clear all {n} gap warnings? The lines stay as they are.'), { n: gapN })}</span>
              <button class="btn-confirm-yes" ?disabled=${b === 'cleargaps'} @click=${clearAllGaps}>${b === 'cleargaps' ? t('cashbook.clearing', 'Clearing…') : t('common.yes', 'Yes')}</button>
              <button class="btn-confirm-no" ?disabled=${b === 'cleargaps'} @click=${() => { confirmClearGaps.value = false; }}>${t('common.no', 'No')}</button></span>`
          : html`<button class="btn btn-sm btn-outline cb-clear-gaps" title=${t('cashbook.clear_gaps_hint', 'The account reconciles to its statement — clear the gap warning on every line. No line changes.')}
              @click=${() => { confirmClearGaps.value = true; }}>✓ ${gapN === 1 ? t('cashbook.clear_gap', 'Clear 1 gap warning')
              : fill(t('cashbook.clear_gaps', 'Clear {n} gap warnings'), { n: gapN })}</button>`}
      </div>` : []}

      ${held.length ? html`<div class="cb-held-banner" role="status">
        <span class="cb-held-msg">⚠ ${heldMsg}</span>
        <button class="btn btn-sm btn-outline cb-held-review" @click=${() => review.open(held[0].id)}>${t('coop.review_btn', 'Review')}</button>
      </div>` : []}

      ${imp && jobs.length ? html`<div class="cb-panel">${jobProgressUI(jobs)}</div>` : []}

      ${m === 'manual' ? html`
        <div class="cb-tabs">
          <button class=${'cb-tab cb-tab-dep' + (tabv === 'deposit' ? ' active' : '')} @click=${() => { tab.value = 'deposit'; onlyUnalloc.value = false; pane(); }}>
            ${t('bank_import.deposits', 'Deposits')} <span class="cb-tab-badge">${deposits.length}</span></button>
          <button class=${'cb-tab cb-tab-pay' + (tabv === 'payment' ? ' active' : '')} @click=${() => { tab.value = 'payment'; onlyUnalloc.value = false; pane(); }}>
            ${t('bank_import.payments', 'Payments')} <span class="cb-tab-badge">${payments.length}</span></button>
        </div>` : []}

      ${m === 'auto' ? html`<div class="cb-auto-toolbar">
        ${autoSearch}
        <label class="cb-date-filter">${t('cashbook.date_from', 'From')}
          <sleek-date id="cbFrom" class="cb-in cb-date-in" .value=${regFrom}
            @change=${e => { regFrom = e.target.value; loadRegister(true); }}></sleek-date></label>
        <label class="cb-date-filter">${t('cashbook.date_to', 'To')}
          <sleek-date id="cbTo" class="cb-in cb-date-in" .value=${regTo}
            @change=${e => { regTo = e.target.value; loadRegister(true); }}></sleek-date></label>
        <label class="check-line"><input type="checkbox" .checked=${allChecked} @change=${e => selectAllVisible(feedStaged, e.target.checked)}> ${t('common.select_all', 'Select All')}</label>
        <label class="check-line cb-show-unposted"><input type="checkbox" .checked=${regView.value === 'unposted'} @change=${e => { regView.value = e.target.checked ? 'unposted' : ''; loadRegister(true); }}> ${t('cashbook.show_unposted', 'Show Unposted')}</label>
        <label class="check-line cb-show-gaps"><input type="checkbox" .checked=${regGaps.value} @change=${e => { regGaps.value = e.target.checked; loadRegister(true); }}> ${t('cashbook.show_gaps', 'Show gaps')}</label>
        ${(regChecks.value || regTransfers.value) ? html`<label class="check-line cb-show-transfers" title=${t('cashbook.transfers_hint', 'Payments between two of your own accounts that are in the books twice, or whose other side is booked to something else')}><input type="checkbox" .checked=${regTransfers.value} @change=${e => { regTransfers.value = e.target.checked; loadRegister(true); }}> ⚠ ${fill(t('cashbook.transfers_to_check', 'Transfers to check ({n})'), { n: regChecks.value })}</label>` : []}
      </div>` : []}

      ${selSet.size > 0 ? html`<div class="cb-bulk-bar">
        <span class="fw-700">${selSet.size}</span> ${t('common.selected', 'selected')}
        <input id="cbMoveTo" type="text" class="form-control cb-in cb-bulk-sel" data-float-anchor autocomplete="off"
          placeholder=${t('cashbook.move_to_ph', '— Move to… —')} .value=${acctName(bulkTarget)} aria-label="Move selected to cashbook"
          @focus=${e => { e.target.select(); showMoveDrop(e.target, moveTargets(acct), a => { bulkTarget = String(a.id); }, true); }}
          @input=${e => showMoveDrop(e.target, moveTargets(acct), a => { bulkTarget = String(a.id); }, false)}
          @keydown=${moveDropKeydown}
          @blur=${e => setTimeout(() => { e.target.value = acctName(bulkTarget); }, 200)}>
        <button class="btn btn-sm btn-primary" ?disabled=${b === 'bulk'} @click=${bulkMove}>${t('cashbook.move', 'Move')}</button>
        <button class="btn btn-sm btn-outline" title=${t('cashbook.flip_bulk_hint', 'Flip selected between deposits/payments')} ?disabled=${b === 'bulk'} @click=${bulkFlip}>⇄ ${t('cashbook.flip', 'Flip')}</button>
        ${bulkAsk ? html`<span class="confirm-inline"><span class="confirm-q">Delete ${selSet.size} entries?</span>
          <button class="btn-confirm-yes" ?disabled=${b === 'bulk'} @click=${bulkDelete}>${t('common.yes', 'Yes')}</button>
          <button class="btn-confirm-no" @click=${() => { confirmBulkDel.value = false; }}>${t('common.no', 'No')}</button></span>`
        : html`<button class="btn btn-sm btn-danger" title=${t('cashbook.delete_bulk_hint', 'Delete selected entries')} ?disabled=${b === 'bulk'} @click=${() => { confirmBulkDel.value = true; }}>${t('common.delete', 'Delete')}</button>`}
        <button class="btn btn-sm btn-outline" @click=${() => { selected.value = new Set(); confirmBulkDel.value = false; }}>${t('common.deselect_all', 'Deselect All')}</button>
      </div>` : []}

      ${m === 'manual' ? html`
        <div class="cb-form">
          <div class="cb-row">
            <sleek-date id="cbDate" class="cb-w-date" data-next="#cbGlSearch" .value=${fDate}
              @change=${e => { fDate = e.target.value; }}></sleek-date>
            <div class="cb-dest-pills">
              ${dests.map(([code, label]) => html`<button type="button" data-dest=${code}
                class=${'cb-dest-pill' + (fDest === code ? ' active' : '')}
                @click=${() => setDest(code)}>${label}</button>`)}
            </div>
            <div class="cb-gl-wrap">
              <input id="cbGlSearch" type="text" class="form-control cb-in" data-float-anchor autocomplete="off"
                placeholder=${t('cashbook.gl_ph', 'GL account… e.g. Rent')} .value=${fGlLabel}
                @input=${e => showGlDrop(e.target)} @keydown=${glKeydown}
                @focus=${e => { if(e.target.value && !fGlId) showGlDrop(e.target); }}>
            </div>
            <input id="cbRef" type="text" class="form-control cb-in cb-w-ref" placeholder=${t('common.ref_ph', 'Ref e.g. BS1')}
              tabindex=${fRef.trim() ? '-1' : '0'} .value=${fRef}
              @input=${e => { fRef = e.target.value;
                e.target.tabIndex = e.target.value.trim() ? -1 : 0; /* live: Tab skips a filled Ref */ }}
              @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); const d = document.getElementById('cbDesc'); d && d.focus(); } }}>
            <input id="cbDesc" type="text" class="form-control cb-in cb-w-desc" placeholder=${t('cashbook.desc_ph', 'Description e.g. Rent August')}
              .value=${fDesc} @input=${e => { fDesc = e.target.value; }}
              @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); const a = document.getElementById('cbAmount'); a && a.focus(); } }}>
            <select id="cbTax" class=${'form-control cb-in cb-w-tax' + (vatOn ? '' : ' cb-hide')}
              tabindex="-1" .value=${fTaxId}
              @change=${e => { fTaxId = e.target.value; updateVatPreview();
                const tc = taxCodes.find(x => String(x.id) === e.target.value);
                dieselOn.value = !!(tc && String(tc.code) === '25'); }}>
              <option value="">${t('cashbook.no_tax', '— No tax —')}</option>
              ${taxCodes.map(tc => html`<option value=${tc.id}>${tc.code} - ${tc.name}</option>`)}
            </select>
            <input id="cbAmount" type="text" inputmode="decimal" autocomplete="off" class="form-control cb-in cb-w-amt mono" placeholder="e.g. 1 500.00"
              .value=${fAmount} @input=${e => { fAmount = e.target.value; updateVatPreview(); }}
              @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); addEntry(); } }}>
            ${costCentres.length ? html`<select id="cbCc" class="form-control cb-in cb-w-cost" tabindex="-1" .value=${fCcId} @change=${e => { fCcId = e.target.value; }}>
              <option value="">CC</option>
              ${costCentres.map(cc => html`<option value=${cc.id}>${cc.code}</option>`)}
            </select>` : []}
            <button class="btn btn-sm btn-primary cb-add" ?disabled=${b === 'add'} @click=${addEntry}>${b === 'add' ? '…' : t('common.add', 'Add')}</button>
          </div>
          <div class="cb-form-options">
            <label class="cb-switch"><input type="checkbox" .checked=${fIncRef} @change=${e => { fIncRef = e.target.checked; }}><span class="cb-slider"></span> ${t('cashbook.inc_ref', 'Inc. Ref no')}</label>
            <label class="cb-switch"><input type="checkbox" .checked=${fKeepDesc} @change=${e => { fKeepDesc = e.target.checked; }}><span class="cb-slider"></span> ${t('cashbook.keep_desc', 'Keep Description')}</label>
            <span class="cb-mode-spacer"></span>
            <span id="cbVatLine" class="cb-vat-inline is-hidden">${t('common.vat', 'VAT')}: <span class="mono">0.00</span></span>
            ${dieselOn.value ? html`<span class="cb-diesel-inline">${t('cashbook.diesel', 'Diesel')}: <input id="cbDiesel" type="number" min="0" step="0.01"
              class="form-control cb-in cb-w-amt mono" placeholder=${t('cashbook.litres_ph', 'Litres e.g. 213.5')}
              title=${t('cashbook.diesel_hint', 'Diesel litres (tax code 25)')}
              .value=${fDiesel} @input=${e => { fDiesel = e.target.value; }}></span>` : []}
          </div>
        </div>

        ${rows.length === 0 ? html`<div class="muted-block">${filterUn ? t('cashbook.no_unalloc', 'No unallocated entries — everything is allocated.') : (staged.length === 0 ? t('cashbook.no_entries', 'No entries yet — capture one above or import a statement.') : `No ${tabv === 'deposit' ? 'deposits' : 'payments'} staged.`)}</div>`
        : html`<div class="table-wrap"><table class="cb-table">
          <thead><tr>
            <th class="cb-th-sel"><input type="checkbox" .checked=${allChecked} title=${t('common.select_all', 'Select All')} @change=${e => selectAllVisible(rows, e.target.checked)}></th>
            <th>${t('common.date', 'Date')}</th><th></th><th>${t('common.ref', 'Ref')}</th><th>${t('common.description', 'Description')}</th>
            <th class="text-right">${t('common.tax', 'Tax')}</th><th class="text-right">${t('common.amount', 'Amount')}</th><th></th>
          </tr></thead>
          <tbody>
            ${rows.map(l => html`<tr class=${'cb-line ' + (l.status === 'unallocated' ? 'cb-row-unalloc' : (l.amount >= 0 ? 'cb-row-in' : 'cb-row-out'))}>
              <td class="cb-th-sel"><input type="checkbox" title=${t('common.select', 'Select')} .checked=${selSet.has(l.id)} @change=${e => toggleSel(l.id, e.target.checked)}></td>
              <td class="mono cb-td-date">${dmy(l.txn_date)}${l.is_duplicate ? html` <button class="cb-dup" title=${t('cashbook.dup_confirm_hint', 'Possible duplicate — click to confirm this is NOT a duplicate so it can post')} @click=${e => { e.stopPropagation(); confirmDup(l.id); }}>⚠</button>` : []}${l.is_gap ? html` <button class="cb-gap" title=${t('cashbook.gap_hint', 'Balance gap — a transaction may be missing before this line. Click to mark it verified.')} @click=${e => { e.stopPropagation(); clearGap(l.id); }}>⚠ ${t('cashbook.gap', 'gap')}</button>` : []}</td>
              <td>${destTag(l)}</td>
              <td class="cb-td-ref mono">${l.reference || ''}</td>
              <td class="cb-td-desc">${l.description}<br>${allocCell(l)}</td>
              <td class="mono text-right" title=${taxTitleOf(l)}>${l.tax_amount ? fmtM(Math.abs(l.tax_amount)).replace(sym + ' ', '') : ''}</td>
              <td class="mono text-right cb-td-amt">
                <button class="cb-flip" title=${l.amount >= 0 ? t('cashbook.to_payments', 'Move to payments') : t('cashbook.to_deposits', 'Move to deposits')} @click=${() => flipLine(l.id)}>⇄</button>
                <span class=${l.amount >= 0 ? 't-in' : 't-out'}>${fmtM(l.amount)}</span></td>
              <td class="cb-td-act">${deleteControl(l)}</td>
            </tr>`)}
          </tbody>
        </table></div>`}` : []}

      ${m === 'auto' ? html`
        ${(shown.length > 0 || regOpening.value != null) ? html`<div class="cb-recon-strip">
          <span class="cb-recon-cell"><label>${t('cashbook.opening_balance', 'Opening balance')}</label>
            <b class="mono">${fmtOS(regOpening.value || 0)}</b>${regOpeningDate.value ? html`<small>${dmy(regOpeningDate.value)}</small>` : ''}</span>
          <span class="cb-recon-arrow">→</span>
          <span class="cb-recon-cell"><label>${t('cashbook.book_balance', 'Book balance')}</label>
            <b class="mono">${fmtFS(ledger)}</b></span>
          ${newestBal !== undefined && newestBal !== null ? html`<span class="cb-recon-cell"><label>${t('cashbook.statement_balance', 'Statement')}</label>
            <b class="mono">${fmtMS(newestBal)}</b></span>` : ''}
          ${reconDelta !== null ? (Math.abs(reconDelta) < 0.005
            ? html`<span class="cb-recon cb-recon-ok">✓ ${t('cashbook.reconciles', 'Reconciles')}</span>`
            : html`<span class="cb-recon cb-recon-off" title=${'Book ' + fmtFS(projected) + ' vs statement ' + fmtMS(newestBal)}>△ ${t('cashbook.off_by', 'Off by')} ${fmtF(reconDelta)}</span>`) : ''}
        </div>` : ''}
        ${shown.length === 0 ? html`<div class="muted-block">${regN === 0 ? t('cashbook.no_txns', 'No transactions — import a statement or capture entries in Manual mode.') : t('cashbook.no_txns_match', 'No transactions match.')}</div>`
        : html`<div class="table-wrap cb-register-wrap"><table class="cb-table cb-register-table">
          <thead><tr>
            <th class="cb-th-sel"><input type="checkbox" .checked=${allChecked} title=${t('cashbook.select_all_staged', 'Select all staged rows')} @change=${e => selectAllVisible(feedStaged, e.target.checked)}></th>
            ${sortTh(t('common.date', 'Date'), 'date', rsort, setRegSort)}${sortTh(t('common.ref', 'Ref'), 'ref', rsort, setRegSort)}${sortTh(t('common.description', 'Description'), 'description', rsort, setRegSort)}
            <th class="cb-th-act"></th><th class="text-right">${t('cashbook.received', 'Received')}</th><th class="text-right">${t('cashbook.paid', 'Paid')}</th>
            <th>${t('common.source', 'Source')}</th><th class="text-right">${t('common.balance', 'Balance')}</th>
          </tr></thead>
          <tbody>
            ${shown.map((l, i) => { if(l.kind === 'move') return moveRow(l); const rowHtml = editing === l.id ? html`
              <tr class="cb-line cb-row-edit">
                <td></td>
                <td><sleek-date id="cbEditDate" class="cb-w-date" data-next="#cbEditRef" .value=${editVals.txn_date} @change=${e => { editVals.txn_date = e.target.value; }}></sleek-date></td>
                <td><input id="cbEditRef" type="text" class="form-control cb-in cb-w-ref" placeholder=${t('common.ref_ph', 'Ref e.g. BS1')} .value=${editVals.reference} @input=${e => { editVals.reference = e.target.value; }}
                  @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); saveEdit(l.id); } else if(e.key === 'Escape'){ editId.value = 0; } }}></td>
                <td><input id="cbEditDesc" type="text" class="form-control cb-in" placeholder=${t('cashbook.desc_ph', 'Description e.g. Rent August')} .value=${editVals.description} @input=${e => { editVals.description = e.target.value; }}
                  @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); saveEdit(l.id); } else if(e.key === 'Escape'){ editId.value = 0; } }}></td>
                <td class="cb-td-act2"></td>
                <td colspan="2" class="text-right"><input id="cbEditAmt" type="text" inputmode="decimal" class="form-control cb-in cb-w-amt mono text-right" placeholder="0.00"
                  .value=${editVals.amount} @input=${e => { editVals.amount = e.target.value; }}
                  @blur=${e => { const n = parseAmount(e.target.value, openMarks()); if(n !== null){ editVals.amount = fmtAmountInput(n); e.target.value = editVals.amount; } }}
                  @keydown=${e => { if(e.key === 'Enter'){ e.preventDefault(); saveEdit(l.id); } else if(e.key === 'Escape'){ editId.value = 0; } }}></td>
                <td></td>
                <td class="text-right"><span class="cb-edit-actions"><button class="cb-edit-ok" title=${t('common.save', 'Save')} ?disabled=${b === 'edit'} @click=${() => saveEdit(l.id)}>✓</button><button class="cb-edit-no" title=${t('common.cancel', 'Cancel')} @click=${() => { editId.value = 0; }}>✕</button></span></td>
              </tr>` : (() => {   /* display row (9 cols: sel·date·ref·desc·actions·received·paid·source·balance) */
              const isStaged = l.status === 'unallocated' || l.status === 'allocated';
              return html`
              <tr class=${'cb-line ' + (l.status === 'unallocated' ? 'cb-row-unalloc' : (l.status === 'posted' ? 'cb-row-posted' : (l.amount >= 0 ? 'cb-row-in' : 'cb-row-out')))} data-line-id=${l.id}>
                <td class="cb-th-sel" data-label=${t('common.select', 'Select')}>${isStaged ? html`<input type="checkbox" title=${t('common.select', 'Select')} .checked=${selSet.has(l.id)} @change=${e => toggleSel(l.id, e.target.checked)}>` : []}</td>
                <td class="mono cb-td-date" data-label=${t('common.date', 'Date')}>${dmy(l.txn_date)}${l.is_duplicate ? html` <button class="cb-dup" title=${t('cashbook.dup_confirm_hint', 'Possible duplicate — click to confirm this is NOT a duplicate so it can post')} @click=${e => { e.stopPropagation(); confirmDup(l.id); }}>⚠</button>` : []}${l.is_gap ? html` <button class="cb-gap" title=${t('cashbook.gap_hint', 'Balance gap — a transaction may be missing before this line. Click to mark it verified.')} @click=${e => { e.stopPropagation(); clearGap(l.id); }}>⚠ ${t('cashbook.gap', 'gap')}</button>` : []}</td>
                <td class="cb-td-ref mono" data-label=${t('common.ref', 'Ref')}>${l.reference || ''}</td>
                <td class="cb-td-desc" data-label=${t('common.description', 'Description')}>${l.description}<br>${allocCell(l)}</td>
                <td class="cb-td-act2" data-label=${t('common.actions', 'Actions')}><span class="cb-row-actions">${l.has_statement_image ? html`<button class="cb-locate" title=${t('cashbook.locate_hint', 'See where this line came from on the statement')} @click=${() => locateOnStatement(l)}>🔎</button>` : []}${isStaged ? html`<span class="cb-act-edit">
                  <button class="cb-flip" title=${t('common.edit', 'Edit')} @click=${() => startEdit(l)}>✎</button>
                  <button class="cb-flip" title=${l.amount >= 0 ? t('cashbook.to_payments_out', 'Move to payments (out)') : t('cashbook.to_deposits_in', 'Move to deposits (in)')} @click=${() => flipLine(l.id)}>⇄</button>
                  ${deleteControl(l)}</span>`
                : l.locked
                  ? html`<span class="cb-act-edit"><span class="row-lock row-lock-red" title=${t('cashbook.locked_hint', 'This post is locked — see Financial Years (top-right corner)')}>🔒</span></span>`
                  : html`<span class="cb-act-edit"><button class="cb-flip cb-unpost" title=${t('cashbook.unpost_hint', 'Unpost — return this entry to unallocated so you can re-allocate it')} ?disabled=${b === 'unpost' + l.id} @click=${() => unpostLine(l.id)}>🔓</button></span>`}</span></td>
                <td class="mono text-right t-in cb-td-received" data-label=${t('cashbook.received', 'Received')}>${l.amount >= 0 ? fmtM(l.amount) : ''}</td>
                <td class="mono text-right t-out cb-td-paid" data-label=${t('cashbook.paid', 'Paid')}>${l.amount < 0 ? fmtM(l.amount) : ''}</td>
                <td class="cb-td-source" data-label=${t('common.source', 'Source')}>${l.statement_id ? html`<span class="cb-src cb-src-auto">${t('cashbook.mode_auto', 'Auto')}</span>` : html`<span class="cb-src cb-src-manual">${t('cashbook.mode_manual', 'Manual')}</span>`}</td>
                <td class="mono text-right cb-td-balance" data-label=${t('common.balance', 'Balance')}>${(l.computed_balance ?? l.running_balance) != null ? html`<span class=${(l.computed_balance ?? l.running_balance) >= 0 ? 't-in' : 't-out'}>${fmtMS(l.computed_balance ?? l.running_balance)}</span>` : ''}</td>
              </tr>`; })();
              // one continuous chronological list (latest first) so the running balance ties to the
              // bank statement — no Unposted/Posted section split; posted rows still show the grey lock.
              return rowHtml;
            })}
            ${regOpening.value != null && feed.length >= regTotal.value ? html`
              <tr class="cb-line cb-row-opening" title=${t('cashbook.opening_row_hint', 'The account opening balance — every running balance above builds on this')}>
                <td class="cb-th-sel"></td>
                <td class="mono cb-td-date">${regOpeningDate.value ? dmy(regOpeningDate.value) : ''}</td>
                <td class="cb-td-ref mono"></td>
                <td class="cb-td-desc"><b>${t('cashbook.opening_balance', 'Opening balance')}</b></td>
                <td class="cb-td-act2"></td>
                <td class="mono text-right t-in cb-td-received" data-label=${t('cashbook.received', 'Received')}>${regOpening.value >= 0 ? fmtO(regOpening.value) : ''}</td>
                <td class="mono text-right t-out cb-td-paid" data-label=${t('cashbook.paid', 'Paid')}>${regOpening.value < 0 ? fmtO(regOpening.value) : ''}</td>
                <td class="cb-td-source" data-label=${t('common.source', 'Source')}></td>
                <td class="mono text-right cb-td-balance" data-label=${t('common.balance', 'Balance')}><span class=${regOpening.value >= 0 ? 't-in' : 't-out'}>${fmtOS(regOpening.value)}</span></td>
              </tr>` : []}
          </tbody>
        </table></div>
        <div id="cbRegEnd" class="cb-reg-end muted">${regBusy ? t('cashbook.loading_more', 'Loading more…')
          : `${feed.length} of ${regN} transaction${regN === 1 ? '' : 's'}`}</div>`}` : []}
    </div>`;
  };

  // ── "See this line on the statement" (v2 locate parity) ──────────────────────
  // Only PDF/image statements have a page to show a row on (CSV has no image). Old imports
  // predating source-storage return no_pages → a friendly note, never a hard failure.
  const hasStatementImage = tx => /\.(pdf|png|jpe?g)$/i.test(tx._file || '');
  function closeLocate(){
    locateSeq++;                             // invalidate any in-flight page load
    const s = locate.value;
    if(s && s.imgUrl){ try { URL.revokeObjectURL(s.imgUrl); } catch { /* already revoked */ } }
    locate.value = null;
  }
  async function locateOnStatement(tx){
    const job = tx._job || tx.source_job_id;   // review rows carry _job; register rows source_job_id
    if(!job) return;
    const seq = ++locateSeq;                   // this open supersedes any earlier in-flight one
    const title = (tx.description || '').slice(0, 60) || t('cashbook.statement', 'Statement');
    locate.value = { status: 'loading', title, imgUrl: '', box: null, page: 0, job };
    let data;
    try {
      data = await api.post(P(`/bank-imports/${job}/locate`),
        { date: tx.date || tx.txn_date, description: tx.description || '',
          reference: tx.reference || '', amount: tx.amount });   // payee (reference) sharpens the match
    } catch(e){
      if(seq === locateSeq) locate.value = { status: 'error', title,
        error: (e.data && e.data.error) || t('cashbook.locate_failed', 'Could not reach the statement locator.') };
      return;
    }
    if(seq !== locateSeq) return;              // user closed or re-opened while we waited
    const errMap = {
      no_key: t('cashbook.locate_no_key', 'Add your Gemini API key in AI settings to locate lines on the statement.'),
      no_pages: t('cashbook.locate_no_source', 'The original statement image was not stored for this import (it predates this feature, or was a CSV).'),
    };
    if(data && data.error && errMap[data.error]){
      locate.value = { status: 'error', title, error: errMap[data.error] };
      return;
    }
    const pageCount = (data && data.page_count) || 1;
    const foundPage = (data && typeof data.page === 'number' && data.page >= 0 && data.page < pageCount) ? data.page : 0;
    let foundBox = (data && Array.isArray(data.box)) ? data.box : null;   // absent when not_found
    // Defence against a bad AI box that would paint the WHOLE statement red: a real row is a thin
    // strip, so reject an absent/inverted box or one taller than half the page → show the "couldn't
    // pinpoint" note instead of a giant highlight (owner 2026-09-08; also guards older API images).
    if(foundBox && !(foundBox.length === 4 && foundBox[0] >= 0 && foundBox[2] <= 1 && foundBox[2] > foundBox[0] && (foundBox[2] - foundBox[0]) <= 0.5)) foundBox = null;
    let imgUrl = '';
    try {
      imgUrl = await api.objectUrl(P(`/bank-imports/${job}/page?page=${foundPage}`));
    } catch(e){
      if(seq === locateSeq) locate.value = { status: 'error', title,
        error: (e.data && e.data.error) || t('cashbook.locate_no_image', 'Could not load the statement page image.') };
      return;
    }
    if(seq !== locateSeq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    locate.value = { status: 'ready', title, job, imgUrl, box: foundBox, page: foundPage,
                     pageCount, foundPage, foundBox };
    // bring the highlighted band into view once tina4 has painted the image node
    setTimeout(() => { const el = document.querySelector('.cb-stmt-hl'); if(el && el.scrollIntoView) el.scrollIntoView({ block: 'center' }); }, 250);
  }

  // Flip to another page of the SAME statement — so a multi-page statement can be read in full, and
  // the line can be found by eye when the locator couldn't pinpoint it (owner 2026-09-16). The red
  // highlight only shows on the page the line was actually located on.
  async function gotoLocatePage(n){
    const s = locate.value;
    if(!s || s.status !== 'ready' || !s.job) return;
    const page = Math.max(0, Math.min((s.pageCount || 1) - 1, n));
    if(page === s.page) return;
    const seq = ++locateSeq;
    let imgUrl;
    try {
      imgUrl = await api.objectUrl(P(`/bank-imports/${s.job}/page?page=${page}`));
    } catch { return; }                        // leave the current page shown on a failed flip
    const cur = locate.value;
    if(!cur || seq !== locateSeq){ try { URL.revokeObjectURL(imgUrl); } catch { /* stale */ } return; }
    if(cur.imgUrl){ try { URL.revokeObjectURL(cur.imgUrl); } catch { /* already revoked */ } }
    locate.value = { ...cur, imgUrl, page, box: (page === cur.foundPage ? cur.foundBox : null) };
    if(page === cur.foundPage) setTimeout(() => { const el = document.querySelector('.cb-stmt-hl'); if(el && el.scrollIntoView) el.scrollIntoView({ block: 'center' }); }, 200);
  }
  function locateModal(s){
    let band = '';
    if(s.box){                                    // full-width band at the AI's vertical position
      const pad = 1.2;                            // % padding so the band snugly frames the row
      const top = Math.max(0, s.box[0] * 100 - pad);
      const h = Math.min(100 - top, (s.box[2] - s.box[0]) * 100 + pad * 2);
      band = `--hl-top:${top}%;--hl-h:${h}%`;     // geometry only — appearance lives in .cb-stmt-hl
    }
    const nav = s.status === 'ready' && s.pageCount > 1 ? html`<span class="cb-stmt-nav">
        <button class="cb-stmt-pg" ?disabled=${s.page <= 0} title=${t('cashbook.prev_page', 'Previous page')} @click=${() => gotoLocatePage(s.page - 1)}>‹</button>
        <span class="cb-stmt-page mono">p${(s.page || 0) + 1}/${s.pageCount}</span>
        <button class="cb-stmt-pg" ?disabled=${s.page >= s.pageCount - 1} title=${t('cashbook.next_page', 'Next page')} @click=${() => gotoLocatePage(s.page + 1)}>${icon('chevron-right', {size: 14})}</button>
      </span>` : [];
    return html`<div class="cb-stmt-overlay" @click=${e => { if(e.target === e.currentTarget) closeLocate(); }}>
      <div class="cb-stmt-modal" role="dialog" aria-label="Statement source">
        <div class="cb-stmt-head">
          <span class="fw-700 cb-stmt-title">${s.title}</span>
          ${nav}
          <span class="cb-mode-spacer"></span>
          <button class="cb-clear-btn" title=${t('common.close', 'Close')} @click=${closeLocate}>✕</button>
        </div>
        <div class="cb-stmt-body">
          ${s.status === 'loading'
            ? html`<div class="cb-stmt-locating">${t('cashbook.locating', 'Asking Sleek to find this line on the statement…')}</div>`
            : s.status === 'error'
              ? html`<div class="muted-block cb-stmt-err">${s.error}</div>`
              : html`<div class="cb-stmt-img-wrap">
                  <img class="cb-stmt-img" src=${s.imgUrl} alt="Statement page">
                  ${s.box ? html`<div class="cb-stmt-hl" style=${band}></div>` : []}
                </div>
                ${!s.box ? html`<div class="cb-stmt-noline">${s.pageCount > 1
                    ? t('cashbook.locate_noline_multi', 'Showing the statement — the exact line couldn’t be pinpointed. Use ‹ › to look through the pages.')
                    : t('cashbook.locate_noline', 'Showing the statement — the exact line could not be pinpointed automatically.')}</div>` : []}`}
        </div>
      </div>
    </div>`;
  }
  const locateView = () => locate.value ? locateModal(locate.value) : [];

  // ── wrong-account-drop confirm ─────────────────────────────────────────────
  // The drop is held by the API and listed under "Waiting for you" (2026-10-27): Continue imports it into the card
  // (importHeldOnCard), Cancel discards it — nothing of it is imported — and the question closed by a click beside it
  // leaves it waiting on the list, as the add-account form's ✕ does.
  async function confirmMismatchImport(){
    const m = mismatch.value;
    mismatch.value = null;
    if(m) await importHeldOnCard(m.jobId, m.accountId);
  }
  async function cancelMismatchImport(){
    const m = mismatch.value;
    mismatch.value = null;
    if(!m) return;
    try {
      await api.post(P(`/bank-imports/jobs/${m.jobId}/discard`), {});
      setToast(t('cashbook.import_cancelled', 'Import cancelled — nothing was imported'), 'warn');
    } catch(err){ setToast((err && err.data && err.data.error) || t('cashbook.held_discard_failed', 'Could not discard it'), 'err'); }
    loadHeld();
  }
  function closeMismatch(){
    const m = mismatch.value;
    mismatch.value = null;
    if(!m) return;
    setToast(fill(t('cashbook.held_waits', '{file} waits for you — see “Waiting for you” above the cards.'),
      { file: m.files || t('cashbook.the_statement', 'the statement') }), 'warn');
    loadHeld();
  }
  const mismatchView = () => {
    const m = mismatch.value;
    if(!m) return [];
    return html`<div class="cb-stmt-overlay" @click=${e => { if(e.target === e.currentTarget) closeMismatch(); }}>
      <div class="cb-confirm-modal cb-mismatch-modal" role="dialog" aria-label=${t('cashbook.mismatch_title', 'Wrong bank account?')}>
        <div class="cb-confirm-head"><span class="cb-mismatch-icon">⚠</span> ${t('cashbook.mismatch_title', 'Wrong bank account?')}</div>
        <div class="cb-confirm-body">
          ${t('cashbook.mismatch_body', 'This statement is for account {detected}, but you are importing it into “{account}” ({expected}). Import it here anyway?')
            .replace('{detected}', m.detected || t('common.unknown', 'unknown'))
            .replace('{account}', m.accountName || t('common.this_account', 'this account'))
            .replace('{expected}', m.expected || '')}
        </div>
        <div class="cb-confirm-actions">
          <button class="btn btn-sm btn-outline" @click=${cancelMismatchImport}>${t('common.cancel', 'Cancel')}</button>
          <button class="btn btn-sm btn-primary cb-mismatch-continue" @click=${confirmMismatchImport}>${t('common.continue', 'Continue')}</button>
        </div>
      </div>
    </div>`;
  };

  const toastView = () => toast.value
    ? html`<div class=${'cb-toast cb-toast-' + toast.value.kind}>${toast.value.msg}${toast.value.action
        ? html` <button class="cb-toast-action" @click=${() => { const a = toast.value.action; toast.value = null; a.fn(); }}>${toast.value.action.label}</button>` : []}</div>` : [];

  const content = html`<div class="page page-wide cb-page">
    ${headRow}
    ${cards}
    ${view}
    ${toastView}
    ${locateView}
    ${mismatchView}
    ${review.view}
  </div>`;
  return tenantShell(companyId, t('common.bank_accounts', 'Bank Accounts'), content);
}
